import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";

import { useAuth } from "../auth/AuthProvider";
import { MYKYC_API_BASE } from "../config/api";
import { useStaffTopBand } from "../components/ui/StaffTopBandContext";
import regulatoryHeaderIcon from "../images/regulatory-logo-2.svg";
import RegulatoryAddOns from "../components/regulatory/RegulatoryAddOns";
import RegulatoryTopicOverview, { hasRegulatoryTopicOverview } from "../components/regulatory/RegulatoryTopicOverview";
import preserveListContext from "../regulations/preserveListContext";
import useRegulatoryAddOns from "../utils/useRegulatoryAddOns";
import clientsTopLogo from "../images/mykyc-logo-blue-800px.png";
import dfsaFigureRiskBasedApproach from "../images/DFSA rulebook images/dfsa figure 1.png";
import dfsaFigureBusinessRiskAssessment from "../images/DFSA rulebook images/dfsa figure 2.png";
import dfsaFigureCustomerRiskAssessment from "../images/DFSA rulebook images/dfsa figure 3.png";
import dfsaFigureCdd from "../images/DFSA rulebook images/dfsa figure 4.png";
import dfsaFigureReliance from "../images/DFSA rulebook images/dfsa figure x reliance.png";
import cabinetDecision74Pdf from "../regulations/Cabinet Decision No. 74/2020.pdf";
import beneficialOwnershipLawPdf from "../regulations/Cabinet Resolution No. (109) of 2023 - UBO Law.pdf";
import virtualAssetsLawPdf from "../regulations/Cabinet Resolution No. (111) of 2022 - Virtual Assets.pdf";
import cabinetResolution134Pdf from "../regulations/Cabinet Resolution No. (134) of 2025.pdf";
import dfsaAmlRulebookPdf from "../regulations/DFSA AML Rulebook VER30:04-26.pdf";
import difcRegulatoryLawPdf from "../regulations/DIFC-LAW-2004.pdf";
import fsraAmlRulebookPdf from "../regulations/FSRA AML Rulebook VER11.210526 .pdf";
import fatfRecommendationsPdf from "../regulations/FATF Recommendations.pdf";
import fatfUbosPdf from "../regulations/FATF UBOs.pdf";
import federalDecreeLaw10Pdf from "../regulations/Federal Decree by Law No. (10) of 2025.pdf";
import federalLaw7Pdf from "../regulations/Federal Law No. (7) of 2014 Combating Terrorism Crimes.pdf";
import dnfbpGuidancePdf from "../regulations/UAE Guidance AML:CFT DNFBPs.pdf";
import fiGuidancePdf from "../regulations/UAE Guidance AML:CFT FIs.pdf";
import cabinetDecision74_2020 from "../regulations/data/cabinetDecision74_2020";
import cabinetResolution109_2023 from "../regulations/data/cabinetResolution109_2023";
import cabinetResolution111_2022 from "../regulations/data/cabinetResolution111_2022";
import cabinetResolution134_2025 from "../regulations/data/cabinetResolution134_2025";
import dnfbpAmlCftGuidelines_2021 from "../regulations/data/dnfbpAmlCftGuidelines_2021";
import dfsaAmlModule from "../regulations/data/dfsaAmlModule";
import fatfBeneficialOwnership_2024 from "../regulations/data/fatfBeneficialOwnership_2024";
import fiAmlCftGuidelines_2021 from "../regulations/data/fiAmlCftGuidelines_2021";
import fsraAmlRulebook from "../regulations/data/fsraAmlRulebook";
import federalDecreeLaw10_2025 from "../regulations/data/federalDecreeLaw10_2025";
import federalLaw7_2014 from "../regulations/data/federalLaw7_2014";
import swissAmla from "../regulations/data/swissAmla";
import swissAmlo from "../regulations/data/swissAmlo";
import swissAmloFinma from "../regulations/data/swissAmloFinma";
import swissCdb2020 from "../regulations/data/swissCdb2020";
import swissCdb2020Commentary from "../regulations/data/swissCdb2020Commentary";
import { getCdbArticleLink } from "../regulations/cdbArticleLinks";
import RelatedRegulationArticleModal from "../components/regulatory/RelatedRegulationArticleModal";
import regulatoryTopics from "../regulations/topics/regulatoryTopics";
import { latestUpdatesForCountry } from "../regulations/latestUpdates";
import "./css/RegulatoryPage.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const baseTopicChips = [
  "Onboarding",
  "Identification UBOs",
  "Verification identity",
  "Due diligence",
  "Simplified Due diligence",
  "Enhanced Due diligence",
  "PEPs",
  "Sanctions",
  "Terrorism",
  "SoW/SoF",
  "Risk factors",
  "Risk rating",
  "Transaction monitoring",
  "SARs",
  "Record keeping",
  "Periodic review",
  "Governance",
  "Training",
];

const regulatoryCountryOptions = [
  { value: "uae", label: "UAE" },
  { value: "switzerland", label: "Switzerland" },
];

const regulatoryEntityTypeOptions = [
  { value: "fi", label: "FI" },
  { value: "dnfbp", label: "DNFBP" },
];

const regulatoryRegimeOptions = [
  { value: "uae", label: "Mainland" },
  { value: "dfsa", label: "DIFC" },
  { value: "fsra", label: "ADGM" },
];

const dnfbpOrgIndustries = new Set(["csp", "legal", "accounting", "real_estate", "real estate"]);

function normalizeRegulatoryOrgCountry(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (!normalized) return "";
  if (normalized === "uae" || normalized === "ae" || normalized.includes("united arab emirates")) {
    return "uae";
  }
  if (normalized === "switzerland" || normalized === "ch" || normalized.includes("swiss")) {
    return "switzerland";
  }

  return "";
}

function normalizeRegulatoryOrgEntityType(org) {
  const industry = String(org?.industry || "").trim().toLowerCase();
  const type = String(org?.type || org?.entityType || org?.businessType || "").trim().toLowerCase();
  const source = `${industry} ${type}`.trim();

  if (!source) return "";
  if (industry === "finance" || source.includes("financial") || /\bfi\b/.test(source)) {
    return "fi";
  }
  if (dnfbpOrgIndustries.has(industry) || source.includes("dnfbp")) {
    return "dnfbp";
  }

  return "";
}

function normalizeRegulatoryOrgRegime(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (!normalized) return "";
  if (normalized === "difc" || normalized === "dfsa") return "dfsa";
  if (normalized === "adgm" || normalized === "fsra") return "fsra";
  if (normalized === "mainland" || normalized === "uae" || normalized === "free_zone" || normalized === "fz") {
    return "uae";
  }

  return "";
}

const dfsaTopicDocumentIds = new Set(["dfsa-aml-module"]);
const fsraTopicDocumentIds = new Set(["fsra-aml-rulebook"]);
const globalTopicDocumentIds = new Set(["fatf-beneficial-ownership-2024"]);

function isTopicDocumentVisibleForSelection(documentId, country, regime, entityType) {
  if (documentId === "fi-aml-cft-guidelines-2021") {
    return country === "uae" && entityType === "fi";
  }

  if (documentId === "dnfbp-aml-cft-guidelines-2021") {
    return country === "uae" && entityType === "dnfbp";
  }

  if (globalTopicDocumentIds.has(documentId)) {
    return true;
  }

  if (dfsaTopicDocumentIds.has(documentId)) {
    return country === "uae" && regime === "dfsa";
  }

  if (fsraTopicDocumentIds.has(documentId)) {
    return country === "uae" && regime === "fsra";
  }

  return country === "uae";
}


const mainlandBaseRegulations = [
  {
    title: "Core AML Law",
    legalName:
      "Federal Decree-Law No. 10/2025 Regarding Anti-Money Laundering, and Combating the Financing of Terrorism and Proliferation Financing",
    topics: ["AML", "CFT", "CPF"],
    accent: "blue",
    documentUrl: federalDecreeLaw10Pdf,
    readerData: federalDecreeLaw10_2025,
  },
  {
    title: "AML Law Implementing Rules",
    legalName:
      "Cabinet Resolution No. 134/2025 Regarding the Executive Regulations of Federal Decree-Law No. 10/2025",
    topics: ["CDD", "EDD", "SARs"],
    accent: "green",
    documentUrl: cabinetResolution134Pdf,
    readerData: cabinetResolution134_2025,
  },
  {
    title: "Sanctions & Freezing Duties",
    legalName:
      "Cabinet Decision No. 74/2020 Concerning the Terrorism Lists Regulation and the Implementation of UN Security Council Decisions",
    topics: ["Sanctions", "Screening", "Freeze"],
    accent: "amber",
    documentUrl: cabinetDecision74Pdf,
    readerData: cabinetDecision74_2020,
  },
];

const mainlandGuidelineByEntityType = {
  fi: {
    title: "FI AML Guidelines",
    legalName:
      "Anti-Money Laundering and Combating the Financing of Terrorism and Illegal Organisations Guidelines for Financial Institutions",
    topics: ["FI", "Due diligence", "Controls"],
    accent: "slate",
    documentUrl: fiGuidancePdf,
    readerData: fiAmlCftGuidelines_2021,
  },
  dnfbp: {
    title: "DNFBP AML Guidelines",
    legalName:
      "Guidelines for Designated Non-Financial Businesses and Professions on Anti-Money Laundering and Combating the Financing of Terrorism",
    topics: ["DNFBP", "Due diligence", "Controls"],
    accent: "rose",
    documentUrl: dnfbpGuidancePdf,
    readerData: dnfbpAmlCftGuidelines_2021,
  },
};

const dfsaCoreRegulations = [
  {
    title: "DFSA Rulebook - AML Module",
    legalName:
      "DFSA Rulebook, Anti-Money Laundering, Counter-Terrorist Financing and Sanctions Module (AML/VER30/04-26)",
    accent: "green",
    documentUrl: dfsaAmlRulebookPdf,
    readerData: dfsaAmlModule,
  },
  {
    title: "DIFC Regulatory Law 2004",
    legalName: "DIFC Law No. 1 of 2004, Regulatory Law 2004",
    accent: "blue",
    documentUrl: difcRegulatoryLawPdf,
    pdfReaderUrl: difcRegulatoryLawPdf,
  },
];

const fsraCoreRegulations = [
  {
    title: "FSRA AML Rulebook",
    legalName: "FSRA Anti-Money Laundering and Sanctions Rulebook (VER11.210526)",
    accent: "blue",
    documentUrl: fsraAmlRulebookPdf,
    readerData: fsraAmlRulebook,
  },
];

const specializedUaeRegulations = [
  {
    title: "Beneficial Ownership Law",
    legalName: "Cabinet Resolution No. (109) of 2023 Regulating the Real Beneficiary Procedures",
    accent: "teal",
    documentUrl: beneficialOwnershipLawPdf,
    readerData: cabinetResolution109_2023,
  },
  {
    title: "Terrorism Offences Law",
    legalName: "Federal Law No. 7 of 2014 regarding Combating Terrorism Offences",
    accent: "blue",
    documentUrl: federalLaw7Pdf,
    readerData: federalLaw7_2014,
  },
  {
    title: "Virtual Assets Laws",
    legalName: "Cabinet Resolution No. (111) of 2022 Regulating Virtual Assets and the Related Service Providers",
    accent: "amber",
    documentUrl: virtualAssetsLawPdf,
    readerData: cabinetResolution111_2022,
  },
];

const swissCoreRegulations = [
  {
    title: "Anti-Money Laundering Act (AMLA)",
    legalName:
      "Federal Act on Combating Money Laundering and Terrorist Financing in the Financial Sector (AMLA)",
    accent: "blue",
    readerData: swissAmla,
    documentPending: true,
  },
  {
    title: "Anti-Money Laundering Ordinance (AMLO)",
    legalName: "Ordinance on Combating Money Laundering and Terrorist Financing (AMLO)",
    accent: "green",
    readerData: swissAmlo,
    documentPending: true,
  },
  {
    title: "FINMA Anti-Money Laundering Ordinance (AMLO-FINMA)",
    legalName:
      "Ordinance of the Swiss Financial Market Supervisory Authority on Combating Money Laundering and Terrorist Financing in the Financial Sector (AMLO-FINMA)",
    accent: "slate",
    readerData: swissAmloFinma,
    documentPending: true,
  },
];

const specializedSwissRegulations = [
  {
    title: "CDB 2020",
    legalName:
      "Agreement on the Swiss Banks’ Code of Conduct with Regard to the Exercise of Due Diligence (CDB 2020)",
    accent: "blue",
    readerData: swissCdb2020,
  },
  {
    title: "CDB 2020 Commentary",
    legalName:
      "Commentary on the Agreement on the Swiss Banks’ Code of Conduct with Regard to the Exercise of Due Diligence (CDB 2020)",
    accent: "green",
    readerData: swissCdb2020Commentary,
  },
];

const fatfRegulations = [
  {
    title: "FATF Recommendations",
    legalName: "International Standards on Combating Money Laundering and the Financing of Terorrism & Proliferation: The FATF Recommendations",
    accent: "slate",
    documentUrl: fatfRecommendationsPdf,
    pdfReaderUrl: fatfRecommendationsPdf,
  },
  {
    title: "FATF Beneficial Ownership",
    legalName: "Guidance for a Risk-Based Approach: Beneficial Ownership and Transparency of Legal Arrangements",
    accent: "green",
    documentUrl: fatfUbosPdf,
    readerData: fatfBeneficialOwnership_2024,
  },
];

const readerSections = [
  {
    label: "Overview",
    title: "Purpose and scope",
    body:
      "This placeholder section gives a concise overview of the regulatory instrument, the supervised entities in scope and the main compliance expectations. It will later be replaced by the extracted regulation text.",
  },
  {
    label: "Governance",
    title: "Governance and controls",
    body:
      "Sample regulatory text can sit here in a readable long-form format, with clear paragraph spacing and enough room for practical review. The final version can support highlighted search matches and section navigation.",
  },
  {
    label: "Due Diligence",
    title: "Customer due diligence",
    body:
      "This area is designed for article-level content, references, definitions and obligations. For now it uses template copy so the visual spacing, menu and reader density can be tuned first.",
  },
  {
    label: "Reporting",
    title: "Monitoring and reporting",
    body:
      "Placeholder content for transaction monitoring, suspicious activity reporting and record keeping. The layout keeps the legal text separate from the regulation card summary.",
  },
];

const placeholderReaderData = {
  intro: "",
  chapters: [
    {
      chapter: "Template",
      title: "Visual Template",
      articles: readerSections.map((section, index) => ({
        number: String(index + 1),
        label: section.label,
        title: section.title,
        body: `${section.body}\n\nAdditional placeholder paragraph for visual tuning. This reader will later display the real regulation text, split by chapters and articles for fast review inside the application.`,
      })),
    },
  ],
};

function cleanDefinitionTerm(term, text) {
  const normalizedTerm = term.replace(/\s+/g, " ").trim();
  const normalizedText = text.replace(/\s+/g, " ").trim();

  if (normalizedTerm === "Terrorist" && normalizedText.startsWith("A group")) {
    return "Terrorist Organization";
  }

  const definitionTermMap = {
    "The Higher": "The Higher Committee",
    "The National": "The National Committee",
    "The General": "The General Secretariat",
    "The Supervisory": "The Supervisory Authority",
    "Law Enforcement": "Law Enforcement Authorities",
    Concerned: "Concerned Authorities",
    "The Executive": "The Executive Office",
    "Targeted Financial": "Targeted Financial Sanctions",
    Senior: "Senior Management",
    Intermediary: "Intermediary Financial Institution",
    Beneficiary: "Beneficiary Financial Institution",
    "Financing of": "Financing of Terrorism",
    "Weapons of Mass": "Weapons of Mass Destruction",
    Suspicious: "Suspicious Transactions",
    "Without Prior": "Without Prior Notice",
    Financial: "Financial Institutions",
    "Designated Non-": "Designated Non-Financial Businesses and Professions",
    "Non-Profit": "Non-Profit Organizations",
    Commercial: normalizedText.startsWith("Any person")
      ? "Commercial Gaming Operators"
      : "Commercial Gaming",
    Nominee: "Nominee Shareholder",
    Reasonable: "Reasonable Measures",
    Business: "Business Relationship",
    Correspondent: "Correspondent Banking Relationship",
    "Payable-Through": "Payable-Through Accounts",
    "Financial Group": "Financial Group",
    "International Core": "International Core Principles for Financial Supervision",
    "High-Risk": "High-Risk Customers",
    "Politically Exposed": "Politically Exposed Persons (PEPs)",
    "Virtual Asset": "Virtual Asset Service Provider",
    "Customer Due": "Customer Due Diligence Measures",
    Controlled: "Controlled Delivery",
    Undercover: "Undercover Operation",
  };

  return definitionTermMap[normalizedTerm] || normalizedTerm;
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeHtml(value) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function getPdfSearchRegex(searchTerm) {
  if (!searchTerm) {
    return null;
  }

  return new RegExp(`(^|[^A-Za-z0-9])(${escapeRegExp(searchTerm)}[A-Za-z0-9]*)`, "gi");
}

function renderPdfSearchText(str, searchTerm) {
  if (!searchTerm) {
    return escapeHtml(str);
  }

  const regex = getPdfSearchRegex(searchTerm);
  return escapeHtml(str).replace(regex, (match, prefix, termMatch) => {
    return `${prefix}<mark class="regulatoryPdfSearchMark">${termMatch}</mark>`;
  });
}

function getSearchTermRegex(searchTerm) {
  if (!searchTerm) {
    return null;
  }

  return new RegExp(`(^|[^A-Za-z0-9])(${escapeRegExp(searchTerm)})`, "gi");
}

function getWordSegmentEnd(text, startIndex) {
  let endIndex = startIndex;

  while (endIndex < text.length && /[A-Za-z0-9]/.test(text[endIndex])) {
    endIndex += 1;
  }

  return endIndex;
}

function getSearchTermMatches(value, searchTerm) {
  const text = String(value || "");
  if (Array.isArray(searchTerm)) {
    const matches = searchTerm
      .flatMap((term) => getSearchTermMatches(text, term))
      .sort((first, second) => first.start - second.start || second.end - second.start - (first.end - first.start));
    const nonOverlappingMatches = [];
    let cursor = 0;

    matches.forEach((match) => {
      if (match.start < cursor) {
        return;
      }

      nonOverlappingMatches.push(match);
      cursor = match.end;
    });

    return nonOverlappingMatches;
  }

  const regex = getSearchTermRegex(searchTerm);

  if (!regex) {
    return [];
  }

  return [...text.matchAll(regex)].map((match) => {
    const prefixLength = match[1]?.length || 0;
    const start = match.index + prefixLength;

    return {
      start,
      end: getWordSegmentEnd(text, start + match[2].length),
    };
  });
}

function includesSearchTerm(value, searchTerm) {
  if (!searchTerm) {
    return true;
  }

  if (Array.isArray(searchTerm)) {
    return searchTerm.some((term) => includesSearchTerm(value, term));
  }

  return getSearchTermRegex(searchTerm).test(String(value || ""));
}

function appendRegulationLine(existingText, line) {
  if (!existingText) {
    return line;
  }

  return existingText.endsWith("-") ? `${existingText}${line}` : `${existingText} ${line}`;
}

function isReaderContentHeading(line) {
  // This sentence introduces the following list; it is body text, not a heading.
  if (/^Commonly referred to as the three lines of defen[cs]e\b/i.test(line)) {
    return false;
  }

  if (isGuidanceCategoryHeading(line)) {
    return true;
  }

  if (!line || line.length > 120) {
    return false;
  }

  if (/^In this module,/i.test(line)) {
    return false;
  }

  if (/^In complying with Rule\b/i.test(line)) {
    return false;
  }

  if (/^A Relevant Person\b/i.test(line)) {
    return false;
  }

  if (/^If a Relevant Person\b/i.test(line)) {
    return false;
  }

  if (/^Where a Relevant Person\b/i.test(line)) {
    return false;
  }

  if (
    /^(For the purposes of implementing|A Legal Person shall|The Public Prosecution|Any person|An independent|By virtue of|Pursuant to|The Supervisory Authorities shall|No natural or legal person shall|Without prejudice to|Information obtained|The penalties prescribed|Whoever deliberately|The Cabinet shall|This Decree by Law shall|Financial Institutions, Designated Non-Financial Businesses and Professions, and Virtual Asset Service Providers shall have|Banking secrecy, professional secrecy|In carrying out its functions|For the purpose of performing its functions|The Supervisory Authority for Non-Profit Organizations shall|In applying the provisions of this Chapter|For the purpose of requesting and executing international cooperation requests|The Concerned Authorities shall exchange Crime-related information|Competent judicial authorities shall|International judicial cooperation requests|Execution of judgments or judicial orders|Cabinet Resolution No\. \(10\) of 2019|This Resolution shall|In the implementation of the provisions of this Decision|Anyone whose name is listed on Local Lists|Subject to paragraph \(2\) of Article \(2\) of the present Decision|The Office shall state|In all cases, procedures set forth in UNSCRs|The implementation of a freezing order|The mechanism for lifting freezing|The Office may set any controls|Cabinet Decision No\. \(20\) of 2019|The present Decision shall)\b/i.test(
      line
    )
  ) {
    return false;
  }

  if (/[.;:,]$/.test(line)) {
    return false;
  }

  return /^[A-Z][A-Za-z0-9’'().,:\-/& ]+$/.test(line);
}

function isGuidanceCategoryHeading(line) {
  return (
    /^Federal authorities:\s*NAMLCFTC,\s*FIU and EOCN$/i.test(line) ||
    /^Overview$/i.test(line) ||
    /^The UAE criminal law$/i.test(line) ||
    /^Financial Action Task Force Standards$/i.test(line) ||
    /^Basel Committee Standards$/i.test(line) ||
    /^Wolfsberg Group$/i.test(line) ||
    /^Sanctions$/i.test(line)
  );
}

function isClauseBreakingContentHeading(line) {
  return (
    /^Identifying and verifying\b/i.test(line) ||
    /^Politically Exposed Persons:/i.test(line) ||
    isGuidanceCategoryHeading(line)
  );
}

function isStandaloneRegulatoryContentHeading(line) {
  return isClauseBreakingContentHeading(line) || /^Annual Information Return$/i.test(line);
}

const dfsaGlossaryTerms = [
  "AML",
  "Authorised Person",
  "Beneficial Owner",
  "body corporate",
  "Branch",
  "Cabinet Resolution No. 134 of 2025",
  "Cabinet Decision No. 74 of 2020",
  "Client",
  "company service provider",
  "Contract of Insurance",
  "CTF",
  "customer",
  "Customer Due Diligence (CDD)",
  "Designated Non-Financial Business or Profession (DNFBP)",
  "DIFC entity",
  "Employee",
  "Enhanced Customer Due Diligence",
  "FATF",
  "FATF Recommendations",
  "Federal AML legislation",
  "Federal Law No. 10 of 2025",
  "Federal Law No. 7 of 2014",
  "FIU",
  "Financial Institution",
  "Financial Services Regulator",
  "foundation",
  "Governing Body",
  "Group",
  "IMF",
  "International Organisation",
  "Law",
  "legal arrangement",
  "legal person",
  "Member",
  "MENAFATF",
  "Money Laundering Reporting Officer (MLRO)",
  "National Committee",
  "natural person",
  "Non-Fungible Token",
  "OECD",
  "person",
  "Politically Exposed Person (PEP)",
  "proliferation",
  "Registered Auditor",
  "Regulated Exchange",
  "Regulated Financial Institution",
  "Relevant Person",
  "senior management",
  "Shell Bank",
  "Simplified Customer Due Diligence",
  "source of funds",
  "source of wealth",
  "State",
  "Suspicious Activity Report (SAR)",
  "transaction",
  "Utility Token",
  "Virtual Asset Service Provider (VASP)",
];

const glossaryDisplayAcronyms = new Set([
  "AML",
  "CDD",
  "CTF",
  "DFSA",
  "DIFC",
  "DNFBP",
  "FATF",
  "FIU",
  "GEN",
  "GLO",
  "IMF",
  "MENAFATF",
  "MLRO",
  "NFT",
  "OECD",
  "PEP",
  "SAR",
  "UAE",
  "VASP",
]);

const dfsaGlossaryTermAliases = {
  "Cabinet Resolution No. 134 of 2025": ["Cabinet Resolution"],
  "Cabinet Decision No. 74 of 2020": ["Cabinet Decision"],
  "Federal Law No. 10 of 2025": ["Federal Law No."],
  "Federal Law No. 7 of 2014": ["Federal Law No. 7"],
  "International Organisation": ["International"],
  "Politically Exposed Person (PEP)": ["Politically Exposed"],
};

const dfsaGlossaryDefinitionOverrides = {
  FATF: "Means the Financial Action Task Force",
  "FATF Recommendations":
    "Means the publication entitled the “International Standards on Combatting Money Laundering and the Financing of Terrorism and Proliferation” as published and amended from time to time by FATF.",
  "Federal AML legislation":
    "Means all U.A.E. Federal Laws and their implementing regulations relating to money laundering, terrorist financing, and proliferation financing, as well as sanctions compliance, including Federal Law No. 10 of 2025, Federal Law No. 7 of 2014, Cabinet Resolution No. 134 of 2025 and Cabinet Decision No. 74 of 2020.",
  "Federal Law No. 10 of 2025":
    "Means U.A.E. Federal Law No. 10 of 2025 on Anti-Money Laundering, and Combating the Financing of Terrorism and Proliferation Financing.",
  "Federal Law No. 7 of 2014":
    "Means U.A.E Federal Law No. 7 of 2014 on Combating Terrorism Offences.",
  FIU: "The Financial Intelligence Unit of the U.A.E.",
  IMF: "The International Monetary Fund.",
  "International Organisation":
    "Means an organisation established by formal political agreement between member countries, where the agreement has the status of an international treaty, and the organisation is recognised in the law of countries which are members.",
  Law: "Means the Regulatory Law.",
  person: "Means a natural or legal person.",
  "Politically Exposed Person (PEP)":
    "Means a natural person (and includes, where relevant, a family member or close associate) who is or has been entrusted with a prominent public function, whether in the State or elsewhere, including but not limited to, a head of state or of government, senior politician, senior government, judicial or military official, ambassador, senior person in an International Organisation, senior executive of a state owned corporation, an important political party official, or a member of senior management or an individual who has been entrusted with similar functions such as a director or a deputy director. This definition does not include middle ranking or more junior individuals in the above categories.",
  "Relevant Person": "Has the meaning given to that term in Rule 1.1.2.",
  "senior management":
    "Means: (1) Except in AML Rules 7.3.8(2)(a), 7.3.8(3)(a), 7.4.1(e) and 9.2.1(e), in relation to a Relevant Person every member of the Relevant Person’s executive management and includes: (a) for a DIFC entity, every member of the Relevant Person’s Governing Body; (b) for a Branch, the person or persons who control the day to day operations of the Relevant Person in the DIFC and would include, at a minimum, the senior executive officer or equivalent officer, such as the managing director; or (c) for a Registered Auditor, every member of the Relevant Person’s executive management in the U.A.E. (2) In AML Rules 7.3.8(2)(a), 7.3.8(3)(a), 7.4.1(e) and 9.2.1(e), a member of the executive management of the Relevant Person who has significant responsibility for the management of the Relevant Person’s money laundering risk exposure. (3) In relation to a customer that is a body corporate, every member of the body corporate’s Governing Body and the person or persons who control the day-to-day operations of the body corporate, including its senior executive officer, chief operating officer and chief financial officer.",
};

function formatGlossaryTermLabel(term) {
  return String(term || "").replace(/[A-Za-z]+/g, (word) => {
    const upperWord = word.toUpperCase();

    if (glossaryDisplayAcronyms.has(upperWord)) {
      return upperWord;
    }

    return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
  });
}

function normalizeDfsaGlossaryText(text) {
  return String(text || "")
    .replace(/\u00a0/g, " ")
    .replace(/Non-\s+/g, "Non-")
    .replace(/\s+/g, " ")
    .replace(/^following meanings:\s*/i, "")
    .trim();
}

function findDfsaGlossaryTerm(source, term, cursor) {
  const candidateTerms = [term, ...(dfsaGlossaryTermAliases[term] || [])];
  const matches = candidateTerms
    .map((candidateTerm) => {
      const normalizedTerm = escapeRegExp(candidateTerm).replace(/\s+/g, "\\s+");
      // These terms also occur within the preceding definition. The entry
      // itself is followed by "Means", whereas a cross-reference is not.
      const suffix = /^source of (funds|wealth)$/i.test(candidateTerm) ? "(?=\\s+Means\\b)" : "(?=\\s|$)";
      const pattern = new RegExp(`(^|\\s)(${normalizedTerm})${suffix}`, "i");
      const match = source.slice(cursor).match(pattern);

      if (!match || match.index === undefined) {
        return null;
      }

      const prefixLength = match[1]?.length || 0;
      const start = cursor + match.index + prefixLength;

      return {
        start,
        end: start + match[2].length,
      };
    })
    .filter(Boolean)
    .sort((first, second) => first.start - second.start);

  if (matches.length === 0) {
    return null;
  }

  return matches[0];
}

function parseDfsaGlossaryRows(tableText) {
  const source = normalizeDfsaGlossaryText(tableText);
  const foundTerms = [];
  let cursor = 0;

  dfsaGlossaryTerms.forEach((term) => {
    const match = findDfsaGlossaryTerm(source, term, cursor);

    if (!match) {
      return;
    }

    foundTerms.push({
      term,
      start: match.start,
      end: match.end,
    });
    cursor = match.end;
  });

  const rows = foundTerms
    .map((termMatch, index) => {
      const nextTerm = foundTerms[index + 1];
      const definition = source
        .slice(termMatch.end, nextTerm ? nextTerm.start : source.length)
        .replace(/\s+/g, " ")
        .trim();

      return {
        term: termMatch.term,
        definition: dfsaGlossaryDefinitionOverrides[termMatch.term] || definition,
      };
    })
    .filter((row) => row.definition);

  const forceRowAfter = (anchorTerm, term) => {
    const anchorIndex = rows.findIndex((row) => row.term === anchorTerm);

    if (anchorIndex === -1) {
      return;
    }

    const existingIndex = rows.findIndex((row) => row.term === term);
    const row = {
      term,
      definition: dfsaGlossaryDefinitionOverrides[term],
    };

    rows[anchorIndex].definition =
      dfsaGlossaryDefinitionOverrides[anchorTerm] || rows[anchorIndex].definition;

    if (existingIndex !== -1) {
      rows.splice(existingIndex, 1);
    }

    rows.splice(anchorIndex + 1, 0, row);
  };

  const requiredAfterFatf = ["FATF Recommendations", "Federal AML legislation"];
  const fatfIndex = rows.findIndex((row) => row.term === "FATF");

  if (fatfIndex !== -1) {
    requiredAfterFatf
      .slice()
      .reverse()
      .forEach((term) => {
        const existingIndex = rows.findIndex((row) => row.term === term);
        const row = {
          term,
          definition: dfsaGlossaryDefinitionOverrides[term],
        };

        if (existingIndex !== -1) {
          rows.splice(existingIndex, 1);
        }

        rows.splice(fatfIndex + 1, 0, row);
      });
  }

  const imfIndex = rows.findIndex((row) => row.term === "IMF");

  if (imfIndex !== -1) {
    const term = "International Organisation";
    const existingIndex = rows.findIndex((row) => row.term === term);
    const row = {
      term,
      definition: dfsaGlossaryDefinitionOverrides[term],
    };

    if (existingIndex !== -1) {
      rows.splice(existingIndex, 1);
    }

    rows.splice(imfIndex + 1, 0, row);
  }

  forceRowAfter("person", "Politically Exposed Person (PEP)");
  forceRowAfter("Relevant Person", "senior management");

  return rows;
}

function parseColonGlossaryRows(tableText) {
  return String(tableText || "")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .map((line) => {
      const rowMatch = line.match(/^([^:]{2,140}):\s+(.+)$/);

      if (!rowMatch) {
        return null;
      }

      return {
        term: rowMatch[1].trim(),
        definition: rowMatch[2].trim(),
      };
    })
    .filter(Boolean);
}

function splitGlossaryDefinition(definition) {
  const text = String(definition || "").replace(/\s+/g, " ").trim();
  const parentheticalNumberMatches = [...text.matchAll(/(?:^|\s)(\(\d+\))\s/g)];

  if (parentheticalNumberMatches.length > 0) {
    const firstMatch = parentheticalNumberMatches[0];
    const firstMarkerStart = firstMatch.index + (firstMatch[0].startsWith(" ") ? 1 : 0);
    const lead = text.slice(0, firstMarkerStart).trim();
    const items = parentheticalNumberMatches.map((match, index) => {
      const markerStart = match.index + (match[0].startsWith(" ") ? 1 : 0);
      const textStart = markerStart + match[1].length + 1;
      const nextMatch = parentheticalNumberMatches[index + 1];
      const nextStart =
        nextMatch?.index !== undefined
          ? nextMatch.index + (nextMatch[0].startsWith(" ") ? 1 : 0)
          : text.length;
      const itemText = text.slice(textStart, nextStart).trim();
      const childMatches = [...itemText.matchAll(/(?:^|\s)(\([a-z]\))\s/gi)];

      if (childMatches.length === 0) {
        return {
          marker: match[1],
          text: itemText,
          children: [],
        };
      }

      const firstChildMatch = childMatches[0];
      const firstChildStart =
        firstChildMatch.index + (firstChildMatch[0].startsWith(" ") ? 1 : 0);
      const parentText = itemText.slice(0, firstChildStart).trim();
      const children = childMatches.map((childMatch, childIndex) => {
        const childMarkerStart =
          childMatch.index + (childMatch[0].startsWith(" ") ? 1 : 0);
        const childTextStart = childMarkerStart + childMatch[1].length + 1;
        const nextChildMatch = childMatches[childIndex + 1];
        const nextChildStart =
          nextChildMatch?.index !== undefined
            ? nextChildMatch.index + (nextChildMatch[0].startsWith(" ") ? 1 : 0)
            : itemText.length;

        return {
          marker: childMatch[1],
          text: itemText.slice(childTextStart, nextChildStart).trim(),
        };
      });

      return {
        marker: match[1],
        text: parentText,
        children,
      };
    });

    return { lead, leadItem: null, items };
  }

  const firstClauseMatch = text.match(/\s(a\.)\s/i);

  if (!firstClauseMatch || firstClauseMatch.index === undefined) {
    const numberedLeadMatch = text.match(/^\((\d+)\)\s+(.+)$/);

    return numberedLeadMatch
      ? { lead: "", leadItem: { marker: `(${numberedLeadMatch[1]})`, text: numberedLeadMatch[2] }, items: [] }
      : { lead: text, leadItem: null, items: [] };
  }

  let lead = text.slice(0, firstClauseMatch.index).trim();
  let leadItem = null;
  const numberedLeadMatch = lead.match(/^\((\d+)\)\s+(.+)$/);

  if (numberedLeadMatch) {
    lead = "";
    leadItem = {
      marker: `(${numberedLeadMatch[1]})`,
      text: numberedLeadMatch[2],
    };
  }

  const clauseText = text.slice(firstClauseMatch.index).trim();
  const matches = [...clauseText.matchAll(/(?:^|\s)([a-z]\.|\d+\.)\s/gi)];

  if (matches.length === 0) {
    return { lead, leadItem, items: [] };
  }

  const items = matches.map((match, index) => {
    const markerStart = match.index + (match[0].startsWith(" ") ? 1 : 0);
    const textStart = markerStart + match[1].length + 1;
    const nextMatch = matches[index + 1];
    const nextStart =
      nextMatch?.index !== undefined
        ? nextMatch.index + (nextMatch[0].startsWith(" ") ? 1 : 0)
        : clauseText.length;

    return {
      marker: /^\d+\.$/.test(match[1]) ? `(${match[1].slice(0, -1)})` : match[1],
      text: clauseText.slice(textStart, nextStart).trim(),
      children: [],
    };
  });

  return { lead, leadItem, items };
}

function parseDfsaApplicationTableRows(lines) {
  const rows = [];
  let footnote = "";
  let nextIndex = 0;

  while (nextIndex < lines.length) {
    const line = lines[nextIndex];

    if (!line) {
      nextIndex += 1;
      continue;
    }

    if (line.startsWith("*")) {
      footnote = line;
      nextIndex += 1;

      while (nextIndex < lines.length) {
        const nextLine = lines[nextIndex];
        if (!nextLine) {
          nextIndex += 1;
          continue;
        }

        footnote = appendRegulationLine(footnote, nextLine);
        nextIndex += 1;
      }

      break;
    }

    const rowMatch = line.match(
      /^(Authorised Person(?:, other than a Credit)?|Representative Office|Registered Auditor|DNFBP|NPO)\s+(.+)$/
    );

    if (!rowMatch) {
      break;
    }

    let person = rowMatch[1];
    let chapters = rowMatch[2];

    if (
      person === "Authorised Person, other than a Credit" &&
      lines[nextIndex + 1] &&
      !/\d/.test(lines[nextIndex + 1].trim())
    ) {
      person = `${person} ${lines[nextIndex + 1].trim()}`;
      nextIndex += 1;
    }

    rows.push({
      person,
      chapters: chapters
        .replace(/\s*-\s*/g, "-")
        .replace(/(\d+-\d+)\s+(\d+-\d+)/g, "$1, $2")
        .replace(/\s+/g, " ")
        .trim(),
    });
    nextIndex += 1;
  }

  return { rows, footnote, consumed: nextIndex };
}

function stripDefinitionTermContinuation(term, line) {
  let nextLine = String(line || "").replace(/\s+/g, " ").trim();

  if (!nextLine) {
    return "";
  }

  const termWords = String(term || "")
    .replace(/[()]/g, "")
    .split(/\s+/)
    .filter(Boolean);

  for (let wordCount = Math.min(3, termWords.length); wordCount >= 1; wordCount -= 1) {
    const prefix = termWords.slice(-wordCount).join(" ");
    const escapedPrefix = escapeRegExp(prefix);
    const prefixRegex = new RegExp(`^${escapedPrefix}\\b\\s*`, "i");

    if (prefixRegex.test(nextLine)) {
      nextLine = nextLine.replace(prefixRegex, "").trim();
      break;
    }
  }

  for (let startIndex = 0; startIndex < termWords.length; startIndex += 1) {
    for (let wordCount = Math.min(3, termWords.length - startIndex); wordCount >= 1; wordCount -= 1) {
      const prefix = termWords.slice(startIndex, startIndex + wordCount).join(" ");
      const escapedPrefix = escapeRegExp(prefix);
      const prefixRegex = new RegExp(`^${escapedPrefix}\\b\\s*`, "i");

      if (prefixRegex.test(nextLine)) {
        nextLine = nextLine.replace(prefixRegex, "").trim();
        return nextLine;
      }
    }
  }

  return nextLine;
}

function parseCabinetDefinitionRows(lines) {
  const cabinetDefinitionOverrides = {
    "Financial Institutions":
      "Any person engaging in one or more financial activities or operations for the benefit of, or on behalf of, a Customer, as stipulated in Article (2) of this Resolution.",
    "Designated Non-Financial Businesses and Professions":
      "Any person engaging in one or more commercial or professional activities or businesses for the benefit of, or on behalf of, a Customer, as stipulated in Article (3) of this Resolution.",
    "Virtual Asset Service Provider":
      "Any person who, as a commercial activity, conducts one or more Virtual Asset activities or related operations for the benefit of, or on behalf of, another natural or legal person, as stipulated in Article (4) of this Resolution.",
    "Beneficial Owner":
      "The natural person who owns or exercises ultimate effective control over the Customer, or the natural person on whose behalf Transactions are conducted, including any natural person exercising ultimate effective control over a legal person or Legal Arrangement, whether directly or through a chain of ownership or control or by any other indirect means, and who is identified, whether one or more persons, in accordance with Article (10) of this Resolution.",
    "Decree by Law":
      "Federal Decree by Law No. (10) of 2025 Regarding Anti-Money Laundering, and Combating the Financing of Terrorism and Proliferation Financing.",
  };
  const rows = [];
  let currentRow = null;
  let nextIndex = 0;

  while (nextIndex < lines.length) {
    const line = lines[nextIndex];
    const definitionMatch = line.match(/^([A-Z][A-Za-z /()-]{1,60})\s*:\s+(.*)$/);

    if (definitionMatch) {
      const term = cleanDefinitionTerm(definitionMatch[1], definitionMatch[2]);
      currentRow = {
        term,
        definition: definitionMatch[2].replace(/\s+/g, " ").trim(),
      };
      rows.push(currentRow);
      nextIndex += 1;
      continue;
    }

    if (currentRow) {
      const nextText = stripDefinitionTermContinuation(currentRow.term, line);
      if (nextText) {
        currentRow.definition = appendRegulationLine(currentRow.definition, nextText);
      }
    }

    nextIndex += 1;
  }

  return rows.map((row) => {
    const cleanedDefinition = row.definition
      .replace(/\bArticle\s+\((\d+)\)\s+of\b/g, "Article ($1) of")
      .replace(/\bFederal Decree by Law No\.\s+\((\d+)\)\s+of\b/g, "Federal Decree by Law No. ($1) of")
      .replace(/\s+/g, " ")
      .trim();

    return {
      ...row,
      definition: cabinetDefinitionOverrides[row.term] || cleanedDefinition,
    };
  });
}

function formatRegulationText(text) {
  const lines = String(text || "")
    .split("\n")
    .map((line) => line.trim());
  const blocks = [];
  let currentParagraph = null;
  let currentClause = null;
  let currentParentClause = null;
  let currentLetterClause = null;
  let currentRomanClause = null;
  let currentDefinition = null;
  let blockId = 0;
  let subsectionIndex = 0;

  const nextBlockId = (type, marker = "") => `${type}-${marker}-${blockId++}`;

  const flushParagraph = () => {
    if (!currentParagraph) {
      return;
    }
    blocks.push({ id: nextBlockId("paragraph"), type: "paragraph", text: currentParagraph });
    currentParagraph = null;
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex];
    if (!line) {
      flushParagraph();
      currentClause = null;
      currentDefinition = null;
      continue;
    }

    const parenthesizedClauseMatch = line.match(/^\((\d+)\)\s+(.*)$/);
    const clauseMatch = parenthesizedClauseMatch || line.match(/^(\d+|[a-z])[\.-]\s+(.*)$/);
    const nestedClauseMatch = line.match(/^(\d+)\)\s+(.*)$/);
    const romanClauseMatch = line.match(/^(i|ii|iii|iv|v|vi|vii|viii|ix|x)\.\s+(.*)$/i);
    const upperAlphaClauseMatch = line.match(/^([A-Z])\.\s+(.*)$/);
    const inlineBareRuleMatch = line.match(/^(\d+\.\d+[A-Z]\.\d+)\s+(.+)$/i);
    const subsectionMatch = line.match(/^(\d+(?:\.\d+)+\.?)\s+(.+)$/);
    const ruleLabelMatch = line.match(/^Rule\s+([A-Za-z0-9.]+)$/);
    const bulletMatch = line.match(/^(\u2022|-)\s+(.*)$/);
    const definitionMatch = line.match(/^([A-Z][A-Za-z /()-]{1,60})\s*:\s+(.*)$/);

    // PDF text can place the next division heading directly after an article's
    // final clause. Keep it separate from that clause and its nested children.
    if (/^Division (?:One|Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten|Eleven|Twelve)$/i.test(line)) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;
      const divisionTitle = lines[lineIndex + 1];
      const hasDivisionTitle = isReaderContentHeading(divisionTitle);
      blocks.push({
        id: nextBlockId("content-heading"),
        type: "contentHeading",
        text: hasDivisionTitle ? `${line} — ${divisionTitle}` : line,
      });
      if (hasDivisionTitle) lineIndex += 1;
      continue;
    }

    if (/^(On|Corresponding to):\s+/i.test(line) || /^On\s+\d/i.test(line) || /^Corresponding to\s+\d/i.test(line)) {
      const inlineDateLine = line.replace(/^(On|Corresponding to):\s+/i, "$1 ");
      if (currentClause) {
        currentClause.text = appendRegulationLine(currentClause.text, inlineDateLine);
      } else if (currentDefinition) {
        currentDefinition.text = appendRegulationLine(currentDefinition.text, inlineDateLine);
      } else {
        currentParagraph = appendRegulationLine(currentParagraph, inlineDateLine);
      }
      continue;
    }

    if (/^\((?:19|20)\d{2}\)\s+/i.test(line)) {
      if (currentClause) {
        currentClause.text = appendRegulationLine(currentClause.text, line);
      } else if (currentDefinition) {
        currentDefinition.text = appendRegulationLine(currentDefinition.text, line);
      } else {
        currentParagraph = appendRegulationLine(currentParagraph, line);
      }
      continue;
    }

    if (currentClause && isGuidanceCategoryHeading(line)) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;
      blocks.push({
        id: nextBlockId("content-heading"),
        type: "contentHeading",
        text: line.replace(/\s+/g, " ").trim(),
      });
      continue;
    }

    if (/^The definitions set forth in Federal Decree by Law No\. \(10\) of 2025/i.test(line)) {
      let intro = line;
      let nextIndex = lineIndex + 1;

      while (nextIndex < lines.length) {
        intro = appendRegulationLine(intro, lines[nextIndex]);
        if (lines[nextIndex].endsWith(":")) {
          break;
        }
        nextIndex += 1;
      }

      flushParagraph();
      blocks.push({
        id: nextBlockId("paragraph"),
        type: "paragraph",
        text: intro,
      });

      const rows = parseCabinetDefinitionRows(lines.slice(nextIndex + 1));
      blocks.push({
        id: nextBlockId("glossary-table"),
        type: "glossaryTable",
        text: rows.map((row) => `${row.term} ${row.definition}`).join(" "),
        rows,
        splitDefinitions: false,
      });
      break;
    }

    if (
      /^In application of the provisions herein, the following words and expressions/i.test(line) ||
      /^Whenever mentioned in this Resolution, the following words and expressions/i.test(line) ||
      /^In the application of the provisions of this law, the following words and phrases/i.test(line)
    ) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;

      blocks.push({
        id: nextBlockId("paragraph"),
        type: "paragraph",
        text: line.replace(/\s+/g, " ").trim(),
      });

      const rows = parseColonGlossaryRows(lines.slice(lineIndex + 1).join("\n"));
      blocks.push({
        id: nextBlockId("glossary-table"),
        type: "glossaryTable",
        text: rows.map((row) => `${row.term} ${row.definition}`).join(" "),
        rows,
        splitDefinitions: false,
      });
      break;
    }

    if (/^Guidance$/i.test(line)) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;
      blocks.push({
        id: nextBlockId("guidance-start"),
        type: "guidanceStart",
        text: line.replace(/\s+/g, " ").trim(),
      });
      continue;
    }

    if (/following meanings:?$/i.test(line)) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;

      const glossaryLines = [];
      let nextIndex = lineIndex + 1;

      while (nextIndex < lines.length) {
        glossaryLines.push(lines[nextIndex]);
        nextIndex += 1;
      }

      const glossaryText = [line, ...glossaryLines].join("\n");
      const colonRows = parseColonGlossaryRows(glossaryLines.join("\n"));
      const rows = colonRows.length > 5 ? colonRows : parseDfsaGlossaryRows(glossaryText);
      blocks.push({
        id: nextBlockId("glossary-table"),
        type: "glossaryTable",
        text: rows.map((row) => `${row.term} ${row.definition}`).join(" "),
        rows,
      });
      break;
    }

    if (ruleLabelMatch) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;
      blocks.push({
        id: nextBlockId("rule-label", ruleLabelMatch[1]),
        type: "ruleLabel",
        text: `Rule ${ruleLabelMatch[1]}`,
      });
      continue;
    }

    if (inlineBareRuleMatch) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;
      blocks.push({
        id: nextBlockId("rule-label", inlineBareRuleMatch[1]),
        type: "ruleLabel",
        text: `Rule ${inlineBareRuleMatch[1]}`,
      });
      lines.splice(lineIndex + 1, 0, inlineBareRuleMatch[2]);
      continue;
    }

    if (/^Relevant Person\s+Applicable Chapter(?:\(s\)|s)$/i.test(line)) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;

      const table = parseDfsaApplicationTableRows(lines.slice(lineIndex + 1));
      blocks.push({
        id: nextBlockId("application-table"),
        type: "applicationTable",
        text: [
          "Relevant Person Applicable Chapters",
          ...table.rows.map((row) => `${row.person} ${row.chapters}`),
          table.footnote,
        ]
          .filter(Boolean)
          .join(" "),
        rows: table.rows,
        footnote: table.footnote,
      });
      lineIndex += table.consumed;
      continue;
    }

    if (subsectionMatch) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;
      blocks.push({
        id: nextBlockId("subheading", subsectionMatch[1]),
        type: "subheading",
        text: `${subsectionMatch[1]} ${subsectionMatch[2]}`.replace(/\s+/g, " ").trim(),
        subsectionIndex: subsectionIndex++,
      });
      continue;
    }

    if (upperAlphaClauseMatch && currentRomanClause) {
      flushParagraph();
      const nestedClause = {
        id: nextBlockId("nested", upperAlphaClauseMatch[1]),
        type: "clause",
        marker: `${upperAlphaClauseMatch[1]}.`,
        level: "nested",
        text: upperAlphaClauseMatch[2],
        children: [],
      };
      currentRomanClause.children = [...(currentRomanClause.children || []), nestedClause];
      currentClause = currentRomanClause;
      continue;
    }

    if (!currentParagraph && !currentClause && !currentDefinition && isReaderContentHeading(line)) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;
      blocks.push({
        id: nextBlockId("content-heading"),
        type: "contentHeading",
        text: line.replace(/\s+/g, " ").trim(),
      });
      continue;
    }

    if (bulletMatch) {
      if (bulletMatch[1] === "-" && currentClause?.marker === "\u2022") {
        currentClause.children = [
          ...(currentClause.children || []),
          {
            id: nextBlockId("nested-bullet", currentClause.children?.length || 0),
            type: "clause",
            marker: "\u2022",
            level: "nested",
            text: bulletMatch[2],
          },
        ];
        continue;
      }

      flushParagraph();
      currentDefinition = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentClause = {
        id: nextBlockId("clause", "bullet"),
        type: "clause",
        marker: "\u2022",
        level: "sub",
        text: bulletMatch[2],
        children: [],
      };
      blocks.push(currentClause);
      continue;
    }

    if (nestedClauseMatch && currentClause) {
      const nestedClause = {
        id: nextBlockId("nested", nestedClauseMatch[1]),
        type: "clause",
        marker: `${nestedClauseMatch[1]})`,
        level: "nested",
        text: nestedClauseMatch[2],
      };
      currentClause.children = [...(currentClause.children || []), nestedClause];
      currentRomanClause = null;
      continue;
    }

    if (
      romanClauseMatch &&
      (currentClause?.level === "sub" || currentLetterClause) &&
      /:\s*$/.test((currentClause?.level === "sub" ? currentClause : currentLetterClause)?.text || "")
    ) {
      const targetClause = currentClause?.level === "sub" ? currentClause : currentLetterClause;
      const nestedClause = {
        id: nextBlockId("nested", romanClauseMatch[1]),
        type: "clause",
        marker: `${romanClauseMatch[1].toLowerCase()}.`,
        level: "nested",
        text: romanClauseMatch[2],
      };
      targetClause.children = [...(targetClause.children || []), nestedClause];
      currentClause = targetClause;
      currentRomanClause = nestedClause;
      continue;
    }

    if (currentDefinition && clauseMatch) {
      currentClause = {
        id: nextBlockId("definition-clause", clauseMatch[1]),
        type: "definitionClause",
        marker: `${clauseMatch[1]}.`,
        level: /^[a-z]$/.test(clauseMatch[1]) ? "sub" : "main",
        text: clauseMatch[2],
        children: [],
      };
      currentDefinition.items.push(currentClause);
      continue;
    }

    if (clauseMatch) {
      flushParagraph();
      currentDefinition = null;
      const isSubClause = /^[a-z]$/.test(clauseMatch[1]);
      currentClause = {
        id: nextBlockId("clause", clauseMatch[1]),
        type: "clause",
        marker: `${clauseMatch[1]}.`,
        level: isSubClause ? "sub" : "main",
        text: clauseMatch[2],
        children: [],
      };

      if (isSubClause && currentParentClause) {
        currentParentClause.children = [...(currentParentClause.children || []), currentClause];
        currentLetterClause = currentClause;
        currentRomanClause = null;
        continue;
      }

      blocks.push(currentClause);
      currentLetterClause = isSubClause ? currentClause : null;
      currentRomanClause = null;
      currentParentClause = isSubClause ? null : currentClause;
      continue;
    }

    if (definitionMatch) {
      flushParagraph();
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = {
        id: nextBlockId("definition", definitionMatch[1]),
        type: "definition",
        term: cleanDefinitionTerm(definitionMatch[1], definitionMatch[2]),
        text: definitionMatch[2].replace(/\s+/g, " ").trim(),
        items: [],
      };
      blocks.push(currentDefinition);
      continue;
    }

    if (currentClause && isClauseBreakingContentHeading(line) && isReaderContentHeading(line)) {
      currentClause = null;
      currentParentClause = null;
      currentLetterClause = null;
      currentRomanClause = null;
      currentDefinition = null;
      blocks.push({
        id: nextBlockId("content-heading"),
        type: "contentHeading",
        text: line.replace(/\s+/g, " ").trim(),
      });
      continue;
    }

    if (currentClause) {
      const lastChild = currentClause.children?.[currentClause.children.length - 1];
      if (lastChild) {
        lastChild.text = appendRegulationLine(lastChild.text, line);
      } else {
        currentClause.text = appendRegulationLine(currentClause.text, line);
      }
      continue;
    }

    if (currentDefinition) {
      if (currentClause?.type === "definitionClause") {
        currentClause.text = appendRegulationLine(currentClause.text, line);
      } else {
        currentDefinition.text = appendRegulationLine(currentDefinition.text, line);
      }
      continue;
    }

    currentParagraph = appendRegulationLine(currentParagraph, line);
  }

  flushParagraph();
  return blocks;
}

function getReaderArticleKey(chapter, article) {
  return `${chapter.chapter}-${article.label}`;
}

function slugifyRegulatoryId(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getReaderSubsectionId(keyPrefix, index) {
  return `regulatory-reader-subsection-${slugifyRegulatoryId(keyPrefix)}-${index}`;
}

function normalizeTopicReference(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function matchesTopicChapter(chapter, sectionRef) {
  const normalizedChapter = normalizeTopicReference(sectionRef.chapter);
  const normalizedChapterTitle = normalizeTopicReference(sectionRef.chapterTitle);

  return (
    (!normalizedChapter || normalizeTopicReference(chapter.chapter) === normalizedChapter) &&
    (!normalizedChapterTitle || normalizeTopicReference(chapter.title) === normalizedChapterTitle)
  );
}

function extractTopicBodySubsection(chapter, article, sectionRef) {
  const targetNumber = normalizeTopicReference(sectionRef.sectionNumber || sectionRef.subsectionNumber);

  if (!targetNumber || !article?.body) {
    return null;
  }

  const headingPattern = /^(\d+(?:\.\d+)+)\.?\s+(.+)$/gm;
  const headings = [];
  let match;

  while ((match = headingPattern.exec(article.body)) !== null) {
    headings.push({
      number: normalizeTopicReference(match[1]),
      rawNumber: match[1],
      title: match[2].trim(),
      start: match.index,
      contentStart: headingPattern.lastIndex,
    });
  }

  const headingIndex = headings.findIndex((heading) => heading.number === targetNumber);

  if (headingIndex === -1) {
    return null;
  }

  const heading = headings[headingIndex];
  const nextHeading = headings[headingIndex + 1];
  const body = article.body.slice(heading.contentStart, nextHeading?.start ?? article.body.length).trim();

  return {
    chapter,
    article: {
      ...article,
      number: heading.rawNumber,
      label: `Section ${heading.rawNumber}`,
      title: heading.title,
      body,
    },
  };
}

function extractTopicDefinition(chapter, article, sectionRef) {
  const targetTerm = normalizeTopicReference(sectionRef.definitionTerm);

  if (!targetTerm || !article?.body) {
    return null;
  }

  // FI/DNFBP glossaries preserve PDF columns. Use the term column to stop at
  // the next entry, including entries whose term/colon wraps onto another line.
  const glossaryLines = article.body.split("\n");
  const columnHeader = glossaryLines.find((line) => /^\s*Term\s{2,}Definition\s*$/.test(line));
  if (columnHeader) {
    const definitionColumn = columnHeader.indexOf("Definition");
    const rowIndex = glossaryLines.findIndex((line) =>
      normalizeTopicReference(line.slice(0, definitionColumn).replace(/:\s*$/, "")) === targetTerm
    );
    if (rowIndex !== -1) {
      const definitionLines = [glossaryLines[rowIndex].slice(definitionColumn).trim()];
      for (let index = rowIndex + 1; index < glossaryLines.length; index += 1) {
        const line = glossaryLines[index];
        if (line.slice(0, definitionColumn).trim()) break;
        definitionLines.push(line.slice(definitionColumn).trim());
      }
      const definition = definitionLines.join(" ").replace(/\s+/g, " ").trim();
      if (definition) {
        return {
          chapter,
          article: {
            ...article,
            title: "",
            body: "",
            blocks: [{
              id: `topic-definition-${slugifyRegulatoryId(targetTerm)}`,
              type: "definition",
              term: sectionRef.definitionTerm,
              text: definition,
              items: [],
            }],
          },
        };
      }
    }
  }

  const blocks = formatRegulationText(article.body);
  const termMatches = (candidateTerm) => {
    const normalizedCandidateTerm = normalizeTopicReference(candidateTerm);

    return (
      normalizedCandidateTerm === targetTerm ||
      (targetTerm === "suspicious activities" && normalizedCandidateTerm === "suspicious transactions")
    );
  };
  const glossaryBlock = blocks.find((block) => block.type === "glossaryTable");
  const row = glossaryBlock?.rows?.find(
    (candidate) =>
      termMatches(candidate.term) ||
      termMatches(formatGlossaryTermLabel(candidate.term))
  );

  if (row) {
    const termLabel = formatGlossaryTermLabel(row.term);

    return {
      chapter,
      article: {
        ...article,
        label: article.label,
        title: "",
        body: "",
        blocks: [
          {
            id: `topic-definition-${slugifyRegulatoryId(termLabel)}`,
            type: "glossaryTable",
            text: `${termLabel} ${row.definition}`,
            rows: [row],
            splitDefinitions: glossaryBlock.splitDefinitions,
          },
        ],
      },
    };
  }

  const definitionBlock = blocks.find(
    (block) => block.type === "definition" && termMatches(block.term)
  );

  if (!definitionBlock) {
    return null;
  }

  return {
    chapter,
    article: {
      ...article,
      label: article.label,
      title: "",
      body: "",
      blocks: [definitionBlock],
    },
  };
}

function normalizeTopicClauseMarker(value) {
  return normalizeTopicReference(value).replace(/[().]/g, "");
}

function filterTopicClauseByPath(blocks, clausePath) {
  if (!Array.isArray(clausePath) || clausePath.length === 0) {
    return null;
  }

  const normalizedPath = clausePath.map(normalizeTopicClauseMarker).filter(Boolean);

  if (normalizedPath.length === 0) {
    return null;
  }

  const findClauseAtDepth = (clauses, depth) => {
    const targetMarker = normalizedPath[depth];
    const clause = (clauses || []).find(
      (candidate) =>
        candidate.type === "clause" && normalizeTopicClauseMarker(candidate.marker) === targetMarker
    );

    if (!clause) {
      return null;
    }

    const clonedClause = {
      ...clause,
      children: depth === normalizedPath.length - 1 ? clause.children || [] : [],
    };

    if (depth < normalizedPath.length - 1) {
      const child = findClauseAtDepth(clause.children || [], depth + 1);

      if (!child) {
        return null;
      }

      clonedClause.children = [child];
    }

    return clonedClause;
  };

  const clause = findClauseAtDepth(blocks, 0);

  return clause ? [clause] : null;
}

function extractTopicClausePath(chapter, article, sectionRef) {
  const clausePaths = sectionRef.clausePaths || [sectionRef.clausePath];
  if (!clausePaths.length || clausePaths.some((path) => !Array.isArray(path) || path.length === 0)) {
    return null;
  }

  const blocks = getArticleBlocks(article);
  const selectedClauses = clausePaths.map((path) => filterTopicClauseByPath(blocks, path));

  if (selectedClauses.some((clauses) => !clauses)) {
    return null;
  }
  // Keep shared parent clauses once, with the selected children in source order.
  const selectedIds = new Set();
  const collectIds = (clauses) => clauses.forEach((clause) => {
    selectedIds.add(clause.id);
    collectIds(clause.children || []);
  });
  collectIds(selectedClauses.flat());
  const keepSelected = (clauses) => clauses
    .filter((clause) => selectedIds.has(clause.id))
    .map((clause) => ({
      ...clause,
      children: keepSelected(clause.children || []),
    }));
  const filteredBlocks = preserveListContext(blocks, keepSelected(blocks));

  return {
    chapter,
    article: {
      ...article,
      title: article.label,
      body: "",
      blocks: filteredBlocks,
    },
  };
}

function extractTopicRuleHeading(chapter, article, sectionRef) {
  const targetRule = normalizeTopicReference(sectionRef.ruleNumber);

  if (!targetRule || !article?.body) {
    return null;
  }

  const rulePattern = /^Rule\s+(\d+(?:\.\d+)+[A-Z]?)\s*$/gim;
  const rules = [];
  let match;

  while ((match = rulePattern.exec(article.body)) !== null) {
    rules.push({
      number: normalizeTopicReference(match[1]),
      rawNumber: match[1],
      start: match.index,
    });
  }

  const ruleIndex = rules.findIndex((rule) => rule.number === targetRule);

  if (ruleIndex === -1) {
    return null;
  }

  const rule = rules[ruleIndex];
  const nextRule = rules[ruleIndex + 1];
  const guidanceMatch = article.body.slice(rule.start).match(/\nGuidance(?: on[^\n]*)?\s*\n/i);
  const guidanceStart =
    guidanceMatch && rule.start + guidanceMatch.index > rule.start
      ? rule.start + guidanceMatch.index
      : null;
  const bodyEnd = Math.min(
    nextRule?.start ?? article.body.length,
    guidanceStart ?? article.body.length
  );
  const body = article.body.slice(rule.start, bodyEnd).trim();
  const blocks = formatRegulationText(body);
  while (blocks[blocks.length - 1]?.type === "contentHeading") blocks.pop();

  return {
    chapter,
    article: {
      ...article,
      number: rule.rawNumber,
      label: `Rule ${rule.rawNumber}`,
      title: "",
      body,
      blocks,
    },
  };
}

function extractTopicGuidanceParagraph(chapter, article, sectionRef) {
  const targetParagraph = normalizeTopicReference(sectionRef.guidanceNumber);

  if (!targetParagraph || !article?.body) {
    return null;
  }

  const paragraphPattern = /^(\d+)\.\s+(.+)$/gm;
  const paragraphs = [];
  let match;

  while ((match = paragraphPattern.exec(article.body)) !== null) {
    paragraphs.push({
      number: normalizeTopicReference(match[1]),
      rawNumber: match[1],
      start: match.index,
      contentStart: match.index,
    });
  }

  const paragraphIndex = paragraphs.findIndex((paragraph) => paragraph.number === targetParagraph);

  if (paragraphIndex === -1) {
    return null;
  }

  const paragraph = paragraphs[paragraphIndex];
  const nextParagraph = paragraphs[paragraphIndex + 1];
  const contextualExcerpt = extractTopicClausePath(chapter, article, {
    clausePath: [paragraph.rawNumber],
  });
  const body = article.body
    .slice(paragraph.contentStart, nextParagraph?.start ?? article.body.length)
    .trim();

  return {
    chapter,
    article: {
      ...article,
      number: paragraph.rawNumber,
      label: "Guidance",
      title: `Guidance ${paragraph.rawNumber}`,
      body: contextualExcerpt ? "" : body,
      ...(contextualExcerpt ? { blocks: contextualExcerpt.article.blocks } : {}),
    },
  };
}

function extractTopicNamedGuidance(chapter, article, sectionRef) {
  const lines = String(article.body || "").split("\n");
  const start = lines.findIndex((line) =>
    normalizeTopicReference(line) === normalizeTopicReference(sectionRef.guidanceHeading)
  );
  if (start < 0) return null;
  const relativeEnd = lines.slice(start + 1).findIndex((line) => /^\s*(?:Guidance\b|Rule\s+[\d.]+\s*$)/i.test(line));
  const end = relativeEnd < 0 ? lines.length : start + 1 + relativeEnd;
  const scopedArticle = {
    ...article,
    label: `Section ${article.number} — ${sectionRef.guidanceTitle || "Customer risk assessment guidance"}`,
    title: sectionRef.guidanceTitle || sectionRef.guidanceHeading,
    body: lines.slice(start + 1, end).join("\n").trim(),
    blocks: undefined,
  };
  const excerpt = extractTopicClausePath(chapter, scopedArticle, {
    clausePaths: sectionRef.guidanceNumbers.map((number) => [number]),
  });
  return excerpt ? { ...excerpt, article: { ...excerpt.article, title: scopedArticle.title } } : null;
}

function extractTopicBullet(chapter, article, sectionRef) {
  const blocks = getArticleBlocks(article);
  const headingIndex = sectionRef.headingText ? blocks.findIndex((block) =>
    block.type === "contentHeading" &&
    normalizeTopicReference(block.text) === normalizeTopicReference(sectionRef.headingText)
  ) : -1;
  if (sectionRef.headingText && headingIndex === -1) return null;

  const followingBlocks = blocks.slice(headingIndex + 1);
  const firstBulletIndex = followingBlocks.findIndex((block) => block.marker === "•");
  const prefixes = sectionRef.bulletStartsWithAny || [sectionRef.bulletStartsWith];
  const bullets = followingBlocks.filter((block) =>
    block.marker === "•" && prefixes.some((prefix) =>
      normalizeTopicReference(block.text).startsWith(normalizeTopicReference(prefix)))
  );
  if (firstBulletIndex === -1 || !bullets.length || prefixes.some((prefix) =>
    !bullets.some((bullet) => normalizeTopicReference(bullet.text).startsWith(normalizeTopicReference(prefix)))
  )) return null;

  return {
    chapter,
    article: {
      ...article,
      body: "",
      blocks: [
        ...(headingIndex >= 0 ? [blocks[headingIndex]] : []),
        ...followingBlocks.slice(0, firstBulletIndex),
        ...bullets,
      ],
    },
  };
}

function extractTopicSelectedText(chapter, article, sectionRef) {
  const prefixes = sectionRef.textStartsWithAny.map(normalizeTopicReference);
  const found = new Set();
  function select(blocks) {
    const selected = blocks.flatMap((block) => {
      const matches = prefixes.filter((prefix) => normalizeTopicReference(block.text).startsWith(prefix));
      matches.forEach((prefix) => found.add(prefix));
      if (matches.length) return [block];
      const children = block.children?.length ? select(block.children) : [];
      return children.length ? [{ ...block, children }] : [];
    });
    return preserveListContext(blocks, selected);
  }
  const blocks = select(getArticleBlocks(article));
  if (!blocks.length || prefixes.some((prefix) => !found.has(prefix))) return null;
  return { chapter, article: { ...article, body: "", blocks } };
}

function extractTopicDefinitions(chapter, article, sectionRef) {
  const excerpts = sectionRef.definitionTerms.map((definitionTerm) =>
    extractTopicDefinition(chapter, article, { definitionTerm })
  );
  if (excerpts.some((excerpt) => !excerpt)) return null;
  return { chapter, article: { ...article, title: "", body: "",
    blocks: excerpts.flatMap((excerpt) => excerpt.article.blocks) } };
}

function findTopicSections(readerData, sectionRef) {
  if (!readerData?.chapters?.length || !sectionRef) {
    return [];
  }

  const normalizedArticleNumber = normalizeTopicReference(
    sectionRef.articleNumber || sectionRef.sectionNumber || sectionRef.subsectionNumber
  );
  const normalizedArticleLabel = normalizeTopicReference(sectionRef.articleLabel || sectionRef.sectionLabel);
  const wantsWholeChapter =
    Boolean(sectionRef.chapter || sectionRef.chapterTitle) &&
    !normalizedArticleNumber &&
    !normalizedArticleLabel;
  const matches = [];

  for (const chapter of readerData.chapters) {
    if (!matchesTopicChapter(chapter, sectionRef)) {
      continue;
    }

    if (wantsWholeChapter) {
      (chapter.articles || []).forEach((article) => {
        matches.push({ chapter, article, fullArticle: article });
      });
      continue;
    }

    (chapter.articles || []).forEach((candidate) => {
      const addMatch = (match) => matches.push({ ...match, fullArticle: candidate });
      if (sectionRef.ruleNumber) {
        const rule = extractTopicRuleHeading(chapter, candidate, sectionRef);
        if (rule) {
          const excerpt = sectionRef.clausePath || sectionRef.clausePaths
            ? extractTopicClausePath(chapter, rule.article, sectionRef) : rule;
          if (excerpt) addMatch(excerpt);
        }
        return;
      }
      const candidateNumber = normalizeTopicReference(candidate.number);
      const candidateLabel = normalizeTopicReference(candidate.label);
      const candidateTitle = normalizeTopicReference(candidate.title);
      const directMatch =
        (normalizedArticleNumber && candidateNumber === normalizedArticleNumber) ||
        (normalizedArticleLabel &&
          (candidateLabel === normalizedArticleLabel || candidateTitle === normalizedArticleLabel));

      if (directMatch) {
        if (sectionRef.definitionTerms || sectionRef.textStartsWithAny) {
          const excerpt = sectionRef.definitionTerms
            ? extractTopicDefinitions(chapter, candidate, sectionRef)
            : extractTopicSelectedText(chapter, candidate, sectionRef);
          if (excerpt) addMatch(excerpt);
          return;
        }
        if (sectionRef.guidanceHeading) {
          const excerpt = extractTopicNamedGuidance(chapter, candidate, sectionRef);
          if (excerpt) addMatch(excerpt);
          return;
        }
        if (sectionRef.bulletStartsWith || sectionRef.bulletStartsWithAny) {
          const excerpt = extractTopicBullet(chapter, candidate, sectionRef);
          if (excerpt) addMatch(excerpt);
          return;
        }
        addMatch(
          extractTopicDefinition(chapter, candidate, sectionRef) ||
            extractTopicClausePath(chapter, candidate, sectionRef) ||
            extractTopicRuleHeading(chapter, candidate, sectionRef) ||
            extractTopicGuidanceParagraph(chapter, candidate, sectionRef) ||
            { chapter, article: candidate }
        );
        return;
      }

      const bodyRule = extractTopicRuleHeading(chapter, candidate, sectionRef);

      if (bodyRule) {
        addMatch(bodyRule);
        return;
      }

      const bodySubsection = extractTopicBodySubsection(chapter, candidate, sectionRef);

      if (bodySubsection) {
        if (sectionRef.textStartsWithAny) {
          const excerpt = extractTopicSelectedText(chapter, bodySubsection.article, sectionRef);
          if (excerpt) addMatch(excerpt);
          return;
        }
        if (sectionRef.bulletStartsWith || sectionRef.bulletStartsWithAny) {
          const excerpt = extractTopicBullet(chapter, bodySubsection.article, sectionRef);
          if (excerpt) addMatch(excerpt);
          return;
        }
        addMatch(
          extractTopicDefinition(bodySubsection.chapter, bodySubsection.article, sectionRef) ||
            extractTopicClausePath(bodySubsection.chapter, bodySubsection.article, sectionRef) ||
            extractTopicRuleHeading(bodySubsection.chapter, bodySubsection.article, sectionRef) ||
            extractTopicGuidanceParagraph(bodySubsection.chapter, bodySubsection.article, sectionRef) ||
            bodySubsection
        );
      }
    });
  }

  return matches;
}

function resolveRegulatoryTopic(topic, documentMap) {
  if (!topic) {
    return null;
  }

  const documents = (topic.documents || [])
    .map((topicDocument) => {
      const regulation = documentMap.get(topicDocument.documentId);

      if (!regulation?.readerData) {
        return null;
      }

      const sections = (topicDocument.sections || [])
        .flatMap((sectionRef) => {
          const matches = findTopicSections(regulation.readerData, sectionRef);

          return matches.map((match) => ({
            ...sectionRef,
            chapter: match.chapter,
            article: match.article,
            fullArticle: match.fullArticle,
          }));
        })
        .filter(Boolean);

      if (sections.length === 0) {
        return null;
      }

      return {
        ...topicDocument,
        regulation,
        sections,
      };
    })
    .filter(Boolean);

  return {
    ...topic,
    documents,
  };
}

const CLAUDE_INLINE_PATTERN = /\*\*(.+?)\*\*|\[\[Source:\s*([^\]]+)\]\]/g;
const CLAUDE_SOURCE_PATTERN = /\[\[Source:\s*([^\]]+)\]\]/g;

function getClaudeCitationKey(citationText) {
  return String(citationText || "").replace(/\s+/g, " ").trim().toLowerCase();
}

function splitClaudeCitation(citationText) {
  const text = String(citationText || "").replace(/\s+/g, " ").trim();
  const commaIndex = text.lastIndexOf(",");

  if (commaIndex === -1) {
    return { title: text, locator: "" };
  }

  return {
    title: text.slice(0, commaIndex).trim(),
    locator: text.slice(commaIndex + 1).trim(),
  };
}

function extractClaudeCitations(answerText) {
  const citations = [];
  const seen = new Map();
  let match;
  CLAUDE_SOURCE_PATTERN.lastIndex = 0;

  while ((match = CLAUDE_SOURCE_PATTERN.exec(String(answerText || "")))) {
    const text = match[1].trim();
    const key = getClaudeCitationKey(text);

    if (!key || seen.has(key)) {
      continue;
    }

    const id = citations.length + 1;
    seen.set(key, id);
    citations.push({ id, key, text, ...splitClaudeCitation(text) });
  }

  return citations;
}

function buildClaudeCitationIndex(citations) {
  return new Map(citations.map((citation) => [citation.key, citation.id]));
}

function getClaudeDisplayDocumentTitle(title, documents = []) {
  const normalizedTitle = getClaudeCitationKey(title);
  const matchingDocuments = documents.filter((doc) => {
    const documentTitle = getClaudeCitationKey(doc?.title);

    return (
      documentTitle &&
      normalizedTitle &&
      (documentTitle === normalizedTitle ||
        documentTitle.startsWith(`${normalizedTitle} (`) ||
        normalizedTitle.startsWith(`${documentTitle} (`))
    );
  });

  if (matchingDocuments.length === 1) {
    const matchedTitle = matchingDocuments[0].title;
    const normalizedMatchedTitle = getClaudeCitationKey(matchedTitle);

    if (normalizedMatchedTitle.includes("guidelines for designated non-financial businesses and professions")) {
      return "UAE AML/CFT Guidelines for DNFBPs";
    }

    if (normalizedMatchedTitle.includes("guidelines for financial institutions")) {
      return "UAE AML/CFT Guidelines for FIs";
    }

    if (normalizedMatchedTitle.includes("counter terrorist financing and sanctions module")) {
      return "DFSA AML Module";
    }

    if (normalizedMatchedTitle.includes("anti money laundering and sanctions rulebook")) {
      return "ADGM AML Rulebook";
    }

    return matchedTitle;
  }

  return title;
}

function groupClaudeCitationsByDocument(citations, documents = []) {
  const groups = [];
  const groupIndex = new Map();

  citations.forEach((citation) => {
    const title = getClaudeDisplayDocumentTitle(citation.title || "Regulatory source", documents);
    const groupKey = getClaudeCitationKey(title);
    let group = groupIndex.get(groupKey);

    if (!group) {
      group = { key: groupKey, title, citations: [] };
      groupIndex.set(groupKey, group);
      groups.push(group);
    }

    group.citations.push(citation);
  });

  return groups;
}

function getClaudeCitationIdsForText(text, citationIndex) {
  const ids = [];
  let match;
  CLAUDE_SOURCE_PATTERN.lastIndex = 0;

  while ((match = CLAUDE_SOURCE_PATTERN.exec(String(text || "")))) {
    const id = citationIndex.get(getClaudeCitationKey(match[1]));
    if (id && !ids.includes(id)) {
      ids.push(id);
    }
  }

  return ids;
}

function getClaudeCitationTargetAttributes(citationIds) {
  if (!citationIds.length) {
    return {};
  }

  return {
    "data-claude-citation-targets": citationIds.join(" "),
  };
}

function renderClaudeInlineContent(
  text,
  keyPrefix,
  citationIndex,
  onCitationClick,
  activeCitationId,
  onCitationPreview,
  onCitationPreviewEnd
) {
  const parts = [];
  let cursor = 0;
  let match;
  let matchIndex = 0;
  CLAUDE_INLINE_PATTERN.lastIndex = 0;

  while ((match = CLAUDE_INLINE_PATTERN.exec(text))) {
    if (match.index > cursor) {
      parts.push(text.slice(cursor, match.index));
    }

    if (match[1] !== undefined) {
      parts.push(<strong key={`${keyPrefix}-b-${matchIndex}`}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      const citationText = match[2].trim();
      const citationId = citationIndex.get(getClaudeCitationKey(citationText));
      parts.push(
        <button
          type="button"
          key={`${keyPrefix}-c-${matchIndex}`}
          className={`regulatoryClaudeCitationMarker${
            activeCitationId === citationId ? " regulatoryClaudeCitationMarker--active" : ""
          }`}
          onClick={() => onCitationClick?.(citationText)}
          onMouseEnter={() => onCitationPreview?.(citationId)}
          onMouseLeave={() => onCitationPreviewEnd?.()}
          onFocus={() => onCitationPreview?.(citationId)}
          onBlur={() => onCitationPreviewEnd?.()}
          title="Open in law reader"
          aria-label={`Open source ${citationId || ""} in law reader`.trim()}
        >
          {citationId || "?"}
        </button>
      );
    }

    cursor = match.index + match[0].length;
    matchIndex += 1;
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts;
}

function parseClaudeAnswerBlocks(answerText) {
  const lines = String(answerText || "").split("\n");
  const blocks = [];
  let paragraphLines = [];
  let listItems = [];
  let orderedListItems = [];

  const flushParagraph = () => {
    if (paragraphLines.length) {
      blocks.push({ type: "p", text: paragraphLines.join(" ") });
      paragraphLines = [];
    }
  };
  const flushList = () => {
    if (listItems.length) {
      blocks.push({ type: "ul", items: listItems });
      listItems = [];
    }
  };
  const flushOrderedList = () => {
    if (orderedListItems.length) {
      blocks.push({ type: "ol", items: orderedListItems });
      orderedListItems = [];
    }
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      flushOrderedList();
      return;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      flushOrderedList();
      blocks.push({ type: "h", level: headingMatch[1].length, text: headingMatch[2] });
      return;
    }

    const listMatch = line.match(/^[-*]\s+(.*)$/);
    if (listMatch) {
      flushParagraph();
      flushOrderedList();
      listItems.push(listMatch[1]);
      return;
    }

    const orderedListMatch = line.match(/^\d+[.)]\s+(.*)$/);
    if (orderedListMatch) {
      flushParagraph();
      flushList();
      orderedListItems.push(orderedListMatch[1]);
      return;
    }

    flushList();
    flushOrderedList();
    paragraphLines.push(line);
  });

  flushParagraph();
  flushList();
  flushOrderedList();

  return blocks;
}

function renderClaudeAnswerBlocks(
  answerText,
  citationIndex,
  onCitationClick,
  activeCitationId,
  onCitationPreview,
  onCitationPreviewEnd
) {
  let hasLeadParagraph = false;

  return parseClaudeAnswerBlocks(answerText).map((block, index) => {
    const blockCitationIds =
      block.type === "ul" || block.type === "ol"
        ? []
        : getClaudeCitationIdsForText(block.text, citationIndex);
    const isBlockActive = blockCitationIds.includes(activeCitationId);

    if (block.type === "h") {
      const HeadingTag = block.level <= 2 ? "h4" : "h5";
      return (
        <HeadingTag
          className="regulatoryClaudeAnswerHeading"
          key={`claude-h-${index}`}
          {...getClaudeCitationTargetAttributes(blockCitationIds)}
        >
          <span className={isBlockActive ? "regulatoryClaudeTextHighlight" : undefined}>
            {renderClaudeInlineContent(
              block.text,
              `claude-h-${index}`,
              citationIndex,
              onCitationClick,
              activeCitationId,
              onCitationPreview,
              onCitationPreviewEnd
            )}
          </span>
        </HeadingTag>
      );
    }

    if (block.type === "ul" || block.type === "ol") {
      const ListTag = block.type;
      return (
        <ListTag className="regulatoryClaudeAnswerList" key={`claude-${block.type}-${index}`}>
          {block.items.map((item, itemIndex) => {
            const itemCitationIds = getClaudeCitationIdsForText(item, citationIndex);
            const isItemActive = itemCitationIds.includes(activeCitationId);

            return (
              <li
                key={`claude-li-${index}-${itemIndex}`}
                {...getClaudeCitationTargetAttributes(itemCitationIds)}
              >
                <span className={isItemActive ? "regulatoryClaudeTextHighlight" : undefined}>
                  {renderClaudeInlineContent(
                    item,
                    `claude-li-${index}-${itemIndex}`,
                    citationIndex,
                    onCitationClick,
                    activeCitationId,
                    onCitationPreview,
                    onCitationPreviewEnd
                  )}
                </span>
              </li>
            );
          })}
        </ListTag>
      );
    }

    const isLead = !hasLeadParagraph;
    hasLeadParagraph = true;

    return (
      <p
        className={isLead ? "regulatoryClaudeAnswerLead" : undefined}
        key={`claude-p-${index}`}
        {...getClaudeCitationTargetAttributes(blockCitationIds)}
      >
        <span className={isBlockActive ? "regulatoryClaudeTextHighlight" : undefined}>
          {renderClaudeInlineContent(
            block.text,
            `claude-p-${index}`,
            citationIndex,
            onCitationClick,
            activeCitationId,
            onCitationPreview,
            onCitationPreviewEnd
          )}
        </span>
      </p>
    );
  });
}

const REGULATION_TITLE_STOPWORDS = new Set([
  "the",
  "and",
  "for",
  "of",
  "no",
  "by",
  "on",
  "in",
  "to",
  "its",
  "that",
  "this",
  "or",
  "from",
  "under",
  "issued",
  "jointly",
]);

function extractRegulationTitleTokens(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[().,/-]/g, " ")
    .split(/\s+/)
    .filter((token) => token.length > 2 && !REGULATION_TITLE_STOPWORDS.has(token));
}

function extractRegulationNumber(text) {
  const match = String(text || "").match(/no\.?\s*\(?(\d+)\)?/i);
  return match ? match[1] : null;
}

// Claude's citation titles come from the backend's MyKYC-laws-json corpus, whose title strings
// use different punctuation/word order than this page's independently-authored `legalName`
// strings (e.g. "Cabinet Resolution No. (134) of 2025" vs. "Cabinet Resolution No. 134/2025
// Regarding..."). A prefix match never lines up, so this scores by token overlap plus a strong
// bonus/penalty on the regulation's identifying number, which is resilient to formatting drift.
function resolveClaudeCitationDocument(titlePart, documentMap) {
  if (!titlePart || !documentMap) {
    return null;
  }

  const queryTokens = extractRegulationTitleTokens(titlePart);
  const queryNumber = extractRegulationNumber(titlePart);

  let best = null;
  let bestScore = 0;

  documentMap.forEach((regulation) => {
    const legalName = String(regulation.legalName || regulation.title || "");
    if (!legalName) {
      return;
    }

    const candidateTokens = new Set(extractRegulationTitleTokens(legalName));
    let score = 0;
    queryTokens.forEach((token) => {
      if (candidateTokens.has(token)) {
        score += 1;
      }
    });

    if (queryNumber) {
      const candidateNumber = extractRegulationNumber(legalName);
      if (candidateNumber === queryNumber) {
        score += 10;
      } else if (candidateNumber) {
        score -= 10;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      best = regulation;
    }
  });

  return bestScore >= 3 ? best : null;
}

// findTopicSections synthesizes a pseudo-article for guidance sub-numbers (e.g. "6.3.2", with a
// fabricated label like "Section 6.3.2") for inline excerpt rendering - but the reader modal only
// ever renders a DOM node (and id) for the top-level container article ("Section 6"); subsection
// headings get their own id only via getGuidelineSubsections (the same helper the reader's own
// table-of-contents subsection links use), keyed by the container's real chapter/article label.
function resolveClaudeCitationContainerSection(regulation, sectionRef) {
  const [match] = findTopicSections(regulation.readerData, sectionRef);
  if (!match) {
    return null;
  }

  const matchedNumber = String(match.article.number || "");
  const topLevelNumber = matchedNumber.split(".")[0];
  const containerArticle =
    match.chapter.articles?.find((candidate) => String(candidate.number) === topLevelNumber) ||
    match.article;

  let subsectionId = null;
  if (matchedNumber.includes(".")) {
    const keyPrefix = `${match.chapter.chapter}-${containerArticle.label}`;
    const subsections = getGuidelineSubsections(containerArticle, keyPrefix);
    const targetSubsection = subsections.find((subsection) =>
      String(subsection.title || "").trim().startsWith(matchedNumber)
    );
    subsectionId = targetSubsection?.id || null;
  }

  return { chapter: match.chapter, article: containerArticle, subsectionId };
}

function resolveClaudeCitationSection(regulation, locatorPart) {
  if (!regulation?.readerData || !locatorPart) {
    return null;
  }

  if (locatorPart.startsWith("§")) {
    const sectionNumber = locatorPart.slice(1).trim();
    return resolveClaudeCitationContainerSection(regulation, { sectionNumber });
  }

  const numberMatch = locatorPart.match(/\(?(\d+[A-Za-z]?)\)?/);
  if (numberMatch) {
    const byNumber = resolveClaudeCitationContainerSection(regulation, {
      articleNumber: numberMatch[1],
    });
    if (byNumber) {
      return byNumber;
    }
  }

  return resolveClaudeCitationContainerSection(regulation, { articleLabel: locatorPart });
}

function resolveClaudeCitationArticleSource(citation, documentMap, documents = []) {
  if (!citation?.text || !documentMap) {
    return null;
  }

  const regulation = resolveClaudeCitationDocument(citation.title, documentMap);
  const resolvedSection = regulation
    ? resolveClaudeCitationSection(regulation, citation.locator)
    : null;

  if (!regulation || !resolvedSection?.article) {
    return {
      citation,
      regulation: null,
      resolvedSection: null,
      displayTitle: getClaudeDisplayDocumentTitle(citation.title || "Regulatory source", documents),
      displayArticle: null,
      articleBlocks: null,
    };
  }

  const displayArticle = getArticleDisplay(resolvedSection.article);

  return {
    citation,
    regulation,
    resolvedSection,
    displayTitle: getClaudeDisplayDocumentTitle(
      citation.title || regulation.legalName || regulation.title || "Regulatory source",
      documents
    ),
    displayArticle,
    articleBlocks: getArticleBlocks(resolvedSection.article),
  };
}

function resolveRegulatorySearchTopic(searchTerm, regulations) {
  const normalizedSearchTerm = String(searchTerm || "").trim();

  if (!normalizedSearchTerm) {
    return null;
  }

  const documents = (regulations || [])
    .map((regulation) => {
      const readerData = regulation.readerData;

      if (!readerData?.chapters?.length) {
        return null;
      }

      const isGuidelineSearchReader =
        readerData.readerStyle === "guideline" || readerData.id?.includes("guidelines");
      const sections = readerData.chapters
        .flatMap((chapter) =>
          (chapter.articles || [])
            .map((article) => {
              const displayArticle = getArticleDisplay(article);
              const articleBlocks = getArticleBlocks(article);
              const filteredBlocks = isGuidelineSearchReader
                ? filterGuidelineBlocksForSearch(articleBlocks, normalizedSearchTerm)
                : filterRegulationBlocksForSearch(articleBlocks, normalizedSearchTerm);
              const titleMatches = includesSearchTerm(
                `${chapter.chapter} ${chapter.title} ${article.label} ${displayArticle.title} ${article.divisionHeading || ""}`,
                normalizedSearchTerm
              );

              if (!titleMatches && filteredBlocks.length === 0) {
                return null;
              }

              return {
                chapter,
                fullArticle: article,
                article: {
                  ...article,
                  body: displayArticle.body,
                  title: displayArticle.title,
                  blocks: filteredBlocks.length > 0 ? filteredBlocks : articleBlocks,
                },
              };
            })
            .filter(Boolean)
        );

      if (sections.length === 0) {
        return null;
      }

      return {
        documentId: readerData.id,
        regulation,
        sections,
      };
    })
    .filter(Boolean);

  return {
    id: `search-${slugifyRegulatoryId(normalizedSearchTerm)}`,
    label: normalizedSearchTerm,
    keywords: [normalizedSearchTerm],
    isSearchTopic: true,
    documents,
  };
}

function getTopicDocumentSubtitle(regulation) {
  if (!regulation) {
    return "";
  }

  const subtitleById = {
    "dnfbp-aml-cft-guidelines-2021":
      "Guidelines for Designated Non-Financial Businesses and Professions",
    "fi-aml-cft-guidelines-2021": "Guidelines for Financial Institutions",
  };

  return subtitleById[regulation.readerData?.id] || regulation.legalName || regulation.title;
}

function getTopicDocumentMenuTitle(regulation) {
  const titleById = {
    "dnfbp-aml-cft-guidelines-2021": "DNFBP AML Guidelines",
    "fi-aml-cft-guidelines-2021": "FI AML Guidelines",
  };

  return titleById[regulation?.readerData?.id] || regulation?.title || "";
}

function getTopicSectionElementId(topicId, documentId, topicSection) {
  return `regulatory-topic-${slugifyRegulatoryId(
    `${topicId}-${documentId}-${topicSection.chapter.chapter}-${topicSection.article.label}`
  )}`;
}

function getTopicDocumentElementId(topicId, documentId) {
  return `regulatory-topic-law-${slugifyRegulatoryId(`${topicId}-${documentId}`)}`;
}

function getTopicMenuLabel(label) {
  return String(label || "").replace(/^Article\s+\(([^)]+)\)$/i, "Article $1");
}

function getTopicChapterMenuLabel(label) {
  const text = String(label || "");
  const chapterMatch = text.match(/^Chapter\s+(.+)$/i);
  if (!chapterMatch) return text;

  const chapterToken = chapterMatch[1].trim();
  const chapterNumber =
    readerNumberWords[chapterToken.toLowerCase()] || chapterToken.match(/^\d+$/)?.[0];

  return chapterNumber ? `Chapter ${chapterNumber}` : text;
}

function getTopicSectionMenuItems(topicId, documentId, topicSection) {
  const displayArticle = getArticleDisplay(topicSection.article);
  const articleElementId = getTopicSectionElementId(topicId, documentId, topicSection);
  const articleKeyPrefix = `${topicId}-${documentId}-${topicSection.article.label}`;
  const menuLabel = getTopicMenuLabel(topicSection.article.label);
  const menuTitle = displayArticle.title === topicSection.article.label ? menuLabel : displayArticle.title;
  const items = [
    {
      id: articleElementId,
      label: menuLabel,
      title: menuTitle,
      depth: 0,
    },
  ];
  const articleBlocks = getArticleBlocks(topicSection.article);
  const blockSubheadings = articleBlocks.filter((block) => block.type === "subheading");

  if (blockSubheadings.length > 0) {
    blockSubheadings.forEach((block, index) => {
      const headingMatch = String(block.text || "").match(/^(\d+(?:\.\d+)+)\s+(.+)$/);

      if (!headingMatch) {
        return;
      }

      items.push({
        id: getReaderSubsectionId(articleKeyPrefix, index),
        label: headingMatch[1],
        title: headingMatch[2].trim(),
        depth: 1,
      });
    });

    return items;
  }

  const headingPattern = /^(\d+(?:\.\d+)+)\s+(.+)$/gm;
  let match;
  let subheadingIndex = 0;

  while ((match = headingPattern.exec(displayArticle.body || "")) !== null) {
    items.push({
      id: getReaderSubsectionId(articleKeyPrefix, subheadingIndex),
      label: match[1],
      title: match[2].trim(),
      depth: 1,
    });
    subheadingIndex += 1;
  }

  return items;
}

function topicSectionHasSearchMatch(topicSection, searchTerm) {
  if (!searchTerm) {
    return false;
  }

  const displayArticle = getArticleDisplay(topicSection.article);
  const articleBlocks = getArticleBlocks(topicSection.article);

  return (
    includesSearchTerm(
      `${topicSection.chapter.chapter} ${topicSection.chapter.title} ${topicSection.article.label} ${displayArticle.title} ${topicSection.article.divisionHeading || ""}`,
      searchTerm
    ) || articleBlocks.some((block) => blockHasSearchTerm(block, searchTerm))
  );
}

const guidelineTopicReaderIds = new Set([
  "fi-aml-cft-guidelines-2021",
  "dnfbp-aml-cft-guidelines-2021",
]);

const structuralSearchReaderIds = new Set([
  "swiss-amla",
  "swiss-amlo",
  "swiss-amlo-finma",
  "swiss-cdb-2020",
  "swiss-cdb-2020-commentary",
  "federal-decree-law-10-2025",
  "cabinet-resolution-134-2025",
  "cabinet-resolution-109-2023",
  "cabinet-resolution-111-2022",
  "federal-law-7-2014",
  "cabinet-decision-74-2020",
]);

const rulebookStructuralSearchReaderIds = new Set([
  "dfsa-aml-module",
  "fsra-aml-rulebook",
]);

const readerNumberWords = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
};

function getReaderNumberWord(number) {
  const normalizedNumber = Number(number);
  const entry = Object.entries(readerNumberWords).find(([, value]) => value === normalizedNumber);
  return entry?.[0] || "";
}

function parseReaderStructuralSearch(searchTerm) {
  const normalized = String(searchTerm || "").trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  const ruleMatch = normalized.match(/^rule\s+([0-9]+(?:\.[0-9a-z]+)+)$/i);
  if (ruleMatch) {
    return { type: "rule", number: ruleMatch[1].toUpperCase() };
  }

  const sectionMatch = normalized.match(/^section\s+([0-9]+(?:\.[0-9a-z]+)+)$/i);
  if (sectionMatch) {
    return { type: "section", number: sectionMatch[1].toUpperCase() };
  }

  const articleMatch = normalized.match(/^(?:article|art)\.?\s*\(?(\d+)\)?$/);
  if (articleMatch) {
    return { type: "article", number: articleMatch[1] };
  }

  const chapterMatch = normalized.match(/^(?:chapter|chap)\.?\s*(\d+)$/);
  if (chapterMatch) {
    return { type: "chapter", number: chapterMatch[1] };
  }

  const bareNumberMatch = normalized.match(/^(\d+)$/);
  if (bareNumberMatch) {
    return { type: "number", number: bareNumberMatch[1] };
  }

  const dottedNumberMatch = normalized.match(/^([0-9]+(?:\.[0-9a-z]+)+)$/i);
  if (dottedNumberMatch) {
    return { type: "ruleOrSection", number: dottedNumberMatch[1].toUpperCase() };
  }

  return null;
}

function getStructuralReaderLabelHighlightTerm(text, structuralSearch, labelType) {
  if (
    !structuralSearch ||
    (structuralSearch.type !== "number" && structuralSearch.type !== labelType)
  ) {
    return "";
  }

  const source = String(text || "");
  const targetNumber = String(structuralSearch.number);
  const targetWord = getReaderNumberWord(targetNumber);

  if (labelType === "article") {
    const articlePhraseMatch = source.match(
      new RegExp(`\\bArticle\\s*\\(?0*${escapeRegExp(targetNumber)}\\)?(?=$|[^A-Za-z0-9])`, "i")
    );

    if (articlePhraseMatch) {
      return articlePhraseMatch[0];
    }
  }

  if (labelType === "chapter") {
    const chapterNumberPhraseMatch = source.match(
      new RegExp(`\\bChapter\\s+0*${escapeRegExp(targetNumber)}\\b`, "i")
    );

    if (chapterNumberPhraseMatch) {
      return chapterNumberPhraseMatch[0];
    }

    if (targetWord) {
      const chapterWordPhraseMatch = source.match(
        new RegExp(`\\bChapter\\s+${escapeRegExp(targetWord)}\\b`, "i")
      );

      if (chapterWordPhraseMatch) {
        return chapterWordPhraseMatch[0];
      }
    }
  }

  const digitMatch = source.match(
    new RegExp(`(^|[^A-Za-z0-9])(0*${escapeRegExp(targetNumber)})(?=$|[^A-Za-z0-9])`, "i")
  );

  if (digitMatch) {
    return digitMatch[2];
  }

  if (targetWord && new RegExp(`\\b${escapeRegExp(targetWord)}\\b`, "i").test(source)) {
    return targetWord;
  }

  return "";
}

function getReaderChapterNumber(chapter) {
  const label = String(chapter?.chapter || "").trim();
  const digitMatch = label.match(/\b(\d+)\b/);
  if (digitMatch) {
    return digitMatch[1];
  }

  const wordMatch = label.toLowerCase().match(/\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty)\b/);
  return wordMatch ? String(readerNumberWords[wordMatch[1]]) : "";
}

function isStructuralSearchReader(readerData) {
  return structuralSearchReaderIds.has(readerData?.id);
}

function isRulebookStructuralSearchReader(readerData) {
  return rulebookStructuralSearchReaderIds.has(readerData?.id);
}

function isRulebookStructuralSearch(search) {
  return ["chapter", "number", "section", "rule", "ruleOrSection"].includes(search?.type);
}

function getReaderRuleBoxId(keyPrefix, ruleLabel) {
  return `regulatory-reader-rule-${slugifyRegulatoryId(`${keyPrefix}-${ruleLabel}`)}`;
}

function normalizeReaderRuleNumber(value) {
  return String(value || "")
    .replace(/^Rule\s+/i, "")
    .replace(/\.$/, "")
    .toUpperCase();
}

function filterRulebookBlocksForRule(blocks, ruleNumber) {
  const targetRule = normalizeReaderRuleNumber(ruleNumber);
  const result = [];
  let currentRuleBlocks = null;

  const flushRule = () => {
    if (!currentRuleBlocks) {
      return;
    }

    if (normalizeReaderRuleNumber(currentRuleBlocks[0]?.text) === targetRule) {
      result.push(...currentRuleBlocks);
    }

    currentRuleBlocks = null;
  };

  blocks.forEach((block) => {
    if (block.type === "ruleLabel") {
      flushRule();
      currentRuleBlocks = [block];
      return;
    }

    if (currentRuleBlocks) {
      currentRuleBlocks.push(block);
    }
  });

  flushRule();
  return result;
}

function filterRulebookBlocksForSection(blocks, sectionNumber) {
  const targetSection = normalizeReaderRuleNumber(sectionNumber);
  const result = [];
  let currentSectionBlocks = null;

  const flushSection = () => {
    if (!currentSectionBlocks) {
      return;
    }

    if (
      currentSectionBlocks[0]?.type === "subheading" &&
      normalizeReaderRuleNumber(currentSectionBlocks[0].text.split(/\s+/)[0]) === targetSection
    ) {
      result.push(...currentSectionBlocks);
    }

    currentSectionBlocks = null;
  };

  blocks.forEach((block) => {
    if (block.type === "subheading") {
      flushSection();
      currentSectionBlocks = [block];
      return;
    }

    if (currentSectionBlocks) {
      currentSectionBlocks.push(block);
      return;
    }
  });

  flushSection();
  return result;
}

function filterReaderDataForRulebookStructuralSearch(readerData, structuralSearch) {
  const targetNumber = String(structuralSearch.number);

  const chapters = readerData.chapters
    .map((chapter, index) => {
      const chapterMatches =
        (structuralSearch.type === "chapter" || structuralSearch.type === "number") &&
        getReaderChapterNumber(chapter) === targetNumber;

      const articles = chapter.articles
        .map((article) => {
          const displayArticle = getArticleDisplay(article);
          const articleBlocks = getArticleBlocks(article);

          if (chapterMatches) {
            return {
              ...article,
              filteredBlocks: articleBlocks,
            };
          }

          if (
            (structuralSearch.type === "section" || structuralSearch.type === "ruleOrSection") &&
            normalizeReaderRuleNumber(article.number) === normalizeReaderRuleNumber(targetNumber)
          ) {
            return {
              ...article,
              filteredBlocks: articleBlocks,
            };
          }

          let filteredBlocks = [];

          if (structuralSearch.type === "rule") {
            filteredBlocks = filterRulebookBlocksForRule(articleBlocks, targetNumber);
          } else if (structuralSearch.type === "section") {
            filteredBlocks = filterRulebookBlocksForSection(articleBlocks, targetNumber);
          } else if (structuralSearch.type === "ruleOrSection") {
            filteredBlocks = [
              ...filterRulebookBlocksForSection(articleBlocks, targetNumber),
              ...filterRulebookBlocksForRule(articleBlocks, targetNumber),
            ];
          }

          if (filteredBlocks.length === 0) {
            return null;
          }

          return {
            ...article,
            filteredBlocks,
          };
        })
        .filter(Boolean);

      if (articles.length === 0) {
        return null;
      }

      return {
        ...chapter,
        originalIndex: index,
        articles,
      };
    })
    .filter(Boolean);

  return {
    intro: "",
    introMatches: false,
    chapters,
  };
}

function getRulebookStructuralReaderTargets(filteredReaderData, structuralSearch) {
  const targetNumber = normalizeReaderRuleNumber(structuralSearch.number);
  const targets = [];

  if (structuralSearch.type === "chapter" || structuralSearch.type === "number") {
    filteredReaderData.chapters.forEach((chapter) => {
      if (getReaderChapterNumber(chapter) === targetNumber) {
        targets.push(`regulatory-reader-chapter-${chapter.originalIndex}`);
      }
    });

    return targets;
  }

  filteredReaderData.chapters.forEach((chapter) => {
    chapter.articles.forEach((article) => {
      const articleKeyPrefix = `${chapter.chapter}-${article.label}`;

      if (structuralSearch.type === "section" || structuralSearch.type === "ruleOrSection") {
        const displayArticle = getArticleDisplay(article);

        if (normalizeReaderRuleNumber(article.number) === targetNumber) {
          targets.push(`regulatory-reader-article-${slugifyRegulatoryId(articleKeyPrefix)}`);
        }

        getGuidelineSubsections(article, articleKeyPrefix).forEach((subsection) => {
          if (normalizeReaderRuleNumber(subsection.title.split(/\s+/)[0]) === targetNumber) {
            targets.push(subsection.id);
          }
        });
      }

      if (structuralSearch.type === "rule" || structuralSearch.type === "ruleOrSection") {
        (article.filteredBlocks || [])
          .filter((block) => block.type === "ruleLabel")
          .forEach((block) => {
            if (normalizeReaderRuleNumber(block.text) === targetNumber) {
              targets.push(getReaderRuleBoxId(articleKeyPrefix, block.text));
            }
          });
      }
    });
  });

  return [...new Set(targets)];
}

function filterReaderDataForStructuralSearch(readerData, structuralSearch) {
  const targetNumber = String(structuralSearch.number);

  const chapters = readerData.chapters
    .map((chapter, index) => {
      const chapterMatches =
        (structuralSearch.type === "chapter" || structuralSearch.type === "number") &&
        getReaderChapterNumber(chapter) === targetNumber;

      const articles = chapter.articles
        .filter((article) => {
          if (chapterMatches) {
            return true;
          }

          return (
            (structuralSearch.type === "article" || structuralSearch.type === "number") &&
            String(article.number || "").trim() === targetNumber
          );
        })
        .map((article) => ({
          ...article,
          filteredBlocks: getArticleBlocks(article),
        }));

      if (articles.length === 0) {
        return null;
      }

      return {
        ...chapter,
        originalIndex: index,
        articles,
      };
    })
    .filter(Boolean);

  return {
    intro: "",
    introMatches: false,
    chapters,
  };
}

function getGuidelineSubsections(article, keyPrefix, searchTerm = "") {
  const subsections = [];

  const flushSubsection = (subsection) => {
    if (!subsection) {
      return;
    }

    subsections.push({
      id: subsection.id,
      title: subsection.title,
      hasSearchMatch: Boolean(searchTerm) && includesSearchTerm(subsection.searchText, searchTerm),
    });
  };

  let currentSubsection = null;

  if (article.blocks) {
    article.blocks.forEach((block) => {
      if (block.type === "subheading") {
        flushSubsection(currentSubsection);
        currentSubsection = {
          id: getReaderSubsectionId(keyPrefix, subsections.length),
          title: block.text,
          searchText: block.text,
        };
        return;
      }

      if (currentSubsection) {
        currentSubsection.searchText = `${currentSubsection.searchText} ${block.text || ""}`;
      }
    });

    flushSubsection(currentSubsection);
    return subsections;
  }

  String(article.body || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const subsectionMatch = line.match(/^(\d+(?:\.\d+)+\.?)\s+(.+)$/);

      if (subsectionMatch) {
        flushSubsection(currentSubsection);
        currentSubsection = {
          id: getReaderSubsectionId(keyPrefix, subsections.length),
          title: `${subsectionMatch[1]} ${subsectionMatch[2]}`.replace(/\s+/g, " ").trim(),
          searchText: line,
        };
        return;
      }

      if (currentSubsection) {
        currentSubsection.searchText = `${currentSubsection.searchText} ${line}`;
      }
    });

  flushSubsection(currentSubsection);
  return subsections;
}

function HighlightedText({ text, searchTerm, activeMatchIndex, matchIndexRef, forceActive = false }) {
  if (!searchTerm) {
    return text;
  }

  const sourceText = String(text || "");
  const matches = getSearchTermMatches(sourceText, searchTerm);

  if (matches.length === 0) {
    return sourceText;
  }

  const parts = [];
  let cursor = 0;

  matches.forEach((match, index) => {
    if (match.start > cursor) {
      parts.push(sourceText.slice(cursor, match.start));
    }

    const part = sourceText.slice(match.start, match.end);
    const matchIndex = matchIndexRef ? matchIndexRef.current : null;

    if (matchIndexRef) {
      matchIndexRef.current += 1;
    }

    parts.push(
      <mark
        className={`regulatoryReaderMark${forceActive || matchIndex === activeMatchIndex ? " regulatoryReaderMark--active" : ""}`}
        data-regulatory-search-match={matchIndex ?? undefined}
        key={`${part}-${index}`}
      >
        {part}
      </mark>
    );

    cursor = match.end;
  });

  if (cursor < sourceText.length) {
    parts.push(sourceText.slice(cursor));
  }

  return parts;
}

function filterClauseForSearch(clause, searchTerm) {
  if (!searchTerm) {
    return clause;
  }

  const selfMatches = includesSearchTerm(clause.text, searchTerm);

  if (selfMatches) {
    return clause;
  }

  const children = (clause.children || [])
    .map((child) => filterClauseForSearch(child, searchTerm))
    .filter(Boolean);

  if (children.length === 0) {
    return null;
  }

  return {
    ...clause,
    children,
  };
}

function filterDefinitionForSearch(definition, searchTerm) {
  if (!searchTerm) {
    return definition;
  }

  const selfMatches =
    includesSearchTerm(definition.term, searchTerm) ||
    includesSearchTerm(definition.text, searchTerm);
  const items = definition.items
    .map((item) => filterClauseForSearch(item, searchTerm))
    .filter(Boolean);

  if (!selfMatches && items.length === 0) {
    return null;
  }

  return {
    ...definition,
    items,
  };
}

function filterGlossaryTableForSearch(table, searchTerm) {
  if (!searchTerm) {
    return table;
  }

  const rows = (table.rows || []).filter(
    (row) => includesSearchTerm(row.term, searchTerm) || includesSearchTerm(row.definition, searchTerm)
  );

  if (rows.length === 0) {
    return null;
  }

  return {
    ...table,
    text: rows.map((row) => `${row.term} ${row.definition}`).join(" "),
    rows,
  };
}

function filterApplicationTableForSearch(table, searchTerm) {
  if (!searchTerm) {
    return table;
  }

  const rows = (table.rows || []).filter(
    (row) => includesSearchTerm(row.person, searchTerm) || includesSearchTerm(row.chapters, searchTerm)
  );
  const footnoteMatches = includesSearchTerm(table.footnote, searchTerm);

  if (rows.length === 0 && !footnoteMatches) {
    return null;
  }

  return {
    ...table,
    text: [
      "Relevant Person Applicable Chapters",
      ...rows.map((row) => `${row.person} ${row.chapters}`),
      footnoteMatches ? table.footnote : "",
    ]
      .filter(Boolean)
      .join(" "),
    rows,
    footnote: table.footnote,
  };
}

function blockHasSearchTerm(block, searchTerm) {
  if (!searchTerm) {
    return true;
  }

  if (block.type === "clause") {
    return (
      includesSearchTerm(block.text, searchTerm) ||
      (block.children || []).some((child) => blockHasSearchTerm(child, searchTerm))
    );
  }

  if (block.type === "definition") {
    return (
      includesSearchTerm(block.term, searchTerm) ||
      includesSearchTerm(block.text, searchTerm) ||
      (block.items || []).some((item) => blockHasSearchTerm(item, searchTerm))
    );
  }

  if (block.type === "glossaryTable") {
    return (block.rows || []).some(
      (row) => includesSearchTerm(row.term, searchTerm) || includesSearchTerm(row.definition, searchTerm)
    );
  }

  if (block.type === "applicationTable") {
    return (
      (block.rows || []).some(
        (row) => includesSearchTerm(row.person, searchTerm) || includesSearchTerm(row.chapters, searchTerm)
      ) || includesSearchTerm(block.footnote, searchTerm)
    );
  }

  return includesSearchTerm(block.text, searchTerm);
}

function filterRegulationBlocksForSearch(blocks, searchTerm) {
  if (!searchTerm) {
    return blocks;
  }

  const filteredBlocks = blocks
    .map((block) => {
      if (block.type === "clause") {
        return filterClauseForSearch(block, searchTerm);
      }

      if (block.type === "definition") {
        return filterDefinitionForSearch(block, searchTerm);
      }

      if (block.type === "glossaryTable") {
        return filterGlossaryTableForSearch(block, searchTerm);
      }

      if (block.type === "applicationTable") {
        return filterApplicationTableForSearch(block, searchTerm);
      }

      return includesSearchTerm(block.text, searchTerm) ? block : null;
    })
    .filter(Boolean);

  return preserveListContext(blocks, filteredBlocks);
}

function filterGuidelineBlocksForSearch(blocks, searchTerm) {
  if (!searchTerm) {
    return blocks;
  }

  const result = [];
  let prefaceBlocks = [];
  let subsectionBlocks = [];
  let hasSeenSubsection = false;

  const flushSubsection = () => {
    if (subsectionBlocks.length === 0) {
      return;
    }

    const headingMatches = blockHasSearchTerm(subsectionBlocks[0], searchTerm);
    const subsectionMatches = subsectionBlocks.some((block) => blockHasSearchTerm(block, searchTerm));

    if (headingMatches) {
      result.push(...subsectionBlocks);
    } else if (subsectionMatches) {
      result.push(...filterRegulationBlocksForSearch(subsectionBlocks, searchTerm));
    }

    subsectionBlocks = [];
  };

  blocks.forEach((block) => {
    if (block.type === "subheading") {
      flushSubsection();
      hasSeenSubsection = true;
      subsectionBlocks = [block];
      return;
    }

    if (!hasSeenSubsection) {
      prefaceBlocks.push(block);
      return;
    }

    subsectionBlocks.push(block);
  });

  flushSubsection();

  prefaceBlocks = filterRegulationBlocksForSearch(prefaceBlocks, searchTerm);
  return [...prefaceBlocks, ...result];
}

function groupGuidanceBlocks(blocks, keepAllGuidanceBlocksTogether = false) {
  const groupedBlocks = [];
  let currentGuidance = null;

  const flushGuidance = () => {
    if (!currentGuidance) {
      return;
    }

    groupedBlocks.push({
      ...currentGuidance,
      text: currentGuidance.blocks.map((block) => block.text || "").join(" "),
    });
    currentGuidance = null;
  };

  blocks.forEach((block) => {
    if (block.type === "guidanceStart") {
      flushGuidance();
      currentGuidance = {
        id: `guidance-${block.id}`,
        type: "guidanceBox",
        title: block.text,
        blocks: [],
      };
      return;
    }

    if (
      currentGuidance &&
      !keepAllGuidanceBlocksTogether &&
      (block.type === "ruleLabel" ||
        block.type === "subheading" ||
        (block.type === "contentHeading" &&
          currentGuidance.title === "Guidance" &&
          isStandaloneRegulatoryContentHeading(block.text)))
    ) {
      flushGuidance();
      groupedBlocks.push(block);
      return;
    }

    if (currentGuidance) {
      currentGuidance.blocks.push(block);
      return;
    }

    groupedBlocks.push(block);
  });

  flushGuidance();
  return groupedBlocks;
}

function groupGuidanceOnlyBlocks(blocks) {
  const guidanceStartIndex = blocks.findIndex((block) => block.type === "guidanceStart");

  if (guidanceStartIndex === -1) {
    return groupGuidanceBlocks(blocks, true);
  }

  const precedingBlocks = blocks.slice(0, guidanceStartIndex);
  const guidanceStart = blocks[guidanceStartIndex];
  const guidanceBlocks = blocks.slice(guidanceStartIndex + 1);

  return [
    ...precedingBlocks,
    {
      id: `guidance-${guidanceStart.id}`,
      type: "guidanceBox",
      title: guidanceStart.text,
      text: guidanceBlocks.map((block) => block.text || "").join(" "),
      blocks: guidanceBlocks,
    },
  ];
}

function groupRuleBlocks(blocks) {
  const groupedBlocks = [];
  let currentRule = null;

  const flushRule = () => {
    if (!currentRule) {
      return;
    }

    groupedBlocks.push({
      ...currentRule,
      text: [
        currentRule.label,
        ...currentRule.blocks.map((block) => block.text || ""),
      ].join(" "),
    });
    currentRule = null;
  };

  blocks.forEach((block) => {
    if (block.type === "guidanceBox") {
      flushRule();
      groupedBlocks.push({
        ...block,
        blocks: groupRuleBlocks(block.blocks || []),
      });
      return;
    }

    if (block.type === "ruleLabel") {
      flushRule();
      currentRule = {
        id: `rule-box-${block.id}`,
        type: "ruleBox",
        label: block.text,
        labelId: block.id,
        blocks: [],
      };
      return;
    }

    if (
      currentRule &&
      block.type === "contentHeading" &&
      isStandaloneRegulatoryContentHeading(block.text)
    ) {
      flushRule();
      groupedBlocks.push(block);
      return;
    }

    if (
      currentRule &&
      block.type === "contentHeading" &&
      (/^Application$/i.test(block.text) ||
        /^Definitions$/i.test(block.text) ||
        /^Requirements for unhosted wallets$/i.test(block.text))
    ) {
      currentRule.blocks.push(block);
      return;
    }

    if (
      currentRule &&
      block.type !== "paragraph" &&
      block.type !== "clause" &&
      block.type !== "definition" &&
      block.type !== "glossaryTable" &&
      block.type !== "applicationTable"
    ) {
      flushRule();
      groupedBlocks.push(block);
      return;
    }

    if (currentRule) {
      currentRule.blocks.push(block);
      return;
    }

    groupedBlocks.push(block);
  });

  flushRule();
  return groupedBlocks;
}

function collectRegulationBlockIds(blocks) {
  const ids = new Set();

  const collectClause = (clause) => {
    if (clause.id) {
      ids.add(clause.id);
    }

    (clause.children || []).forEach(collectClause);
  };

  blocks.forEach((block) => {
    if (block.id) {
      ids.add(block.id);
    }

    if (block.type === "clause") {
      collectClause(block);
    }

    if (block.type === "definition") {
      (block.items || []).forEach(collectClause);
    }
  });

  return ids;
}

function getArticleDisplay(article) {
  if (
    article.title ===
      "Designated Non-Financial Businesses and Professions (DNFBPs) shall include any person" &&
    article.body?.startsWith("who carries out one or more")
  ) {
    return {
      title: article.label,
      body: `${article.title}\n${article.body}`,
    };
  }

  return {
    title: article.title,
    body: article.body,
  };
}

function getArticleBlocks(article) {
  const displayArticle = getArticleDisplay(article);
  return article.blocks || formatRegulationText(displayArticle.body);
}

function shouldHideArticleHeader(readerData, chapter, article) {
  return Boolean(article?.hideHeader);
}

function shouldHideArticleLabel(article) {
  return Boolean(article?.hideLabel);
}

function formatOpeningText(text) {
  let lines = String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => {
      if (line === "Federal Decree by Law No. (10) of 2025") return false;
      if (line === "Regarding Anti-Money Laundering, and Combating the Financing of") return false;
      if (line === "Terrorism and Proliferation Financing") return false;
      if (line === "Cabinet Resolution No. (134) of 2025") return false;
      if (line === "Regarding the Executive Regulations of Federal Decree by Law No. (10) of") return false;
      if (line === "2025 Regarding Anti-Money Laundering, and Combating the Financing of") return false;
      return true;
    });
  const mergedLines = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (line === "CABINET DECISION NO 74") {
      const decisionParts = [line];
      let nextIndex = index + 1;

      while (nextIndex < lines.length && lines[nextIndex] !== "Abrogating:") {
        decisionParts.push(lines[nextIndex]);
        nextIndex += 1;
      }

      mergedLines.push(
        decisionParts
          .join(" ")
          .replace("CABINET DECISION NO 74 Issued on 27/10/2020. Corresponding to10 Rabi' Al Awwal 1442 AH Regarding", "Cabinet Decision No 74, issued on 27/10/2020, corresponding to10 Rabi' Al Awwal 1442 AH, regarding")
      );
      index = nextIndex - 1;
      continue;
    }

    if (line === "Abrogating:" && lines[index + 1]?.startsWith("Cabinet Decision No. 20")) {
      mergedLines.push(`${line} ${lines[index + 1]}`);
      index += 1;
      continue;
    }

    if (line.startsWith("Regarding Terrorism Lists Regulation")) {
      const headingParts = [line];
      let nextIndex = index + 1;

      while (nextIndex < lines.length && lines[nextIndex] !== "Abrogating:") {
        headingParts.push(lines[nextIndex]);
        nextIndex += 1;
      }

      mergedLines.push(headingParts.join(" "));
      index = nextIndex - 1;
    } else {
      mergedLines.push(line);
    }
  }

  lines = mergedLines;
  const blocks = [];
  let currentBullet = null;

  lines.forEach((line) => {
    if (line === "-") {
      if (currentBullet) {
        blocks.push({ type: "bullet", text: currentBullet });
      }
      currentBullet = "";
      return;
    }

    if (line.startsWith("- ")) {
      if (currentBullet) {
        blocks.push({ type: "bullet", text: currentBullet });
      }
      currentBullet = line.slice(2);
      return;
    }

    if (line.startsWith("Hereby ") || line === "Resolved as follows:") {
      if (currentBullet) {
        blocks.push({ type: "bullet", text: currentBullet });
        currentBullet = null;
      }
      blocks.push({ type: "strong", text: line });
      return;
    }

    if (currentBullet !== null) {
      currentBullet = currentBullet ? `${currentBullet} ${line}` : line;
      return;
    }

    blocks.push({
      type:
        line.startsWith("We, ") ||
        line === "The Cabinet:" ||
        line.startsWith("Hereby ") ||
        line === "Resolved as follows:"
        ? "strong"
        : "paragraph",
      text: line,
    });
  });

  if (currentBullet) {
    blocks.push({ type: "bullet", text: currentBullet });
  }

  return blocks.map((block) => ({
    ...block,
    text: block.text.replace(/\s+/g, " ").trim(),
  }));
}

function renderOpeningText(text, searchTerm, activeMatchIndex, matchIndexRef, forceActive = false) {
  return (
    <div className="regulatoryReaderOpeningText">
      {formatOpeningText(text).map((block, index) => {
        if (block.type === "bullet") {
          return (
            <div className="regulatoryReaderOpeningBullet" key={`opening-bullet-${index}`}>
              <span>-</span>
              <p>
                <HighlightedText
                  text={block.text}
                  searchTerm={searchTerm}
                  activeMatchIndex={activeMatchIndex}
                  matchIndexRef={matchIndexRef}
                  forceActive={forceActive}
                />
              </p>
            </div>
          );
        }

        if (block.type === "strong") {
          return (
            <p className="regulatoryReaderOpeningStrong" key={`opening-strong-${index}`}>
              <HighlightedText
                text={block.text}
                searchTerm={searchTerm}
                activeMatchIndex={activeMatchIndex}
                matchIndexRef={matchIndexRef}
                forceActive={forceActive}
              />
            </p>
          );
        }

        return (
          <p key={`opening-paragraph-${index}`}>
            <HighlightedText
              text={block.text}
              searchTerm={searchTerm}
              activeMatchIndex={activeMatchIndex}
              matchIndexRef={matchIndexRef}
              forceActive={forceActive}
            />
          </p>
        );
      })}
    </div>
  );
}

const regulatoryLayoutTransition = {
  layout: { duration: 0.24, ease: [0.22, 1, 0.36, 1] },
};

function getRegulatoryBlockMotion(shouldDelayReveal) {
  if (!shouldDelayReveal) {
    return {
      layout: "position",
      transition: regulatoryLayoutTransition,
    };
  }

  return {
    layout: "position",
    transition: regulatoryLayoutTransition,
  };
}

function ClauseBlock({
  clause,
  className = "",
  searchTerm,
  activeMatchIndex,
  matchIndexRef,
  clauseKey,
  delayedRevealIds = null,
  collapsingHideIds = null,
  forceActive = false,
  depth = 0,
}) {
  const hasChildren = clause.children?.length > 0;
  const shouldDelayReveal = delayedRevealIds?.has(clause.id);
  const shouldCollapseHide = collapsingHideIds?.has(clause.id);
  const classNames = `regulatoryReaderClause regulatoryReaderClause--${clause.level}${clause.marker === "\u2022" ? " regulatoryReaderClause--bullet" : ""}${shouldDelayReveal ? " regulatoryReaderBlock--delayedReveal" : ""}${shouldCollapseHide ? " regulatoryReaderBlock--collapsingHide" : ""}${className ? ` ${className}` : ""}`;
  const shouldKeepContainerStable = hasChildren && depth === 0;
  const Wrapper = shouldKeepContainerStable ? "div" : motion.div;
  const wrapperMotionProps = shouldKeepContainerStable
    ? {}
    : getRegulatoryBlockMotion(shouldDelayReveal);
  const ClauseLine = shouldKeepContainerStable ? motion.div : "div";
  const clauseLineMotionProps = shouldKeepContainerStable
    ? getRegulatoryBlockMotion(shouldDelayReveal)
    : {};

  return (
    <Wrapper className={classNames} key={clauseKey} {...wrapperMotionProps}>
      <ClauseLine className="regulatoryReaderClauseLine" {...clauseLineMotionProps}>
        <span>{clause.marker}</span>
        <p>
          <HighlightedText
            text={clause.text}
            searchTerm={searchTerm}
            activeMatchIndex={activeMatchIndex}
            matchIndexRef={matchIndexRef}
            forceActive={forceActive}
          />
        </p>
      </ClauseLine>
      {hasChildren && (
        <div className="regulatoryReaderClauseChildren">
          <AnimatePresence initial={false}>
            {clause.children.map((child) => (
              <ClauseBlock
                clause={child}
                searchTerm={searchTerm}
                activeMatchIndex={activeMatchIndex}
                matchIndexRef={matchIndexRef}
                clauseKey={`${clauseKey}-${child.id}`}
                delayedRevealIds={delayedRevealIds}
                collapsingHideIds={collapsingHideIds}
                forceActive={forceActive}
                depth={depth + 1}
                key={`${clauseKey}-${child.id}`}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </Wrapper>
  );
}

function renderRegulationText(
  text,
  keyPrefix,
  searchTerm,
  activeMatchIndex,
  matchIndexRef,
  prefilteredBlocks = null,
  delayedRevealIds = null,
  collapsingHideIds = null,
  forceActive = false,
  keepAllGuidanceBlocksTogether = false,
  hideFigureCaptionText = false,
  structuralSearch = null
) {
  let subheadingIndex = 0;
  const blocks =
    prefilteredBlocks || filterRegulationBlocksForSearch(formatRegulationText(text), searchTerm);
  const groupedBlocks = keepAllGuidanceBlocksTogether
    ? groupGuidanceOnlyBlocks(blocks)
    : groupGuidanceBlocks(blocks);
  const displayBlocks = groupRuleBlocks(groupedBlocks);
  const dfsaFigures = {
    "Figure 1. The Risk-Based Approach (RBA)": {
      src: dfsaFigureRiskBasedApproach,
      alt: "DFSA Figure 1. The Risk-Based Approach",
    },
    "Figure 2. Business risk-based assessment": {
      src: dfsaFigureBusinessRiskAssessment,
      alt: "DFSA Figure 2. Business risk-based assessment",
      size: "wide",
    },
    "Figure 3. Customer risk-based assessment": {
      src: dfsaFigureCustomerRiskAssessment,
      alt: "DFSA Figure 3. Customer risk-based assessment",
      size: "wide",
    },
    "Figure 4. CDD": {
      src: dfsaFigureCdd,
      alt: "DFSA Figure 4. CDD",
      size: "wide",
    },
    "Figure 5. Reliance and outsourcing": {
      src: dfsaFigureReliance,
      alt: "DFSA Figure. Reliance and outsourcing",
      size: "full",
    },
  };
  const renderFigure = (caption) => {
    const figure = dfsaFigures[caption];
    const figureSizeClass = figure?.size ? ` regulatoryReaderFigure--${figure.size}` : "";

    return figure ? (
      <span className={`regulatoryReaderFigure${figureSizeClass}`}>
        <img src={figure.src} alt={figure.alt} />
      </span>
    ) : null;
  };

  const renderBlock = (block, index, parentKey = keyPrefix) => {
    const blockKey = `${parentKey}-${block.id || index}`;

    if (block.type === "guidanceBox") {
      return (
        <motion.div
          className={`regulatoryReaderGuidanceBox${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          <p className="regulatoryReaderGuidanceLabel">
            <HighlightedText
              text={block.title || "Guidance"}
              searchTerm={searchTerm}
              activeMatchIndex={activeMatchIndex}
              matchIndexRef={matchIndexRef}
              forceActive={forceActive}
            />
          </p>
          <div className="regulatoryReaderGuidanceBody">
            {block.blocks.map((guidanceBlock, guidanceIndex) =>
              renderBlock(guidanceBlock, guidanceIndex, blockKey)
            )}
          </div>
        </motion.div>
      );
    }

    if (block.type === "contentBox") {
      return (
        <motion.div
          className={`regulatoryReaderContentBox${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          <h4 className="regulatoryReaderContentBoxTitle">
            <HighlightedText
              text={block.title}
              searchTerm={searchTerm}
              activeMatchIndex={activeMatchIndex}
              matchIndexRef={matchIndexRef}
              forceActive={forceActive}
            />
          </h4>
          <div className="regulatoryReaderContentBoxBody">
            {(block.blocks || []).map((boxBlock, boxBlockIndex) =>
              renderBlock(boxBlock, boxBlockIndex, blockKey)
            )}
          </div>
        </motion.div>
      );
    }

    if (block.type === "contentHeading") {
      const isFigureCaption = Boolean(dfsaFigures[block.text]);
      return (
        <motion.h4
          className={`regulatoryReaderContentHeading${isFigureCaption ? " regulatoryReaderContentHeading--figure" : ""}${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          {!hideFigureCaptionText && (
            <HighlightedText
              text={block.text}
              searchTerm={searchTerm}
              activeMatchIndex={activeMatchIndex}
              matchIndexRef={matchIndexRef}
              forceActive={forceActive}
            />
          )}
          {renderFigure(block.text)}
        </motion.h4>
      );
    }

    if (block.type === "clause") {
      return (
        <ClauseBlock
          clause={block}
          searchTerm={searchTerm}
          activeMatchIndex={activeMatchIndex}
          matchIndexRef={matchIndexRef}
          clauseKey={blockKey}
          delayedRevealIds={delayedRevealIds}
          collapsingHideIds={collapsingHideIds}
          forceActive={forceActive}
          key={blockKey}
        />
      );
    }

    if (block.type === "definition") {
      return (
        <motion.div
          className={`regulatoryReaderDefinition${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          <strong>
            <HighlightedText
              text={block.term}
              searchTerm={searchTerm}
              activeMatchIndex={activeMatchIndex}
              matchIndexRef={matchIndexRef}
              forceActive={forceActive}
            />
          </strong>
          <div>
            <p>
              <HighlightedText
                text={block.text}
                searchTerm={searchTerm}
                activeMatchIndex={activeMatchIndex}
                matchIndexRef={matchIndexRef}
                forceActive={forceActive}
              />
            </p>
            {block.items.map((item, itemIndex) => (
              <ClauseBlock
                clause={item}
                className="regulatoryReaderClause--definition"
                searchTerm={searchTerm}
                activeMatchIndex={activeMatchIndex}
                matchIndexRef={matchIndexRef}
                clauseKey={`${blockKey}-${item.id || itemIndex}`}
                delayedRevealIds={delayedRevealIds}
                collapsingHideIds={collapsingHideIds}
                forceActive={forceActive}
                key={`${blockKey}-${item.id || itemIndex}`}
              />
            ))}
          </div>
        </motion.div>
      );
    }

    if (block.type === "subheading") {
      const subsectionId = getReaderSubsectionId(
        keyPrefix,
        Number.isInteger(block.subsectionIndex) ? block.subsectionIndex : subheadingIndex
      );
      subheadingIndex += 1;
      const subheadingNumber = normalizeReaderRuleNumber(block.text.split(/\s+/)[0]);
      const shouldHighlightStructuralSubheading =
        structuralSearch &&
        (structuralSearch.type === "section" || structuralSearch.type === "ruleOrSection") &&
        subheadingNumber === normalizeReaderRuleNumber(structuralSearch.number);

      return (
        <motion.h4
          className={`regulatoryReaderSubheading${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          id={subsectionId}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          <HighlightedText
            text={block.text}
            searchTerm={shouldHighlightStructuralSubheading ? block.text : searchTerm}
            activeMatchIndex={activeMatchIndex}
            matchIndexRef={matchIndexRef}
            forceActive={forceActive}
          />
        </motion.h4>
      );
    }

    if (block.type === "inlineHeading") {
      return (
        <motion.p
          className={`regulatoryReaderInlineHeading${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          <HighlightedText
            text={block.text}
            searchTerm={searchTerm}
            activeMatchIndex={activeMatchIndex}
            matchIndexRef={matchIndexRef}
            forceActive={forceActive}
          />
        </motion.p>
      );
    }

    if (block.type === "ruleLabel") {
      const shouldHighlightStructuralRule =
        structuralSearch &&
        (structuralSearch.type === "rule" || structuralSearch.type === "ruleOrSection") &&
        normalizeReaderRuleNumber(block.text) === normalizeReaderRuleNumber(structuralSearch.number);

      return (
        <motion.p
          className={`regulatoryReaderRuleLabel${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          <HighlightedText
            text={block.text}
            searchTerm={shouldHighlightStructuralRule ? block.text : searchTerm}
            activeMatchIndex={activeMatchIndex}
            matchIndexRef={matchIndexRef}
            forceActive={forceActive}
          />
        </motion.p>
      );
    }

    if (block.type === "ruleBox") {
      const shouldHighlightStructuralRule =
        structuralSearch &&
        (structuralSearch.type === "rule" || structuralSearch.type === "ruleOrSection") &&
        normalizeReaderRuleNumber(block.label) === normalizeReaderRuleNumber(structuralSearch.number);

      return (
        <motion.div
          className={`regulatoryReaderRuleBox${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          id={getReaderRuleBoxId(keyPrefix, block.label)}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          <p className="regulatoryReaderRuleLabel regulatoryReaderRuleLabel--floating">
            <HighlightedText
              text={block.label}
              searchTerm={shouldHighlightStructuralRule ? block.label : searchTerm}
              activeMatchIndex={activeMatchIndex}
              matchIndexRef={matchIndexRef}
              forceActive={forceActive}
            />
          </p>
          <div className="regulatoryReaderRuleBody">
            {block.blocks.map((ruleBlock, ruleBlockIndex) =>
              renderBlock(ruleBlock, ruleBlockIndex, blockKey)
            )}
          </div>
        </motion.div>
      );
    }

    if (block.type === "applicationTable") {
      return (
        <motion.div
          className={`regulatoryReaderApplicationTableWrap${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          <table className="regulatoryReaderApplicationTable">
            <thead>
              <tr>
                <th>Relevant Person</th>
                <th>Applicable Chapters</th>
              </tr>
            </thead>
            <tbody>
              {(block.rows || []).map((row) => (
                <tr key={`${blockKey}-${row.person}`}>
                  <th scope="row">
                    <HighlightedText
                      text={row.person}
                      searchTerm={searchTerm}
                      activeMatchIndex={activeMatchIndex}
                      matchIndexRef={matchIndexRef}
                      forceActive={forceActive}
                    />
                  </th>
                  <td>
                    <HighlightedText
                      text={row.chapters}
                      searchTerm={searchTerm}
                      activeMatchIndex={activeMatchIndex}
                      matchIndexRef={matchIndexRef}
                      forceActive={forceActive}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {block.footnote && (
            <p className="regulatoryReaderApplicationFootnote">
              <HighlightedText
                text={block.footnote}
                searchTerm={searchTerm}
                activeMatchIndex={activeMatchIndex}
                matchIndexRef={matchIndexRef}
                forceActive={forceActive}
              />
            </p>
          )}
        </motion.div>
      );
    }

    if (block.type === "glossaryTable") {
      return (
        <motion.div
          className={`regulatoryReaderGlossaryTableWrap${delayedRevealIds?.has(block.id) ? " regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`}
          key={blockKey}
          {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
        >
          <table className="regulatoryReaderGlossaryTable">
            <thead>
              <tr>
                <th>Term</th>
                <th>Definition</th>
              </tr>
            </thead>
            <tbody>
              {(block.rows || []).map((row) => {
                const definitionParts =
                  block.splitDefinitions === false
                    ? { lead: row.definition, leadItem: null, items: [] }
                    : splitGlossaryDefinition(row.definition);

                return (
                  <tr key={`${blockKey}-${row.term}`}>
                    <th scope="row">
                      <HighlightedText
                        text={formatGlossaryTermLabel(row.term)}
                        searchTerm={searchTerm}
                        activeMatchIndex={activeMatchIndex}
                        matchIndexRef={matchIndexRef}
                        forceActive={forceActive}
                      />
                    </th>
                    <td>
                      <div className="regulatoryReaderGlossaryDefinition">
                        {definitionParts.lead && (
                          <p>
                            <HighlightedText
                              text={definitionParts.lead}
                              searchTerm={searchTerm}
                              activeMatchIndex={activeMatchIndex}
                              matchIndexRef={matchIndexRef}
                              forceActive={forceActive}
                            />
                          </p>
                        )}
                        {definitionParts.leadItem && (
                          <div className="regulatoryReaderGlossaryClause">
                            <span>{definitionParts.leadItem.marker}</span>
                            <p>
                              <HighlightedText
                                text={definitionParts.leadItem.text}
                                searchTerm={searchTerm}
                                activeMatchIndex={activeMatchIndex}
                                matchIndexRef={matchIndexRef}
                                forceActive={forceActive}
                              />
                            </p>
                          </div>
                        )}
                        {definitionParts.items.length > 0 && (
                          <div className="regulatoryReaderGlossaryClauses">
                            {definitionParts.items.map((item) => (
                              <div
                                className="regulatoryReaderGlossaryClauseGroup"
                                key={`${row.term}-${item.marker}`}
                              >
                                <div className="regulatoryReaderGlossaryClause">
                                  <span>{item.marker}</span>
                                  <p>
                                    <HighlightedText
                                      text={item.text}
                                      searchTerm={searchTerm}
                                      activeMatchIndex={activeMatchIndex}
                                      matchIndexRef={matchIndexRef}
                                      forceActive={forceActive}
                                    />
                                  </p>
                                </div>
                                {item.children?.length > 0 && (
                                  <div className="regulatoryReaderGlossaryNestedClauses">
                                    {item.children.map((child) => (
                                      <div
                                        className="regulatoryReaderGlossaryClause"
                                        key={`${row.term}-${item.marker}-${child.marker}`}
                                      >
                                        <span>{child.marker}</span>
                                        <p>
                                          <HighlightedText
                                            text={child.text}
                                            searchTerm={searchTerm}
                                            activeMatchIndex={activeMatchIndex}
                                            matchIndexRef={matchIndexRef}
                                            forceActive={forceActive}
                                          />
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </motion.div>
      );
    }

    return (
      <motion.p
        className={`${delayedRevealIds?.has(block.id) ? "regulatoryReaderBlock--delayedReveal" : ""}${collapsingHideIds?.has(block.id) ? " regulatoryReaderBlock--collapsingHide" : ""}`.trim() || undefined}
        key={blockKey}
        {...getRegulatoryBlockMotion(delayedRevealIds?.has(block.id))}
      >
        <HighlightedText
          text={block.text}
          searchTerm={searchTerm}
          activeMatchIndex={activeMatchIndex}
          matchIndexRef={matchIndexRef}
          forceActive={forceActive}
        />
        {renderFigure(block.text)}
      </motion.p>
    );
  };

  return (
    <AnimatePresence initial={false}>
      {displayBlocks.map((block, index) => renderBlock(block, index))}
    </AnimatePresence>
  );
}

function RegulationCard({ regulation, onOpen, placeholder = false }) {
  return (
    <article
      className={`regulatoryCard regulatoryCard--${regulation.accent}${placeholder ? " regulatoryCard--placeholder" : ""}`}
    >
      <div className="regulatoryCardTop">
        <h3>{regulation.title}</h3>
        {!placeholder && (
          <div className="regulatoryCardActions">
            {(regulation.readerData || regulation.pdfReaderUrl) && (
              <button
                type="button"
                onClick={() => onOpen(regulation)}
                title="Open regulation"
                aria-label={`Open ${regulation.title}`}
              >
                <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
              </button>
            )}
            {regulation.documentUrl && (
              <a
                href={regulation.documentUrl}
                download
                title="Download regulation"
                aria-label={`Download ${regulation.title}`}
              >
                <i className="bi bi-download" aria-hidden="true" />
              </a>
            )}
            {!regulation.documentUrl && regulation.documentPending && (
              <button
                type="button"
                disabled
                title="PDF link will be added later"
                aria-label={`${regulation.title} PDF link will be added later`}
              >
                <i className="bi bi-file-earmark-arrow-down" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="regulatoryCardBody">
        <p className="regulatoryLegalName">{regulation.legalName}</p>
      </div>
    </article>
  );
}

export default function RegulatoryPage() {
  const nav = useNavigate();
  const { org, user, isAdmin, me } = useAuth();
  const enabledAddOns = useRegulatoryAddOns(me?.orgId);
  const [country, setCountry] = useState("uae");
  const [regime, setRegime] = useState("uae");
  const [entityType, setEntityType] = useState("fi");
  const [selectedRegulation, setSelectedRegulation] = useState(null);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedTopicDocumentId, setSelectedTopicDocumentId] = useState("");
  const [expandedTopicArticles, setExpandedTopicArticles] = useState(() => new Set());
  const [regulatorySearchTerm, setRegulatorySearchTerm] = useState("");
  const [submittedRegulatorySearchTerm, setSubmittedRegulatorySearchTerm] = useState("");
  const [activeTopicSearchMatchIndex, setActiveTopicSearchMatchIndex] = useState(0);
  const [hasNavigatedTopicSearch, setHasNavigatedTopicSearch] = useState(false);
  const [readerSearchTerm, setReaderSearchTerm] = useState("");
  const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState(0);
  const [hasNavigatedReaderSearch, setHasNavigatedReaderSearch] = useState(false);
  const [pdfFitPageWidth, setPdfFitPageWidth] = useState(false);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [activePdfPageNumber, setActivePdfPageNumber] = useState(1);
  const [pdfReaderWidth, setPdfReaderWidth] = useState(0);
  const [pdfPageTexts, setPdfPageTexts] = useState([]);
  const [isPdfTextIndexing, setIsPdfTextIndexing] = useState(false);
  const [pdfSearchTerm, setPdfSearchTerm] = useState("");
  const [activePdfSearchIndex, setActivePdfSearchIndex] = useState(0);
  const [isClaudeWizardOpen, setIsClaudeWizardOpen] = useState(false);
  useEffect(() => {
    if (!enabledAddOns.aiWizard) setIsClaudeWizardOpen(false);
  }, [enabledAddOns.aiWizard]);
  const [claudeQuestion, setClaudeQuestion] = useState("");
  const [claudeAnswer, setClaudeAnswer] = useState(null);
  const [claudeError, setClaudeError] = useState("");
  const [isClaudeLoading, setIsClaudeLoading] = useState(false);
  const [activeClaudeCitationId, setActiveClaudeCitationId] = useState(null);
  const [isClaudeHeaderCompact, setIsClaudeHeaderCompact] = useState(false);
  const [isClaudeArticlesModalOpen, setIsClaudeArticlesModalOpen] = useState(false);
  const [expandedReaderArticles, setExpandedReaderArticles] = useState(() => new Set());
  const [collapsingReaderArticles, setCollapsingReaderArticles] = useState(() => new Set());
  const readerBodyRef = useRef(null);
  const topicBodyRef = useRef(null);
  const pdfReaderRef = useRef(null);
  const pdfPagesScrollerRef = useRef(null);
  const claudeBodyRef = useRef(null);
  const claudeSourcesRef = useRef(null);
  const claudeArticlesBodyRef = useRef(null);
  const pdfPageRefs = useRef([]);
  const collapseTimerRefs = useRef(new Map());
  const [relatedReaderArticle, setRelatedReaderArticle] = useState(null);
  const readerModalRef = useRef(null);
  const orgRegulatoryCountry = org?.geo || org?.country || org?.jurisdictionCountry || "";
  const constrainedCountry = useMemo(
    () => normalizeRegulatoryOrgCountry(orgRegulatoryCountry),
    [orgRegulatoryCountry]
  );
  const latestUpdates = latestUpdatesForCountry(constrainedCountry || country);
  const orgRegulatoryJurisdiction = org?.jurisdiction || org?.juridiction || org?.juridictions || org?.zone || "";
  const constrainedRegime = useMemo(
    () => (constrainedCountry === "uae" ? normalizeRegulatoryOrgRegime(orgRegulatoryJurisdiction) : ""),
    [constrainedCountry, orgRegulatoryJurisdiction]
  );
  const constrainedEntityType = useMemo(() => normalizeRegulatoryOrgEntityType(org), [org]);
  const availableCountryOptions = useMemo(
    () =>
      constrainedCountry
        ? regulatoryCountryOptions.filter((option) => option.value === constrainedCountry)
        : regulatoryCountryOptions,
    [constrainedCountry]
  );
  const availableRegimeOptions = useMemo(
    () =>
      constrainedRegime
        ? regulatoryRegimeOptions.filter((option) => option.value === constrainedRegime)
        : regulatoryRegimeOptions,
    [constrainedRegime]
  );
  const availableEntityTypeOptions = useMemo(
    () =>
      constrainedEntityType
        ? regulatoryEntityTypeOptions.filter((option) => option.value === constrainedEntityType)
        : regulatoryEntityTypeOptions,
    [constrainedEntityType]
  );
  const shouldShowCountrySelector = isAdmin && availableCountryOptions.length > 1;
  const shouldShowRegimeSelector =
    isAdmin && country === "uae" && availableRegimeOptions.length > 1;
  const shouldShowEntityTypeSelector =
    isAdmin && country === "uae" && availableEntityTypeOptions.length > 1;
  const coreRegulations = useMemo(() => {
    if (country === "switzerland") return swissCoreRegulations;

    return [
      ...mainlandBaseRegulations,
      mainlandGuidelineByEntityType[entityType] || mainlandGuidelineByEntityType.fi,
    ];
  }, [country, entityType]);
  const coreRegulationsEyebrow =
    country === "switzerland" ? "Statutory Basis" : "Federal Framework";
  const coreRegulationsTitle =
    country === "switzerland" ? "Core Swiss Legislation & Ordinances" : "Core UAE Regulations";
  const regimeSpecificRegulations =
    country === "uae" && regime === "dfsa"
      ? dfsaCoreRegulations
      : country === "uae" && regime === "fsra"
        ? fsraCoreRegulations
        : [];
  const regimeSpecificTitle = regime === "dfsa" ? "DFSA Regulations" : "FSRA Regulations";
  const regimeSpecificEyebrow = regime === "dfsa" ? "DIFC (DFSA)" : "ADGM (FSRA)";
  const specializedRegulations =
    country === "switzerland" ? specializedSwissRegulations : specializedUaeRegulations;
  const specializedRegulationsEyebrow =
    country === "switzerland" ? "Self-Regulation" : "Specialized";
  const specializedRegulationsTitle =
    country === "switzerland" ? "Swiss Self-Regulatory Standards" : "Specialized UAE Regulations";
  const topicChips = useMemo(
    () => (country === "uae" ? [...baseTopicChips, "MLRO"] : baseTopicChips),
    [country]
  );
  const regulatoryDocumentMap = useMemo(() => {
    const map = new Map();

    [
      ...mainlandBaseRegulations,
      ...Object.values(mainlandGuidelineByEntityType),
      ...dfsaCoreRegulations,
      ...fsraCoreRegulations,
      ...specializedUaeRegulations,
      ...swissCoreRegulations,
      ...specializedSwissRegulations,
      ...fatfRegulations,
    ].forEach((regulation) => {
      if (regulation.readerData?.id) {
        map.set(regulation.readerData.id, regulation);
      }
    });

    return map;
  }, []);
  const claudeCitations = useMemo(
    () => (claudeAnswer ? extractClaudeCitations(claudeAnswer.answer) : []),
    [claudeAnswer]
  );
  const claudeCitationIndex = useMemo(
    () => buildClaudeCitationIndex(claudeCitations),
    [claudeCitations]
  );
  const claudeCitationGroups = useMemo(
    () => groupClaudeCitationsByDocument(claudeCitations, claudeAnswer?.documents),
    [claudeAnswer?.documents, claudeCitations]
  );
  const claudeArticleSources = useMemo(
    () =>
      claudeCitations.map((citation) =>
        resolveClaudeCitationArticleSource(citation, regulatoryDocumentMap, claudeAnswer?.documents)
      ),
    [claudeAnswer?.documents, claudeCitations, regulatoryDocumentMap]
  );
  const searchableRegulations = useMemo(
    () =>
      [
        ...coreRegulations,
        ...regimeSpecificRegulations,
        ...specializedRegulations,
        ...fatfRegulations,
      ].filter((regulation) => regulation.readerData),
    [coreRegulations, regimeSpecificRegulations, specializedRegulations]
  );
  const selectedTopic = useMemo(() => {
    if (submittedRegulatorySearchTerm.trim()) {
      return resolveRegulatorySearchTopic(submittedRegulatorySearchTerm, searchableRegulations);
    }

    const topic = resolveRegulatoryTopic(regulatoryTopics[selectedTopicId], regulatoryDocumentMap);

    if (!topic) {
      return null;
    }

    return {
      ...topic,
      documents: topic.documents.filter((document) =>
        isTopicDocumentVisibleForSelection(document.documentId, country, regime, entityType)
      ),
    };
  }, [
    country,
    entityType,
    regime,
    regulatoryDocumentMap,
    searchableRegulations,
    selectedTopicId,
    submittedRegulatorySearchTerm,
  ]);
  const hasTopicOverview = hasRegulatoryTopicOverview(selectedTopic, country);
  const isTopicOverviewSelected = hasTopicOverview &&
    (!selectedTopicDocumentId || selectedTopicDocumentId === "overview");
  const selectedTopicDocument = isTopicOverviewSelected ? null :
    selectedTopic?.documents.find((document) => document.documentId === selectedTopicDocumentId) ||
    selectedTopic?.documents[0] ||
    null;
  const topicHighlightTerms = selectedTopic?.keywords || "";
  const topicSearchCardTargets = useMemo(() => {
    if (!selectedTopic || !topicHighlightTerms) {
      return [];
    }

    return selectedTopic.documents.flatMap((topicDocument) =>
      topicDocument.sections
        .filter((topicSection) => topicSectionHasSearchMatch(topicSection, topicHighlightTerms))
        .map((topicSection) => ({
          documentId: topicDocument.documentId,
          elementId: getTopicSectionElementId(selectedTopic.id, topicDocument.documentId, topicSection),
        }))
    );
  }, [selectedTopic, topicHighlightTerms]);
  const topicSearchCardCount = topicSearchCardTargets.length;
  const activeTopicSearchTarget =
    topicSearchCardCount > 0 ? topicSearchCardTargets[activeTopicSearchMatchIndex] : null;
  const activeTopicSearchCardId =
    hasNavigatedTopicSearch && activeTopicSearchTarget ? activeTopicSearchTarget.elementId : null;
  const selectTopicDocument = (documentId, options = {}) => {
    setHasNavigatedTopicSearch(false);
    setSelectedTopicDocumentId(documentId);
    if (options.scrollTop) {
      window.requestAnimationFrame(() => {
        topicBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  };
  const closeTopicExplorer = () => {
    setSelectedTopicId(null);
    setSubmittedRegulatorySearchTerm("");
    setActiveTopicSearchMatchIndex(0);
    setHasNavigatedTopicSearch(false);
  };
  const submitRegulatorySearch = () => {
    const searchTerm = regulatorySearchTerm.trim();

    if (!searchTerm) {
      setSubmittedRegulatorySearchTerm("");
      setSelectedTopicId(null);
      return;
    }

    setSelectedRegulation(null);
    setSelectedTopicDocumentId("");
    setSelectedTopicId(null);
    setSubmittedRegulatorySearchTerm(searchTerm);
  };
  const handleRegulatorySearchKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    submitRegulatorySearch();
  };
  // Selection may survive a live source update; resolve the current document
  // rather than rendering the snapshot stored when the reader was opened.
  const modalReaderData =
    regulatoryDocumentMap.get(selectedRegulation?.readerData?.id)?.readerData ||
    selectedRegulation?.readerData ||
    placeholderReaderData;
  const isPdfReader = Boolean(selectedRegulation?.pdfReaderUrl && !selectedRegulation?.readerData);
  const pdfRenderedPageWidth = pdfFitPageWidth
    ? Math.max(360, pdfReaderWidth - 44)
    : Math.min(820, Math.max(360, pdfReaderWidth - 120));
  const normalizedPdfSearchTerm = pdfSearchTerm.trim();
  const pdfSearchMatches = useMemo(() => {
    if (!normalizedPdfSearchTerm) {
      return [];
    }

    const regex = getPdfSearchRegex(normalizedPdfSearchTerm);

    return pdfPageTexts
      .map((text, index) => ({
        pageNumber: index + 1,
        count: (text.match(regex) || []).length,
      }))
      .filter((match) => match.count > 0);
  }, [normalizedPdfSearchTerm, pdfPageTexts]);
  const activePdfSearchPageNumber = pdfSearchMatches[activePdfSearchIndex]?.pageNumber || null;
  const isGuidelineReader =
    modalReaderData.readerStyle === "guideline" || modalReaderData.id?.includes("guidelines");
  const isArticleTitleMenuReader =
    modalReaderData.id === "cabinet-resolution-109-2023" ||
    modalReaderData.id === "cabinet-resolution-111-2022";
  const isFlatGuidelineMenuReader = modalReaderData.id === "fatf-beneficial-ownership-2024";
  const normalizedReaderSearchTerm = readerSearchTerm.trim();
  const structuralReaderSearch = parseReaderStructuralSearch(normalizedReaderSearchTerm);
  const isStructuralReaderSearch =
    Boolean(structuralReaderSearch) &&
    (isStructuralSearchReader(modalReaderData) ||
      (isRulebookStructuralSearchReader(modalReaderData) &&
        isRulebookStructuralSearch(structuralReaderSearch)));
  const readerHighlightTerm = isStructuralReaderSearch ? "" : normalizedReaderSearchTerm;
  const getReaderHeaderHighlightTerm = (text, labelType) =>
    isStructuralReaderSearch
      ? getStructuralReaderLabelHighlightTerm(text, structuralReaderSearch, labelType)
      : readerHighlightTerm;
  const filteredReaderData = useMemo(() => {
    if (!normalizedReaderSearchTerm) {
      return {
        intro: modalReaderData.intro,
        introMatches: Boolean(modalReaderData.intro),
        chapters: modalReaderData.chapters.map((chapter, index) => ({
          ...chapter,
          originalIndex: index,
        })),
      };
    }

    if (isStructuralReaderSearch) {
      if (isRulebookStructuralSearchReader(modalReaderData)) {
        return filterReaderDataForRulebookStructuralSearch(modalReaderData, structuralReaderSearch);
      }

      return filterReaderDataForStructuralSearch(modalReaderData, structuralReaderSearch);
    }

    const introMatches = includesSearchTerm(modalReaderData.intro, normalizedReaderSearchTerm);
    const chapters = modalReaderData.chapters
      .map((chapter, index) => ({
        ...chapter,
        originalIndex: index,
        articles: chapter.articles
          .map((article) => {
            const displayArticle = getArticleDisplay(article);
            const articleBlocks = getArticleBlocks(article);
            const filteredBlocks = isGuidelineReader
              ? filterGuidelineBlocksForSearch(articleBlocks, normalizedReaderSearchTerm)
              : filterRegulationBlocksForSearch(articleBlocks, normalizedReaderSearchTerm);
            const titleMatches = includesSearchTerm(
              `${article.label} ${displayArticle.title} ${article.divisionHeading || ""}`,
              normalizedReaderSearchTerm
            );

            if (!titleMatches && filteredBlocks.length === 0) {
              return null;
            }

            return {
              ...article,
              filteredBlocks,
            };
          })
          .filter(Boolean),
      }))
      .filter((chapter) => chapter.articles.length > 0);

    return {
      intro: introMatches ? modalReaderData.intro : "",
      introMatches,
      chapters,
    };
  }, [isGuidelineReader, isStructuralReaderSearch, modalReaderData, normalizedReaderSearchTerm, structuralReaderSearch]);
  const visibleReaderArticleCount = filteredReaderData.chapters.reduce(
    (count, chapter) => count + chapter.articles.length,
    0
  );
  const hasVisibleReaderIntro = !isGuidelineReader && Boolean(filteredReaderData.intro);
  const hasReaderSearchResults =
    !normalizedReaderSearchTerm ||
    hasVisibleReaderIntro ||
    visibleReaderArticleCount > 0;
  const readerSearchCardTargets = useMemo(() => {
    if (!normalizedReaderSearchTerm) {
      return [];
    }

    const targets = [];

    if (hasVisibleReaderIntro) {
      targets.push("regulatory-reader-opening");
    }

    if (isStructuralReaderSearch) {
      if (isRulebookStructuralSearchReader(modalReaderData)) {
        return getRulebookStructuralReaderTargets(filteredReaderData, structuralReaderSearch);
      }

      const targetNumber = String(structuralReaderSearch.number);

      filteredReaderData.chapters.forEach((chapter) => {
        if (structuralReaderSearch.type === "article" || structuralReaderSearch.type === "number") {
          chapter.articles.forEach((article) => {
            if (String(article.number || "").trim() === targetNumber) {
              targets.push(
                `regulatory-reader-article-${slugifyRegulatoryId(`${chapter.chapter}-${article.label}`)}`
              );
            }
          });
        }
      });

      filteredReaderData.chapters.forEach((chapter) => {
        const chapterMatches =
          (structuralReaderSearch.type === "chapter" || structuralReaderSearch.type === "number") &&
          getReaderChapterNumber(chapter) === targetNumber;

        if (chapterMatches) {
          targets.push(`regulatory-reader-chapter-${chapter.originalIndex}`);
        }
      });

      return targets;
    }

    filteredReaderData.chapters.forEach((chapter) => {
      chapter.articles.forEach((article) => {
        targets.push(
          `regulatory-reader-article-${slugifyRegulatoryId(`${chapter.chapter}-${article.label}`)}`
        );
      });
    });

    return targets;
  }, [
    filteredReaderData,
    hasVisibleReaderIntro,
    isStructuralReaderSearch,
    modalReaderData,
    normalizedReaderSearchTerm,
    structuralReaderSearch,
  ]);
  const readerSearchCardCount = readerSearchCardTargets.length;
  const activeReaderSearchCardId = normalizedReaderSearchTerm && hasNavigatedReaderSearch
    ? readerSearchCardTargets[activeSearchMatchIndex]
    : null;

  useEffect(() => {
    if (!selectedTopic?.documents.length) {
      setSelectedTopicDocumentId("");
      return;
    }

    if (isTopicOverviewSelected) return;

    if (!selectedTopic.documents.some((document) => document.documentId === selectedTopicDocumentId)) {
      setSelectedTopicDocumentId(selectedTopic.documents[0].documentId);
    }
  }, [selectedTopic, selectedTopicDocumentId, isTopicOverviewSelected]);

  useEffect(() => {
    setExpandedTopicArticles(new Set());
    setActiveTopicSearchMatchIndex(0);
    setHasNavigatedTopicSearch(false);
    topicBodyRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [selectedTopic?.id]);

  useEffect(() => {
    if (topicSearchCardCount === 0) {
      setActiveTopicSearchMatchIndex(0);
      return;
    }

    if (activeTopicSearchMatchIndex >= topicSearchCardCount) {
      setActiveTopicSearchMatchIndex(topicSearchCardCount - 1);
    }
  }, [activeTopicSearchMatchIndex, topicSearchCardCount]);

  useEffect(() => {
    if (!hasNavigatedTopicSearch || !activeTopicSearchTarget) {
      return;
    }

    if (selectedTopicDocumentId !== activeTopicSearchTarget.documentId) {
      setSelectedTopicDocumentId(activeTopicSearchTarget.documentId);
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document
          .getElementById(activeTopicSearchTarget.elementId)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
        document
          .getElementById(getTopicDocumentElementId(selectedTopic?.id, activeTopicSearchTarget.documentId))
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    });
  }, [activeTopicSearchTarget, hasNavigatedTopicSearch, selectedTopic?.id, selectedTopicDocumentId]);

  useEffect(() => {
    if (!selectedTopic || topicSearchCardCount < 1) {
      return undefined;
    }

    const handleTopicSearchKeyDown = (event) => {
      const activeElement = document.activeElement;
      const isTextEntry =
        activeElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(activeElement.tagName);

      if (isTextEntry) {
        return;
      }

      if (event.key === "Tab") {
        event.preventDefault();
        if (event.shiftKey) {
          goToPreviousTopicSearchMatch();
        } else {
          goToNextTopicSearchMatch();
        }
        return;
      }

      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        event.preventDefault();
        goToNextTopicSearchMatch();
        return;
      }

      if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
        event.preventDefault();
        goToPreviousTopicSearchMatch();
      }
    };

    window.addEventListener("keydown", handleTopicSearchKeyDown);
    return () => window.removeEventListener("keydown", handleTopicSearchKeyDown);
  }, [selectedTopic, topicSearchCardCount]);

  useEffect(() => {
    setRelatedReaderArticle(null);
    setReaderSearchTerm("");
    setActiveSearchMatchIndex(0);
    setHasNavigatedReaderSearch(false);
    setPdfFitPageWidth(false);
    setPdfPageCount(0);
    setActivePdfPageNumber(1);
    setPdfPageTexts([]);
    setIsPdfTextIndexing(false);
    setPdfSearchTerm("");
    setActivePdfSearchIndex(0);
    setExpandedReaderArticles(new Set());
    setCollapsingReaderArticles(new Set());
    collapseTimerRefs.current.forEach((timerId) => clearTimeout(timerId));
    collapseTimerRefs.current.clear();
  }, [selectedRegulation]);

  useEffect(() => {
    setActivePdfSearchIndex(0);
  }, [normalizedPdfSearchTerm]);

  useEffect(() => {
    if (activePdfSearchIndex >= pdfSearchMatches.length) {
      setActivePdfSearchIndex(Math.max(0, pdfSearchMatches.length - 1));
    }
  }, [activePdfSearchIndex, pdfSearchMatches.length]);

  useEffect(() => {
    if (!activePdfSearchPageNumber) {
      return;
    }

    pdfPageRefs.current[activePdfSearchPageNumber]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }, [activePdfSearchPageNumber]);

  useEffect(() => {
    if (!isPdfReader) return undefined;

    const element = pdfReaderRef.current;
    if (!element) return undefined;

    const updatePdfReaderWidth = () => {
      setPdfReaderWidth(element.clientWidth || 0);
    };

    updatePdfReaderWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updatePdfReaderWidth);
      return () => window.removeEventListener("resize", updatePdfReaderWidth);
    }

    const observer = new ResizeObserver(updatePdfReaderWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isPdfReader, selectedRegulation]);

  useEffect(() => {
    if (!isPdfReader || pdfPageCount < 1) return undefined;

    const scroller = pdfPagesScrollerRef.current;
    if (!scroller) return undefined;

    let animationFrameId = 0;

    const updateActivePdfPage = () => {
      const scrollerRect = scroller.getBoundingClientRect();
      const viewportCenter = scrollerRect.top + scrollerRect.height / 2;
      let closestPageNumber = 1;
      let closestDistance = Infinity;

      for (let pageNumber = 1; pageNumber <= pdfPageCount; pageNumber += 1) {
        const pageElement = pdfPageRefs.current[pageNumber];
        if (!pageElement) continue;

        const pageRect = pageElement.getBoundingClientRect();
        const pageCenter = pageRect.top + pageRect.height / 2;
        const distance = Math.abs(pageCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPageNumber = pageNumber;
        }
      }

      setActivePdfPageNumber(closestPageNumber);
    };

    const scheduleActivePdfPageUpdate = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(updateActivePdfPage);
    };

    scheduleActivePdfPageUpdate();
    scroller.addEventListener("scroll", scheduleActivePdfPageUpdate, { passive: true });
    window.addEventListener("resize", scheduleActivePdfPageUpdate);

    return () => {
      cancelAnimationFrame(animationFrameId);
      scroller.removeEventListener("scroll", scheduleActivePdfPageUpdate);
      window.removeEventListener("resize", scheduleActivePdfPageUpdate);
    };
  }, [isPdfReader, pdfPageCount, pdfRenderedPageWidth]);

  useEffect(() => {
    setActiveSearchMatchIndex(0);
    setHasNavigatedReaderSearch(false);
    setExpandedReaderArticles(new Set());
    setCollapsingReaderArticles(new Set());
    readerBodyRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
    collapseTimerRefs.current.forEach((timerId) => clearTimeout(timerId));
    collapseTimerRefs.current.clear();
  }, [normalizedReaderSearchTerm]);

  useEffect(
    () => () => {
      collapseTimerRefs.current.forEach((timerId) => clearTimeout(timerId));
      collapseTimerRefs.current.clear();
    },
    []
  );

  useEffect(() => {
    if (readerSearchCardCount === 0) {
      setActiveSearchMatchIndex(0);
      return;
    }

    if (activeSearchMatchIndex >= readerSearchCardCount) {
      setActiveSearchMatchIndex(readerSearchCardCount - 1);
    }
  }, [activeSearchMatchIndex, readerSearchCardCount]);

  useEffect(() => {
    if (!normalizedReaderSearchTerm || !hasNavigatedReaderSearch || readerSearchCardCount < 1) {
      return;
    }

    const activeCard = activeReaderSearchCardId
      ? document.getElementById(activeReaderSearchCardId)
      : null;

    activeCard?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }, [
    activeSearchMatchIndex,
    activeReaderSearchCardId,
    hasNavigatedReaderSearch,
    normalizedReaderSearchTerm,
    readerSearchCardCount,
  ]);

  useEffect(() => {
    if (!selectedRegulation && !selectedTopic && !isClaudeWizardOpen) {
      return undefined;
    }

    const body = document.body;
    const html = document.documentElement;
    const previousStyles = {
      htmlOverflow: html.style.overflow,
      bodyOverflowY: body.style.overflowY,
      bodyPaddingRight: body.style.paddingRight,
    };
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    html.style.overflow = "hidden";
    body.style.overflowY = "hidden";

    return () => {
      html.style.overflow = previousStyles.htmlOverflow;
      body.style.overflowY = previousStyles.bodyOverflowY;
      body.style.paddingRight = previousStyles.bodyPaddingRight;
    };
  }, [isClaudeWizardOpen, selectedRegulation, selectedTopic]);

  useEffect(() => {
    if (constrainedCountry && country !== constrainedCountry) {
      setCountry(constrainedCountry);
    }
  }, [constrainedCountry, country]);

  useEffect(() => {
    if (constrainedRegime && regime !== constrainedRegime) {
      setRegime(constrainedRegime);
    }
  }, [constrainedRegime, regime]);

  useEffect(() => {
    if (constrainedEntityType && entityType !== constrainedEntityType) {
      setEntityType(constrainedEntityType);
    }
  }, [constrainedEntityType, entityType]);

  useStaffTopBand(
    {
      title: "REGULATORY WATCH",
      titleIcon: regulatoryHeaderIcon,
      logoSrc: clientsTopLogo,
      contentClassName: "regulatoryContentWrap",
      smallBandColor: "#e9d8cf",
      titleColor: "#697785",
      right: (
        <div className="regulatoryTopActions">
          <motion.div className="regulatoryTopSelectorGroup" layout>
            <AnimatePresence initial={false} mode="popLayout">
              {shouldShowCountrySelector && (
                <motion.div
                  className="regulatoryTopSelectWrap"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <select
                    id="regulatory-country"
                    value={country}
                    onChange={(event) => setCountry(event.target.value)}
                    aria-label="Regulatory country"
                  >
                    {availableCountryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence initial={false} mode="popLayout">
              {country === "uae" && (shouldShowRegimeSelector || shouldShowEntityTypeSelector) && (
                <motion.div
                  className="regulatoryTopDynamicControls"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.16, delay: 0.28, ease: "easeOut" }}
                >
                  {shouldShowRegimeSelector && (
                    <div className="regulatoryTopSelectWrap">
                      <select
                        id="regulatory-regime"
                        value={regime}
                        onChange={(event) => setRegime(event.target.value)}
                        aria-label="Regulatory regime"
                      >
                        {availableRegimeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {shouldShowEntityTypeSelector && (
                    <div className="regulatoryTopSelectWrap">
                      <select
                        id="regulatory-entity-type"
                        value={entityType}
                        onChange={(event) => setEntityType(event.target.value)}
                        aria-label="Entity type"
                      >
                        {availableEntityTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <button
            type="button"
            className="btn topBarBtn btn-outline-secondary btn-sm"
            onClick={() => nav("/clients")}
            title="Back to clients"
            aria-label="Back to clients"
          >
            <i className="bi bi-house-door" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn topBarBtn btn-outline-secondary btn-sm"
            title="Refresh"
            aria-label="Refresh"
          >
            <i className="bi bi-arrow-repeat" aria-hidden="true" />
          </button>
        </div>
      ),
    },
    [
      availableCountryOptions,
      availableEntityTypeOptions,
      availableRegimeOptions,
      nav,
      country,
      regime,
      entityType,
      shouldShowCountrySelector,
      shouldShowEntityTypeSelector,
      shouldShowRegimeSelector,
    ]
  );

  const goToPreviousSearchMatch = () => {
    if (readerSearchCardCount < 1) {
      return;
    }

    setHasNavigatedReaderSearch(true);
    setActiveSearchMatchIndex(
      (currentIndex) => (currentIndex - 1 + readerSearchCardCount) % readerSearchCardCount
    );
  };

  const goToNextSearchMatch = () => {
    if (readerSearchCardCount < 1) {
      return;
    }

    setHasNavigatedReaderSearch(true);
    setActiveSearchMatchIndex((currentIndex) => (currentIndex + 1) % readerSearchCardCount);
  };

  const handleReaderSearchKeyDown = (event) => {
    if (readerSearchCardCount < 1) {
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      if (event.shiftKey) {
        goToPreviousSearchMatch();
      } else {
        goToNextSearchMatch();
      }
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      goToNextSearchMatch();
      return;
    }

    if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      goToPreviousSearchMatch();
    }
  };

  const handlePdfLoadSuccess = async (pdfDocument) => {
    setPdfPageCount(pdfDocument.numPages);
    setActivePdfPageNumber(1);
    setPdfPageTexts([]);
    setIsPdfTextIndexing(true);
    pdfPageRefs.current = [];

    try {
      const pageTexts = await Promise.all(
        Array.from({ length: pdfDocument.numPages }, async (_page, index) => {
          const page = await pdfDocument.getPage(index + 1);
          const textContent = await page.getTextContent();
          return (textContent.items || []).map((item) => item.str || "").join(" ");
        })
      );

      setPdfPageTexts(pageTexts);
    } finally {
      setIsPdfTextIndexing(false);
    }
  };

  const goToPreviousPdfSearchMatch = () => {
    if (isPdfTextIndexing || pdfSearchMatches.length < 1) {
      return;
    }

    setActivePdfSearchIndex(
      (currentIndex) => (currentIndex - 1 + pdfSearchMatches.length) % pdfSearchMatches.length
    );
  };

  const goToNextPdfSearchMatch = () => {
    if (isPdfTextIndexing || pdfSearchMatches.length < 1) {
      return;
    }

    setActivePdfSearchIndex((currentIndex) => (currentIndex + 1) % pdfSearchMatches.length);
  };

  const handlePdfSearchKeyDown = (event) => {
    if (isPdfTextIndexing || pdfSearchMatches.length < 1) {
      return;
    }

    if (event.key === "Enter" || event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      goToNextPdfSearchMatch();
      return;
    }

    if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      goToPreviousPdfSearchMatch();
    }
  };

  const openClaudeWizard = () => {
    if (!enabledAddOns.aiWizard) return;
    setIsClaudeWizardOpen(true);
    setClaudeQuestion("");
    setClaudeAnswer(null);
    setClaudeError("");
    setIsClaudeLoading(false);
    setActiveClaudeCitationId(null);
    setIsClaudeHeaderCompact(false);
    setIsClaudeArticlesModalOpen(false);
  };

  const closeClaudeWizard = () => {
    setIsClaudeWizardOpen(false);
    setClaudeError("");
    setIsClaudeHeaderCompact(false);
    setIsClaudeArticlesModalOpen(false);
  };

  const submitClaudeQuestion = async (event) => {
    event.preventDefault();
    if (!enabledAddOns.aiWizard) return;

    const question = claudeQuestion.trim();
    if (question.length < 3 || isClaudeLoading) {
      return;
    }

    setIsClaudeLoading(true);
    setClaudeError("");
    setClaudeAnswer(null);
    setIsClaudeArticlesModalOpen(false);

    try {
      const token = await user?.getIdToken?.();
      const response = await fetch(`${MYKYC_API_BASE}/myKyc/regulatory-ai/ask-claude`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question,
          ...(isAdmin
            ? {
                regulatoryProfile: {
                  country,
                  regime,
                  entityType,
                },
              }
            : {}),
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload?.ok === false) {
        throw new Error(
          [payload?.error, payload?.detail].filter(Boolean).join(" ") ||
            "Unable to get a Claude compliance answer."
        );
      }

      setClaudeAnswer(payload);
    } catch (error) {
      setClaudeError(error?.message || "Unable to get a Claude compliance answer.");
    } finally {
      setIsClaudeLoading(false);
    }
  };

  const openClaudeCitation = (citationText) => {
    const commaIndex = citationText.lastIndexOf(",");
    if (commaIndex === -1) {
      return;
    }

    const titlePart = citationText.slice(0, commaIndex).trim();
    const locatorPart = citationText.slice(commaIndex + 1).trim();

    const regulation = resolveClaudeCitationDocument(titlePart, regulatoryDocumentMap);
    if (!regulation) {
      console.warn("[claude-citation] could not resolve document for citation", citationText);
      return;
    }

    const resolvedSection = resolveClaudeCitationSection(regulation, locatorPart);

    setSelectedRegulation(regulation);

    if (resolvedSection) {
      const targetId =
        resolvedSection.subsectionId ||
        `regulatory-reader-article-${slugifyRegulatoryId(
          `${resolvedSection.chapter.chapter}-${resolvedSection.article.label}`
        )}`;
      const scrollBlock = resolvedSection.subsectionId ? "center" : "start";
      window.setTimeout(() => {
        const target = document.getElementById(targetId);
        const scrollContainer = target?.closest(".regulatoryReaderBody");
        const previousScrollBehavior = scrollContainer?.style.scrollBehavior;

        if (scrollContainer) {
          scrollContainer.style.scrollBehavior = "auto";
        }

        target?.scrollIntoView({ block: scrollBlock });

        if (scrollContainer) {
          window.requestAnimationFrame(() => {
            scrollContainer.style.scrollBehavior = previousScrollBehavior || "";
          });
        }
      }, 120);
    }
  };

  const scrollClaudeAnswerToCitation = (citationId) => {
    if (!citationId) {
      return;
    }

    const container = claudeBodyRef.current;
    const target = container?.querySelector(
      `[data-claude-citation-targets~="${String(citationId)}"]`
    );

    if (!container || !target) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const isAboveFrame = targetRect.top < containerRect.top + 12;
    const isBelowFrame = targetRect.bottom > containerRect.bottom - 12;

    if (isAboveFrame || isBelowFrame) {
      const targetTop =
        target.offsetTop - Math.max(0, (container.clientHeight - target.offsetHeight) / 2);
      container.scrollTo({
        top: Math.max(0, targetTop),
        behavior: "smooth",
      });
    }
  };

  const previewClaudeCitation = (citationId) => {
    setActiveClaudeCitationId(citationId);
    scrollClaudeAnswerToCitation(citationId);
  };

  const scrollClaudeSourceToCitation = (citationId) => {
    if (!citationId) {
      return;
    }

    const container = claudeSourcesRef.current;
    const target = container?.querySelector(
      `[data-claude-source-id="${String(citationId)}"]`
    );

    if (!container || !target) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const isAboveFrame = targetRect.top < containerRect.top + 12;
    const isBelowFrame = targetRect.bottom > containerRect.bottom - 12;

    if (isAboveFrame || isBelowFrame) {
      const targetTop =
        target.offsetTop - Math.max(0, (container.clientHeight - target.offsetHeight) / 2);

      container.scrollTo({
        top: Math.max(0, targetTop),
        behavior: "smooth",
      });
    }
  };

  const previewClaudeSourceCitation = (citationId) => {
    setActiveClaudeCitationId(citationId);
    scrollClaudeSourceToCitation(citationId);
  };

  const scrollClaudeArticleCard = (direction) => {
    const container = claudeArticlesBodyRef.current;
    const cards = Array.from(container?.querySelectorAll(".regulatorySourceExcerptCard") || []);

    if (!container || cards.length < 1) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const currentIndex = cards.findIndex((card) => {
      const cardRect = card.getBoundingClientRect();
      return cardRect.bottom > containerRect.top + 24;
    });
    const normalizedCurrentIndex = currentIndex === -1 ? cards.length - 1 : currentIndex;
    const targetIndex =
      direction === "previous"
        ? Math.max(0, normalizedCurrentIndex - 1)
        : Math.min(cards.length - 1, normalizedCurrentIndex + 1);

    cards[targetIndex]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleClaudeBodyScroll = (event) => {
    setIsClaudeHeaderCompact(event.currentTarget.scrollTop > 8);
  };

  const readerSearchMatchCursor = { current: 0 };

  const goToPreviousTopicSearchMatch = () => {
    if (topicSearchCardCount < 1) {
      return;
    }

    setHasNavigatedTopicSearch(true);
    setActiveTopicSearchMatchIndex(
      (currentIndex) => (currentIndex - 1 + topicSearchCardCount) % topicSearchCardCount
    );
  };

  const goToNextTopicSearchMatch = () => {
    if (topicSearchCardCount < 1) {
      return;
    }

    setHasNavigatedTopicSearch(true);
    setActiveTopicSearchMatchIndex((currentIndex) => (currentIndex + 1) % topicSearchCardCount);
  };

  return (
    <div className="regulatoryPage staffShellContentPage">
      <div className="regulatoryContentWrap">
        <section className="regulatoryTopGrid">
          <div className="regulatorySearchBand" aria-label="Search regulations">
            <div className="regulatorySearchInput">
              <i className="bi bi-search" aria-hidden="true" />
              <input
                type="search"
                value={regulatorySearchTerm}
                onChange={(event) => setRegulatorySearchTerm(event.target.value)}
                onKeyDown={handleRegulatorySearchKeyDown}
                placeholder="Search regulations..."
                aria-label="Search regulations"
              />
              <button
                type="button"
                onClick={submitRegulatorySearch}
                aria-label="Run search"
                title="Search"
              >
                <i className="bi bi-arrow-right" aria-hidden="true" />
              </button>
            </div>

            <div className="regulatoryChipRow" aria-label="Common regulation topics">
              {topicChips.map((chip) => {
                const topicId = slugifyRegulatoryId(chip);
                const hasTopic = Boolean(regulatoryTopics[topicId]);
                const isEnabledTopic = hasTopic &&
                  (topicId === "sars" || (["peps", "sanctions", "mlro", "training", "record-keeping", "risk-factors", "risk-rating", "periodic-review", "sow-sof"].includes(topicId) && country === "uae"));

                return (
                  <button
                    type="button"
                    key={chip}
                    onClick={() => {
                      if (isEnabledTopic) {
                        setSubmittedRegulatorySearchTerm("");
                        setSelectedTopicId(topicId);
                        setSelectedTopicDocumentId("");
                        setSelectedRegulation(null);
                      }
                    }}
                    disabled={!isEnabledTopic}
                    aria-haspopup={isEnabledTopic ? "dialog" : undefined}
                  >
                    {chip}
                  </button>
                );
              })}
            </div>
          </div>

          <RegulatoryAddOns enabled={enabledAddOns} onOpenWizard={openClaudeWizard} />
        </section>

        <section className="regulatoryMainGrid">
          <div className="regulatoryPrimaryColumn">
            <div className="regulatorySectionHeader">
              <div>
                <p className="regulatoryEyebrow">{coreRegulationsEyebrow}</p>
                <h2>{coreRegulationsTitle}</h2>
              </div>
            </div>

            <div className="regulatoryCardsGrid">
              {coreRegulations.map((regulation) => (
                <RegulationCard
                  key={regulation.title}
                  regulation={regulation}
                  onOpen={setSelectedRegulation}
                />
              ))}
            </div>

            {regimeSpecificRegulations.length > 0 && (
              <>
                <div className="regulatorySectionHeader regulatorySectionHeader--specialized">
                  <div>
                    <p className="regulatoryEyebrow">{regimeSpecificEyebrow}</p>
                    <h2>{regimeSpecificTitle}</h2>
                  </div>
                </div>

                <div className="regulatoryCardsGrid regulatoryCardsGrid--specialized">
                  {regimeSpecificRegulations.map((regulation) => (
                    <RegulationCard
                      key={regulation.title}
                      regulation={regulation}
                      onOpen={setSelectedRegulation}
                    />
                  ))}
                </div>
              </>
            )}

            <div className="regulatorySectionHeader regulatorySectionHeader--specialized">
              <div>
                <p className="regulatoryEyebrow">{specializedRegulationsEyebrow}</p>
                <h2>{specializedRegulationsTitle}</h2>
              </div>
            </div>

            <div className="regulatoryCardsGrid regulatoryCardsGrid--specialized">
              {specializedRegulations.map((regulation) => (
                <RegulationCard
                  key={regulation.title}
                  regulation={regulation}
                  onOpen={setSelectedRegulation}
                  placeholder={!regulation.readerData && !regulation.documentUrl}
                />
              ))}
            </div>

            <div className="regulatorySectionHeader regulatorySectionHeader--specialized">
              <div>
                <p className="regulatoryEyebrow">FATF</p>
                <h2>FATF Regulations</h2>
              </div>
            </div>

            <div className="regulatoryCardsGrid regulatoryCardsGrid--specialized">
              {fatfRegulations.map((regulation) => (
                <RegulationCard
                  key={regulation.title}
                  regulation={regulation}
                  onOpen={setSelectedRegulation}
                  placeholder={!regulation.readerData && !regulation.documentUrl}
                />
              ))}
            </div>
          </div>

          <aside className="regulatorySideColumn" aria-label="Regulatory watch">
            <div className="regulatorySectionHeader regulatorySectionHeader--compact">
              <div>
                <p className="regulatoryEyebrow">Watch</p>
                <h2>Latest Updates</h2>
              </div>
            </div>

            <section className="regulatoryUpdatesPanel">
              <div className="regulatoryTimeline">
                {latestUpdates.map((item) => (
                  <article className="regulatoryTimelineItem" key={`${item.label}-${item.date}`}>
                    <div className="regulatoryTimelineMarker" aria-hidden="true" />
                    <div>
                      <div className="regulatoryTimelineMeta">
                        <strong>{item.label}</strong>
                        <span>{item.dateType} <time dateTime={item.dateIso}>{item.date}</time></span>
                      </div>
                      <p>{item.text}</p>
                      <a className="regulatoryUpdateSource" href={item.sourceUrl} target="_blank" rel="noopener noreferrer">
                        {item.source} <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </aside>
        </section>
      </div>

      {isClaudeWizardOpen && (
        <div
          className="regulatoryReaderOverlay regulatoryClaudeOverlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeClaudeWizard();
            }
          }}
        >
          <section
            className={`regulatoryReaderModal regulatoryClaudeModal${
              !claudeAnswer && !claudeError ? " regulatoryClaudeModal--question" : ""
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="regulatory-claude-title"
          >
            <header
              className={`regulatoryReaderHeader regulatoryClaudeHeader${
                isClaudeHeaderCompact ? " regulatoryClaudeHeader--compact" : ""
              }`}
            >
              <div className="regulatoryReaderTitleBlock">
                <p className="regulatoryEyebrow">Regulatory Q&A</p>
                <h2 id="regulatory-claude-title">AI Wizard</h2>
              </div>
              <button
                type="button"
                className="regulatoryReaderClose"
                onClick={closeClaudeWizard}
                title="Close"
                aria-label="Close AI Wizard"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </header>

            <div
              className="regulatoryClaudeBody"
              ref={claudeBodyRef}
              onScroll={handleClaudeBodyScroll}
            >
              {(() => {
                const isClaudeInitialScreen = !claudeAnswer && !claudeError;

                return (
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={isClaudeInitialScreen ? "claude-question" : "claude-answer"}
                      className={`regulatoryClaudeWorkspace${
                        claudeCitations.length ? " regulatoryClaudeWorkspace--withSources" : ""
                      }${isClaudeInitialScreen ? " regulatoryClaudeWorkspace--initial" : ""}${
                        isClaudeLoading ? " regulatoryClaudeWorkspace--loading" : ""
                      }`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                    >
                    <div className="regulatoryClaudeMainColumn">
                      <form
                        className={`regulatoryClaudeQuestionForm${
                          isClaudeInitialScreen ? " regulatoryClaudeQuestionForm--initial" : ""
                        }`}
                        onSubmit={submitClaudeQuestion}
                      >
                        <label htmlFor="regulatory-claude-question">Question</label>
                        <div className="regulatoryClaudeQuestionInput">
                          <textarea
                            id="regulatory-claude-question"
                            value={claudeQuestion}
                            onChange={(event) => setClaudeQuestion(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" && !event.shiftKey) {
                                event.preventDefault();
                                submitClaudeQuestion(event);
                              }
                            }}
                            placeholder="Do we need to identify beneficial owners for a trust?"
                            rows={3}
                          />
                          <button
                            type="submit"
                            disabled={isClaudeLoading || claudeQuestion.trim().length < 3}
                          >
                            Ask
                          </button>
                        </div>
                      </form>

                      {isClaudeLoading && (
                        <div className="regulatoryClaudeLoading" role="status" aria-live="polite">
                          <div
                            className="regulatoryClaudeTextLoader"
                            data-text="Generating answer... Please wait"
                            aria-label="Generating answer... Please wait"
                          />
                        </div>
                      )}

                      {claudeError && (
                        <div className="regulatoryClaudeState regulatoryClaudeState--error" role="alert">
                          {claudeError}
                        </div>
                      )}

                      {claudeAnswer && (
                        <section className="regulatoryClaudeAnswer">
                          <div className="regulatoryClaudeAnswerCard">
                            <p className="regulatoryEyebrow">Answer</p>
                            <div className="regulatoryClaudeAnswerText">
                              {renderClaudeAnswerBlocks(
                                claudeAnswer.answer,
                                claudeCitationIndex,
                                openClaudeCitation,
                                activeClaudeCitationId,
                                previewClaudeSourceCitation,
                                () => setActiveClaudeCitationId(null)
                              )}
                            </div>
                            <p className="regulatoryClaudeAnswerDisclaimer">
                              This is AI-generated regulatory information and should not be considered legal advice. It may contain inaccuracies.
                            </p>
                          </div>
                        </section>
                      )}
                    </div>

                    {isClaudeInitialScreen && (
                      <p className="regulatoryClaudeDisclaimer">
                        This is AI-generated regulatory information and should not be considered legal advice. It may contain inaccuracies.
                      </p>
                    )}

                    {claudeCitations.length > 0 && (
                      <aside
                        className="regulatoryClaudeSources"
                        ref={claudeSourcesRef}
                        aria-label="Claude answer sources"
                      >
                        <p className="regulatoryEyebrow">Sources</p>
                        <div className="regulatoryClaudeSourceList">
                          {claudeCitationGroups.map((group) => (
                            <article className="regulatoryClaudeSourceGroup" key={group.key}>
                              <h4>{group.title}</h4>
                              <div className="regulatoryClaudeSourceRows">
                                {group.citations.map((citation) => (
                                  <button
                                    type="button"
                                    className={`regulatoryClaudeSourceRow${
                                      activeClaudeCitationId === citation.id
                                        ? " regulatoryClaudeSourceRow--active"
                                        : ""
                                    }`}
                                    key={citation.key}
                                    data-claude-source-id={citation.id}
                                    onClick={() => openClaudeCitation(citation.text)}
                                    onMouseEnter={() => previewClaudeCitation(citation.id)}
                                    onMouseLeave={() => setActiveClaudeCitationId(null)}
                                    onFocus={() => previewClaudeCitation(citation.id)}
                                    onBlur={() => setActiveClaudeCitationId(null)}
                                    title="Open in law reader"
                                  >
                                    <span className="regulatoryClaudeSourceNumber">
                                      {citation.id}
                                    </span>
                                    <span>{citation.locator || "Source"}</span>
                                  </button>
                                ))}
                              </div>
                            </article>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="regulatoryClaudeSourceArticleCard"
                          onClick={() => setIsClaudeArticlesModalOpen(true)}
                        >
                          View all cited articles
                        </button>
                      </aside>
                    )}
                    </motion.div>
                  </AnimatePresence>
                );
              })()}
            </div>
          </section>
        </div>
      )}

      {isClaudeArticlesModalOpen && (
        <div
          className="regulatoryReaderOverlay regulatoryClaudeArticlesOverlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsClaudeArticlesModalOpen(false);
            }
          }}
        >
          <section
            className="regulatoryReaderModal regulatoryClaudeArticlesModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="regulatory-claude-articles-title"
          >
            <header className="regulatoryReaderHeader regulatoryClaudeArticlesHeader">
              <div className="regulatoryReaderTitleBlock">
                <p className="regulatoryEyebrow">Sources</p>
                <h2 id="regulatory-claude-articles-title">Cited Regulatory Articles</h2>
              </div>
              <div className="regulatoryClaudeArticlesHeaderActions">
                <button
                  type="button"
                  className="regulatoryClaudeArticlesNavButton"
                  onClick={() => scrollClaudeArticleCard("previous")}
                  title="Previous article"
                  aria-label="Previous article"
                  disabled={claudeArticleSources.length < 2}
                >
                  <i className="bi bi-chevron-left" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="regulatoryClaudeArticlesNavButton"
                  onClick={() => scrollClaudeArticleCard("next")}
                  title="Next article"
                  aria-label="Next article"
                  disabled={claudeArticleSources.length < 2}
                >
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="regulatoryReaderClose"
                  onClick={() => setIsClaudeArticlesModalOpen(false)}
                  title="Close"
                  aria-label="Close selected regulation articles"
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>
            </header>

            <div className="regulatoryClaudeArticlesBody" ref={claudeArticlesBodyRef}>
              <div className="regulatorySourceExcerpts">
                {claudeArticleSources.map((source, index) => {
                  const citationBodyCursor = { current: 0 };
                  const citation = source?.citation;

                  return (
                    <article
                      className="regulatorySourceExcerptCard"
                      key={citation?.key || `claude-article-source-${index}`}
                    >
                      <span className="regulatorySourceExcerptNumber">{citation?.id || index + 1}</span>
                      <span className="regulatorySourceExcerptDocument">
                        {source?.displayTitle || citation?.title || "Regulatory source"}
                      </span>
                      <strong>
                        {citation?.locator || "Source"}
                        {source?.displayArticle?.title ? ` | ${source.displayArticle.title}` : ""}
                      </strong>
                      {source?.displayArticle?.body ? (
                        <div className="regulatorySourceExcerptFullText">
                          {renderRegulationText(
                            source.displayArticle.body,
                            `claude-article-source-${citation?.id || index}`,
                            "",
                            null,
                            citationBodyCursor,
                            source.articleBlocks,
                            null,
                            null,
                            false,
                            Boolean(source.resolvedSection?.article?.guidanceOnly),
                            Boolean(source.resolvedSection?.article?.figureCard),
                            null
                          )}
                        </div>
                      ) : (
                        <p className="regulatorySourceExcerptFullText">
                          {citation?.text || "Unable to display this article excerpt."}
                        </p>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      )}

      {selectedTopic && (
        <div
          className="regulatoryReaderOverlay regulatoryTopicOverlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeTopicExplorer();
            }
          }}
        >
          <section
            className="regulatoryReaderModal regulatoryTopicModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="regulatory-topic-title"
          >
            <header className="regulatoryReaderHeader regulatoryTopicHeader">
              <div className="regulatoryReaderTitleBlock">
                <p className="regulatoryEyebrow">
                  {selectedTopic.isSearchTopic ? "Search Results" : "Topic Explorer"}
                </p>
                <h2 id="regulatory-topic-title">{selectedTopic.label}</h2>
              </div>
              <div className="regulatoryTopicHeaderActions">
                <div className="regulatoryReaderSearchControls regulatoryTopicSearchControls">
                  <span>
                    {topicSearchCardCount > 0 && hasNavigatedTopicSearch
                      ? activeTopicSearchMatchIndex + 1
                      : 0}
                    /
                    {topicSearchCardCount}
                  </span>
                  <button
                    type="button"
                    onClick={goToPreviousTopicSearchMatch}
                    disabled={topicSearchCardCount < 1}
                    title="Previous matching section"
                    aria-label="Previous matching section"
                  >
                    <i className="bi bi-chevron-up" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={goToNextTopicSearchMatch}
                    disabled={topicSearchCardCount < 1}
                    title="Next matching section"
                    aria-label="Next matching section"
                  >
                    <i className="bi bi-chevron-down" aria-hidden="true" />
                  </button>
                </div>
              <button
                type="button"
                className="regulatoryReaderClose"
                onClick={closeTopicExplorer}
                title="Close"
                aria-label={selectedTopic.isSearchTopic ? "Close search results" : "Close topic explorer"}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
              </div>
            </header>

            <div className="regulatoryReaderLayout regulatoryTopicLayout">
              <nav className="regulatoryReaderMenu regulatoryTopicMenu" aria-label="Topic navigation">
                {hasTopicOverview && (
                  <div className={`regulatoryTopicLawGroup regulatoryTopicOverviewMenu${isTopicOverviewSelected ? " regulatoryTopicLawGroup--active" : ""}`}>
                    <button
                      type="button"
                      className={`regulatoryTopicLawButton${isTopicOverviewSelected ? " regulatoryTopicLawButton--active" : ""}`}
                      aria-current={isTopicOverviewSelected ? "page" : undefined}
                      onClick={() => selectTopicDocument("overview", { scrollTop: true })}
                    >
                      <span><i className="bi bi-journal-text" aria-hidden="true" /> Regulatory overview</span>
                      <small>Summary of the regulatory requirements</small>
                    </button>
                  </div>
                )}
                <p className="regulatoryEyebrow">Regulations</p>
                <div className="regulatoryReaderMenuList">
                  {selectedTopic.documents.map((topicDocument) => {
                    const isActiveTopicDocument =
                      selectedTopicDocument?.documentId === topicDocument.documentId;
                    const isGuidelineTopicDocument = guidelineTopicReaderIds.has(
                      topicDocument.regulation?.readerData?.id
                    );

                    return (
                      <div
                        className={`regulatoryTopicLawGroup${
                          isActiveTopicDocument ? " regulatoryTopicLawGroup--active" : ""
                        }${isGuidelineTopicDocument ? " regulatoryTopicLawGroup--guidelines" : ""}`}
                        id={getTopicDocumentElementId(selectedTopic.id, topicDocument.documentId)}
                        key={topicDocument.documentId}
                        onClick={(event) => {
                          if (event.target.closest("button")) return;
                          selectTopicDocument(topicDocument.documentId, { scrollTop: true });
                        }}
                      >
                        <button
                          type="button"
                          className={`regulatoryTopicLawButton${
                            isActiveTopicDocument ? " regulatoryTopicLawButton--active" : ""
                          }`}
                          onClick={() => selectTopicDocument(topicDocument.documentId, { scrollTop: true })}
                          aria-current={isActiveTopicDocument ? "page" : undefined}
                        >
                          <span>{getTopicDocumentMenuTitle(topicDocument.regulation)}</span>
                          <small>{getTopicDocumentSubtitle(topicDocument.regulation)}</small>
                        </button>

                        <div className="regulatoryTopicSectionMenu" aria-label="Displayed sections">
                          {topicDocument.sections.map((topicSection, topicSectionIndex) => {
                            const previousTopicSection =
                              topicDocument.sections[topicSectionIndex - 1] || null;
                            const shouldShowChapter =
                              !previousTopicSection ||
                              previousTopicSection.chapter.chapter !== topicSection.chapter.chapter ||
                              previousTopicSection.chapter.title !== topicSection.chapter.title;

                            return (
                              <div
                                className="regulatoryTopicSectionGroup"
                                key={`${topicDocument.documentId}-${topicSection.article.label}`}
                              >
                                {shouldShowChapter && (
                                  <button
                                    type="button"
                                    className="regulatoryTopicChapterLine"
                                    onClick={() => {
                                      selectTopicDocument(topicDocument.documentId);
                                      window.requestAnimationFrame(() => {
                                        document
                                          .getElementById(
                                            getTopicSectionElementId(
                                              selectedTopic.id,
                                              topicDocument.documentId,
                                              topicSection
                                            )
                                          )
                                          ?.scrollIntoView({ block: "start" });
                                      });
                                    }}
                                  >
                                    <strong>{getTopicChapterMenuLabel(topicSection.chapter.chapter)}</strong>
                                    {topicSection.chapter.title && (
                                      <>
                                        <b aria-hidden="true">|</b>
                                        <span>{topicSection.chapter.title}</span>
                                      </>
                                    )}
                                  </button>
                                )}

                                {getTopicSectionMenuItems(
                                  selectedTopic.id,
                                  topicDocument.documentId,
                                  topicSection
                                ).map((menuItem) => (
                                  <button
                                    type="button"
                                    className={`regulatoryTopicSectionButton${
                                      menuItem.depth > 0 ? " regulatoryTopicSectionButton--child" : ""
                                    }${
                                      isGuidelineTopicDocument && menuItem.depth === 0
                                        ? " regulatoryTopicSectionButton--guidelineSection"
                                        : ""
                                    }`}
                                    key={menuItem.id}
                                    onClick={() => {
                                      selectTopicDocument(topicDocument.documentId);
                                      window.requestAnimationFrame(() => {
                                        document
                                          .getElementById(menuItem.id)
                                          ?.scrollIntoView({ block: "start" });
                                      });
                                    }}
                                  >
                                    <strong>{menuItem.label}</strong>
                                    {menuItem.title !== menuItem.label && (
                                      <span>{menuItem.title}</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </nav>

              <div className="regulatoryReaderBody regulatoryTopicBody" ref={topicBodyRef}>
                {isTopicOverviewSelected ? <RegulatoryTopicOverview
                  topic={selectedTopic}
                  country={country}
                  regime={regime}
                  entityType={entityType}
                /> : selectedTopicDocument ? (
                  <section className="regulatoryReaderChapter regulatoryTopicDocument">
                    {selectedTopicDocument.sections.map((topicSection, topicSectionIndex) => {
                      const excerptBlocks = getArticleBlocks(topicSection.article);
                      const fullArticle = topicSection.fullArticle || topicSection.article;
                      const fullArticleBlocks = getArticleBlocks(fullArticle);
                      const articleElementId = getTopicSectionElementId(
                        selectedTopic.id,
                        selectedTopicDocument.documentId,
                        topicSection
                      );
                      const isExpandedArticle = expandedTopicArticles.has(articleElementId);
                      const canExpandArticle = JSON.stringify(excerptBlocks) !== JSON.stringify(fullArticleBlocks);
                      const displayedArticle = isExpandedArticle ? fullArticle : topicSection.article;
                      const displayArticle = getArticleDisplay(displayedArticle);
                      const articleBlocks = isExpandedArticle ? fullArticleBlocks : excerptBlocks;
                      const previousTopicSection =
                        selectedTopicDocument.sections[topicSectionIndex - 1] || null;
                      const shouldShowParentPath =
                        !previousTopicSection ||
                        previousTopicSection.chapter.chapter !== topicSection.chapter.chapter ||
                        previousTopicSection.chapter.title !== topicSection.chapter.title;
                      const topicMatchCursor = { current: 0 };
                      const isActiveTopicSearchCard = activeTopicSearchCardId === articleElementId;

                      return (
                        <div className="regulatoryTopicExcerpt" id={articleElementId} key={articleElementId}>
                          {shouldShowParentPath && (
                            <div className="regulatoryReaderChapterHeader regulatoryTopicParentPath">
                              <p className="regulatoryReaderChapterLabel">{topicSection.chapter.chapter}</p>
                              {topicSection.chapter.title && <h3>{topicSection.chapter.title}</h3>}
                            </div>
                          )}

                          <article className="regulatoryReaderSection regulatoryTopicSection">
                            {topicSection.article.divisionHeading && (
                              <h3 className="regulatoryReaderDivisionHeading">
                                <HighlightedText
                                  text={topicSection.article.divisionHeading}
                                  searchTerm={topicHighlightTerms}
                                />
                              </h3>
                            )}
                            <div className="regulatoryReaderSectionTop">
                              <div>
                                <p className="regulatoryReaderSectionLabel">
                                  {displayedArticle.label}
                                </p>
                                {displayArticle.title !== displayedArticle.label && (
                                  <h3>{displayArticle.title}</h3>
                                )}
                                {topicSection.note && (
                                  <p className="regulatoryTopicNote">{topicSection.note}</p>
                                )}
                              </div>
                              {canExpandArticle && (
                                <div className="regulatoryReaderArticleActions">
                                  <button
                                    type="button"
                                    className={`regulatoryReaderArticleToggle${isExpandedArticle ? " regulatoryReaderArticleToggle--active" : ""}`}
                                    onClick={() => setExpandedTopicArticles((previous) => {
                                      const next = new Set(previous);
                                      if (next.has(articleElementId)) next.delete(articleElementId);
                                      else next.add(articleElementId);
                                      return next;
                                    })}
                                    title={isExpandedArticle ? "Show only matching paragraphs" : "Show full article"}
                                    aria-label={isExpandedArticle ? "Show only matching paragraphs" : "Show full article"}
                                    aria-expanded={isExpandedArticle}
                                  >
                                    <i className={`bi ${isExpandedArticle ? "bi-funnel-fill" : "bi-arrows-angle-expand"}`} aria-hidden="true" />
                                  </button>
                                </div>
                              )}
                            </div>

                            <div className={`regulatoryReaderArticleBody${isExpandedArticle ? " regulatoryReaderArticleBody--expanded" : ""}`}>
                              {renderRegulationText(
                                displayArticle.body,
                                `${selectedTopic.id}-${selectedTopicDocument.documentId}-${topicSection.article.label}`,
                                topicHighlightTerms,
                                null,
                                topicMatchCursor,
                                articleBlocks,
                                null,
                                null,
                                isActiveTopicSearchCard,
                                Boolean(displayedArticle.guidanceOnly),
                                Boolean(displayedArticle.figureCard),
                                null
                              )}
                            </div>
                          </article>
                        </div>
                      );
                    })}
                  </section>
                ) : (
                  <section className="regulatoryReaderEmpty">
                    <i
                      className={`bi ${selectedTopic.isSearchTopic ? "bi-search" : "bi-journal-text"}`}
                      aria-hidden="true"
                    />
                    <h3>{selectedTopic.isSearchTopic ? "No matching regulations" : "No curated sections"}</h3>
                    <p>
                      {selectedTopic.isSearchTopic
                        ? `No visible regulation contains "${selectedTopic.label}".`
                        : "Add document references to the topic registry to populate this view."}
                    </p>
                  </section>
                )}
              </div>
            </div>
          </section>
        </div>
      )}

      {selectedRegulation && (
        <div
          className="regulatoryReaderOverlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedRegulation(null);
            }
          }}
        >
          <section
            className={`regulatoryReaderModal${
              modalReaderData?.id ? ` regulatoryReaderModal--${modalReaderData.id}` : ""
            }${isPdfReader ? " regulatoryReaderModal--pdf" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="regulatory-reader-title"
            ref={readerModalRef}
          >
            {isPdfReader ? (
              <div className="regulatoryPdfReader" ref={pdfReaderRef}>
                {pdfPageCount > 0 && (
                  <div className="regulatoryPdfPageCounter" aria-label="Current PDF page">
                    {activePdfPageNumber} / {pdfPageCount}
                  </div>
                )}
                <div className="regulatoryPdfSearch" role="search">
                  <i className="bi bi-search" aria-hidden="true" />
                  <input
                    type="search"
                    value={pdfSearchTerm}
                    onChange={(event) => setPdfSearchTerm(event.target.value)}
                    onKeyDown={handlePdfSearchKeyDown}
                    placeholder="Search PDF"
                    aria-label="Search PDF"
                  />
                  {normalizedPdfSearchTerm && (
                    <span className="regulatoryPdfSearchCount">
                      {isPdfTextIndexing
                        ? "Indexing..."
                        : `${pdfSearchMatches.length > 0 ? activePdfSearchIndex + 1 : 0}/${
                            pdfSearchMatches.length
                          }`}
                    </span>
                  )}
                  <button
                    type="button"
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      goToPreviousPdfSearchMatch();
                    }}
                    disabled={isPdfTextIndexing || pdfSearchMatches.length < 1}
                    title="Previous matching page"
                    aria-label="Previous matching page"
                  >
                    <i className="bi bi-chevron-up" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      goToNextPdfSearchMatch();
                    }}
                    disabled={isPdfTextIndexing || pdfSearchMatches.length < 1}
                    title="Next matching page"
                    aria-label="Next matching page"
                  >
                    <i className="bi bi-chevron-down" aria-hidden="true" />
                  </button>
                </div>
                <button
                  type="button"
                  className="regulatoryPdfToggle"
                  onClick={() => setPdfFitPageWidth((previous) => !previous)}
                  title={pdfFitPageWidth ? "Use default PDF width" : "Fit PDF page to width"}
                  aria-label={pdfFitPageWidth ? "Use default PDF width" : "Fit PDF page to width"}
                  aria-pressed={pdfFitPageWidth}
                >
                  <i
                    className={`bi ${pdfFitPageWidth ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className="regulatoryReaderClose regulatoryReaderClose--pdf"
                  onClick={() => setSelectedRegulation(null)}
                  title="Close"
                  aria-label="Close PDF reader"
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
                <div className="regulatoryPdfPages" ref={pdfPagesScrollerRef}>
                  <Document
                    file={selectedRegulation.pdfReaderUrl}
                    loading={<div className="regulatoryPdfStatus">Loading PDF...</div>}
                    error={<div className="regulatoryPdfStatus">Unable to load PDF.</div>}
                    onLoadSuccess={handlePdfLoadSuccess}
                  >
                    {Array.from(new Array(pdfPageCount), (_page, index) => (
                      <div
                        key={`pdf-page-wrap-${index + 1}`}
                        ref={(element) => {
                          pdfPageRefs.current[index + 1] = element;
                        }}
                        className={`regulatoryPdfPageWrap${
                          activePdfSearchPageNumber === index + 1 ? " regulatoryPdfPageWrap--active" : ""
                        }`}
                      >
                        <Page
                          key={`pdf-page-${index + 1}-${pdfRenderedPageWidth}`}
                          pageNumber={index + 1}
                          width={pdfRenderedPageWidth}
                          renderAnnotationLayer={false}
                          renderTextLayer
                          customTextRenderer={({ str }) =>
                            renderPdfSearchText(str, normalizedPdfSearchTerm)
                          }
                        />
                      </div>
                    ))}
                  </Document>
                </div>
              </div>
            ) : (
              <>
            <header className="regulatoryReaderHeader">
              <div className="regulatoryReaderTitleBlock">
                <h2 id="regulatory-reader-title">{selectedRegulation.title}</h2>
                <p>{selectedRegulation.legalName}</p>
              </div>
              <button
                type="button"
                className="regulatoryReaderClose"
                onClick={() => setSelectedRegulation(null)}
                title="Close"
                aria-label="Close regulation reader"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </header>

            <div className="regulatoryReaderSearch">
              <i className="bi bi-search" aria-hidden="true" />
              <input
                type="search"
                value={readerSearchTerm}
                onChange={(event) => setReaderSearchTerm(event.target.value)}
                onKeyDown={handleReaderSearchKeyDown}
                placeholder="Search within this regulation..."
                aria-label="Search within this regulation"
              />
              <div className="regulatoryReaderSearchControls">
                {normalizedReaderSearchTerm && (
                  <span>
                    {readerSearchCardCount > 0 && hasNavigatedReaderSearch
                      ? activeSearchMatchIndex + 1
                      : 0}
                    /
                    {readerSearchCardCount}
                  </span>
                )}
                <button
                  type="button"
                  onClick={goToPreviousSearchMatch}
                  disabled={readerSearchCardCount < 1}
                  title="Previous matching card"
                  aria-label="Previous matching card"
                >
                  <i className="bi bi-chevron-up" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={goToNextSearchMatch}
                  disabled={readerSearchCardCount < 1}
                  title="Next matching card"
                  aria-label="Next matching card"
                >
                  <i className="bi bi-chevron-down" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div
              className={`regulatoryReaderLayout${isGuidelineReader ? " regulatoryReaderLayout--guideline" : ""}`}
            >
              <nav className="regulatoryReaderMenu" aria-label="Regulation contents">
                <p className="regulatoryEyebrow">Table of content</p>
                <div className="regulatoryReaderMenuList">
                  {filteredReaderData.chapters.map((chapter, index) => {
                    const chapterMenuIndex = isGuidelineReader
                      ? chapter.chapter.replace(/^(Chapter|Part)\s+/i, "")
                      : String(index + 1).padStart(2, "0");
                    const chapterHighlightTerm = getReaderHeaderHighlightTerm(
                      `${chapter.chapter} ${chapterMenuIndex}`,
                      "chapter"
                    );

                    return (
                    <div className="regulatoryReaderMenuGroup" key={`${chapter.chapter}-${chapter.title}`}>
                      {!isArticleTitleMenuReader && !isFlatGuidelineMenuReader && (
                        <button
                          type="button"
                          className="regulatoryReaderMenuButton regulatoryReaderMenuButton--chapter"
                          onClick={() => {
                            document
                              .getElementById(`regulatory-reader-chapter-${chapter.originalIndex}`)
                              ?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                        >
                          <span className="regulatoryReaderMenuIndex">
                            <HighlightedText text={chapterMenuIndex} searchTerm={chapterHighlightTerm} />
                          </span>
                          <span className="regulatoryReaderMenuTitle">
                            <HighlightedText text={chapter.title} searchTerm={readerHighlightTerm} />
                          </span>
                        </button>
                      )}

                      {(isGuidelineReader || isArticleTitleMenuReader) && (
                        <div className="regulatoryReaderMenuChildren">
                          {chapter.articles
                            .filter(
                              (article) =>
                                !article.hideHeader &&
                                !article.figureCard &&
                                !(
                                  modalReaderData?.id === "dfsa-aml-module" &&
                                  (
                                    (chapter.chapter === "Chapter 2" &&
                                      article.label === "Guidance") ||
                                    (chapter.chapter === "Chapter 4" &&
                                      article.number === "1" &&
                                      article.title === "Applying A Risk-Based Approach") ||
                                    (chapter.chapter === "Chapter 5" &&
                                      article.number === "1" &&
                                      article.title === "Business Risk Assessment") ||
                                    (chapter.chapter === "Chapter 6" &&
                                      article.number === "1" &&
                                      article.title === "Customer Risk Assessment") ||
                                    (chapter.chapter === "Chapter 7" &&
                                      article.number === "1" &&
                                      article.title.toLowerCase() === "customer due diligence")
                                  )
                                )
                            )
                            .map((article) => {
                            const articleKeyPrefix = `${chapter.chapter}-${article.label}`;
                            const displayArticle = getArticleDisplay(article);
                            const subsections = isGuidelineReader
                              ? getGuidelineSubsections(
                                  article,
                                  articleKeyPrefix,
                                  normalizedReaderSearchTerm
                                )
                              : [];
                            const shouldHighlightStructuralArticleSection =
                              isStructuralReaderSearch &&
                              isRulebookStructuralSearchReader(modalReaderData) &&
                              (structuralReaderSearch.type === "section" ||
                                structuralReaderSearch.type === "ruleOrSection") &&
                              normalizeReaderRuleNumber(article.number) ===
                                normalizeReaderRuleNumber(structuralReaderSearch.number);

                            return (
                              <div
                                className="regulatoryReaderMenuArticle"
                                key={`${chapter.chapter}-${article.label}-menu`}
                              >
                                <button
                                  type="button"
                                  className="regulatoryReaderMenuButton regulatoryReaderMenuButton--section"
                                  onClick={() => {
                                    document
                                      .getElementById(`regulatory-reader-article-${slugifyRegulatoryId(articleKeyPrefix)}`)
                                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                                  }}
                                >
                                  <span className="regulatoryReaderMenuSectionNo">
                                    <HighlightedText
                                      text={isArticleTitleMenuReader ? `Art. ${article.number}` : article.number}
                                      searchTerm={
                                        shouldHighlightStructuralArticleSection
                                          ? isArticleTitleMenuReader
                                            ? `Art. ${article.number}`
                                            : article.number
                                          : ""
                                      }
                                    />
                                  </span>
                                  <span className="regulatoryReaderMenuTitle">
                                    <HighlightedText
                                      text={displayArticle.title}
                                      searchTerm={
                                        shouldHighlightStructuralArticleSection
                                          ? displayArticle.title
                                          : readerHighlightTerm
                                      }
                                    />
                                  </span>
                                </button>

                                {subsections.map((subsection) => {
                                  const shouldHighlightStructuralSubsection =
                                    isStructuralReaderSearch &&
                                    isRulebookStructuralSearchReader(modalReaderData) &&
                                    (structuralReaderSearch.type === "section" ||
                                      structuralReaderSearch.type === "ruleOrSection") &&
                                    normalizeReaderRuleNumber(subsection.title.split(/\s+/)[0]) ===
                                      normalizeReaderRuleNumber(structuralReaderSearch.number);
                                  const subsectionHighlightTerm = shouldHighlightStructuralSubsection
                                    ? subsection.title
                                    : readerHighlightTerm;

                                  return (
                                    <button
                                      type="button"
                                      className="regulatoryReaderMenuButton regulatoryReaderMenuButton--subsection"
                                      key={subsection.id}
                                      onClick={() => {
                                        document
                                          .getElementById(subsection.id)
                                          ?.scrollIntoView({ behavior: "smooth", block: "center" });
                                      }}
                                    >
                                      <span
                                        className={`regulatoryReaderMenuTitle${!isStructuralReaderSearch && subsection.hasSearchMatch ? " regulatoryReaderMenuTitle--searchMatch" : ""}`}
                                      >
                                        <HighlightedText
                                          text={subsection.title}
                                          searchTerm={subsectionHighlightTerm}
                                        />
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </nav>

              <div className="regulatoryReaderBody" ref={readerBodyRef}>
                {!hasReaderSearchResults && (
                  <section className="regulatoryReaderEmpty">
                    <i className="bi bi-search" aria-hidden="true" />
                    <h3>No matching articles</h3>
                    <p>No article in this regulation contains "{normalizedReaderSearchTerm}".</p>
                  </section>
                )}

                {hasVisibleReaderIntro && (
                  <section className="regulatoryReaderChapter regulatoryReaderChapter--opening">
                    <div className="regulatoryReaderChapterHeader">
                      <p className="regulatoryReaderSectionLabel">Opening</p>
                      <h3>
                        <HighlightedText
                          text="Introductory Provisions"
                          searchTerm={readerHighlightTerm}
                        />
                      </h3>
                    </div>
                    <article className="regulatoryReaderSection" id="regulatory-reader-opening">
                      {renderOpeningText(
                        filteredReaderData.intro,
                        readerHighlightTerm,
                        null,
                        readerSearchMatchCursor,
                        activeReaderSearchCardId === "regulatory-reader-opening"
                      )}
                    </article>
                  </section>
                )}

                {filteredReaderData.chapters.map((chapter) => (
                  <section
                    className="regulatoryReaderChapter"
                    id={`regulatory-reader-chapter-${chapter.originalIndex}`}
                    key={`${chapter.chapter}-${chapter.title}`}
                  >
                    <div className="regulatoryReaderChapterHeader">
                      <p className="regulatoryReaderChapterLabel">
                        <HighlightedText
                          text={chapter.chapter}
                          searchTerm={getReaderHeaderHighlightTerm(chapter.chapter, "chapter")}
                        />
                      </p>
                      <h3>
                        <HighlightedText text={chapter.title} searchTerm={readerHighlightTerm} />
                      </h3>
                    </div>

                    {chapter.articles.map((article) => {
                      const displayArticle = getArticleDisplay(article);
                      const articleKey = getReaderArticleKey(chapter, article);
                      const articleElementId = `regulatory-reader-article-${slugifyRegulatoryId(`${chapter.chapter}-${article.label}`)}`;
                      const relatedCdbArticle = country === "switzerland"
                        ? getCdbArticleLink(modalReaderData.id, article.number) : null;
                      const isActiveSearchCard = activeReaderSearchCardId === articleElementId;
                      const isExpandedArticle = expandedReaderArticles.has(articleKey);
                      const isCollapsingArticle = collapsingReaderArticles.has(articleKey);
                      const isGuidanceOnlyArticle = Boolean(article.guidanceOnly);
                      const hideArticleHeader = shouldHideArticleHeader(
                        modalReaderData,
                        chapter,
                        article
                      );
                      const hideArticleLabel = shouldHideArticleLabel(article);
                      const shouldHighlightStructuralArticleSection =
                        isStructuralReaderSearch &&
                        isRulebookStructuralSearchReader(modalReaderData) &&
                        (structuralReaderSearch.type === "section" ||
                          structuralReaderSearch.type === "ruleOrSection") &&
                        normalizeReaderRuleNumber(article.number) ===
                          normalizeReaderRuleNumber(structuralReaderSearch.number);
                      const fullArticleBlocks = getArticleBlocks(article);
                      const visibleFilteredIds = collectRegulationBlockIds(article.filteredBlocks || []);
                      const fullArticleIds = collectRegulationBlockIds(fullArticleBlocks);
                      const revealOnlyIds = new Set(
                        [...fullArticleIds].filter((id) => !visibleFilteredIds.has(id))
                      );
                      const delayedRevealIds =
                        isExpandedArticle && !isCollapsingArticle ? revealOnlyIds : null;
                      const collapsingHideIds = isCollapsingArticle ? revealOnlyIds : null;

                      return (
                        <article
                          className={`regulatoryReaderSection${(hideArticleHeader && !article.figureCard) || isGuidanceOnlyArticle ? " regulatoryReaderSection--plain" : ""}`}
                          id={articleElementId}
                          tabIndex={-1}
                          key={articleKey}
                        >
                          {article.divisionHeading && (
                            <h3 className="regulatoryReaderDivisionHeading">
                              <HighlightedText
                                text={article.divisionHeading}
                                searchTerm={readerHighlightTerm}
                              />
                            </h3>
                          )}
                          {!hideArticleHeader && (
                            <div className="regulatoryReaderSectionTop">
                              <div>
                                {!hideArticleLabel && (
                                  <p className="regulatoryReaderSectionLabel">
                                    <HighlightedText
                                      text={article.label}
                                      searchTerm={
                                        shouldHighlightStructuralArticleSection
                                          ? article.label
                                          : getReaderHeaderHighlightTerm(article.label, "article")
                                      }
                                      activeMatchIndex={null}
                                      matchIndexRef={readerSearchMatchCursor}
                                      forceActive={isActiveSearchCard}
                                    />
                                  </p>
                                )}
                                {(hideArticleLabel || displayArticle.title !== article.label) && (
                                  <h3>
                                    <HighlightedText
                                      text={displayArticle.title}
                                      searchTerm={
                                        shouldHighlightStructuralArticleSection
                                          ? displayArticle.title
                                          : readerHighlightTerm
                                      }
                                      activeMatchIndex={null}
                                      matchIndexRef={readerSearchMatchCursor}
                                      forceActive={isActiveSearchCard}
                                    />
                                  </h3>
                                )}
                              </div>

                              <div className="regulatoryReaderArticleActions">
                              {relatedCdbArticle && (
                                <button
                                  type="button"
                                  className="regulatoryReaderRelatedArticle"
                                  aria-label={`${relatedCdbArticle.label} for Art. ${article.number}`}
                                  aria-haspopup="dialog"
                                  onClick={() => setRelatedReaderArticle(relatedCdbArticle)}
                                >
                                  {relatedCdbArticle.label}
                                  <i className="bi bi-arrow-right" aria-hidden="true" />
                                </button>
                              )}
                              {normalizedReaderSearchTerm && (
                                <button
                                  type="button"
                                  className={`regulatoryReaderArticleToggle${isExpandedArticle ? " regulatoryReaderArticleToggle--active" : ""}`}
                                  onClick={() => {
                                    const existingTimer = collapseTimerRefs.current.get(articleKey);

                                    if (existingTimer) {
                                      clearTimeout(existingTimer);
                                      collapseTimerRefs.current.delete(articleKey);
                                    }

                                    if (isExpandedArticle) {
                                      setCollapsingReaderArticles((previous) => {
                                        const next = new Set(previous);
                                        next.add(articleKey);
                                        return next;
                                      });

                                      const timerId = window.setTimeout(() => {
                                        setExpandedReaderArticles((previous) => {
                                          const next = new Set(previous);
                                          next.delete(articleKey);
                                          return next;
                                        });
                                        setCollapsingReaderArticles((previous) => {
                                          const next = new Set(previous);
                                          next.delete(articleKey);
                                          return next;
                                        });
                                        collapseTimerRefs.current.delete(articleKey);
                                      }, 230);

                                      collapseTimerRefs.current.set(articleKey, timerId);
                                      return;
                                    }

                                    setCollapsingReaderArticles((previous) => {
                                      const next = new Set(previous);
                                      next.delete(articleKey);
                                      return next;
                                    });
                                    setExpandedReaderArticles((previous) => {
                                      const next = new Set(previous);
                                      next.add(articleKey);
                                      return next;
                                    });
                                  }}
                                  title={isExpandedArticle ? "Show only matching paragraphs" : "Show full article"}
                                  aria-label={
                                    isExpandedArticle
                                      ? "Show only matching paragraphs"
                                      : "Show full article"
                                  }
                                >
                                  <i
                                    className={`bi ${isExpandedArticle ? "bi-funnel-fill" : "bi-arrows-angle-expand"}`}
                                    aria-hidden="true"
                                  />
                                </button>
                              )}
                              </div>
                            </div>
                          )}
                          <div
                            className={`regulatoryReaderArticleBody${isExpandedArticle ? " regulatoryReaderArticleBody--expanded" : ""}`}
                          >
                            {renderRegulationText(
                              displayArticle.body,
                              `${chapter.chapter}-${article.label}`,
                              readerHighlightTerm,
                              null,
                              readerSearchMatchCursor,
                              isExpandedArticle ? fullArticleBlocks : article.filteredBlocks || fullArticleBlocks,
                              delayedRevealIds,
                              collapsingHideIds,
                              isActiveSearchCard,
                              isGuidanceOnlyArticle,
                              Boolean(article.figureCard),
                              isRulebookStructuralSearchReader(modalReaderData) &&
                                isStructuralReaderSearch
                                ? structuralReaderSearch
                                : null
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </section>
                ))}
              </div>
            </div>
              </>
            )}
          </section>
        </div>
      )}
      {selectedRegulation && relatedReaderArticle && (
        <RelatedRegulationArticleModal
          documentTitle={regulatoryDocumentMap.get(relatedReaderArticle.documentId)?.title}
          articleLabel={relatedReaderArticle.article.label}
          articleTitle={getArticleDisplay(relatedReaderArticle.article).title}
          parentRef={readerModalRef}
          onClose={() => setRelatedReaderArticle(null)}
        >
          {renderRegulationText(
            getArticleDisplay(relatedReaderArticle.article).body,
            `related-${relatedReaderArticle.documentId}-${relatedReaderArticle.article.number}`,
            "", null, { current: 0 }, getArticleBlocks(relatedReaderArticle.article)
          )}
        </RelatedRegulationArticleModal>
      )}
    </div>
  );
}
